/**
 * Tavsin Web — the Vibe Coding workspace.
 *
 * A real, multi-pane environment (not a single prompt box):
 *   • left   — persisted sessions (conversations) you can resume/rename/delete,
 *   • center — the agent transcript + composer; the agent reads the project,
 *              writes files and runs commands, streamed live over SSE,
 *   • right  — a tabbed workspace: Changes (git diffs), Files (browse + view),
 *              and Runs (launch autonomous sandboxed jobs, watch the console,
 *              review the diff, and apply or discard).
 *
 * Everything is powered by the same core engine as the desktop app, reached over
 * `/api/invoke` and an SSE chat stream (see api.ts) — with optional shared-token
 * auth for shared/remote deployments.
 */
import { localQueueStorage, reasonLabel, TaskQueue } from "./taskQueue.js";
import {
  acceptTeamInvite,
  type AuthInfo,
  type SignInProvider,
  AuthError,
  LimitError,
  TurnEndedError,
  handbackOf,
  type TurnHandback,
  buyCredits,
  listSites,
  publishSite,
  SitePreflightError,
  rollbackSite,
  unpublishSite,
  addSiteDomain,
  verifySiteDomain,
  removeSiteDomain,
  getSiteData,
  deleteSiteRecord,
  exportSiteData,
  type SiteDataSource,
  type SiteDataView,
  type SiteDomainView,
  type SiteView,
  type SitesInfo,
  type TurnConfidenceView,
  type QuestionFrame,
  type SettledQuestionView,
  type UserProfileView,
  type VerificationLadderView,
  type ChangedFile,
  type ConversationMessage,
  confirmPasswordReset,
  type ConversationDetail,
  type ConversationSummary,
  createTeam,
  decideTeamMemory,
  fetchAuthInfo,
  fetchBillingInfo,
  fetchMe,
  type FileTreeNode,
  getToken,
  invoke,
  inviteToTeam,
  type JobView,
  listTeamMembers,
  listTeamMemories,
  listTeams,
  loginWithEmail,
  loginWithPassword,
  logout,
  openBillingPortal,
  proposeTeamMemory,
  type ProviderStatus,
  type QuotaStatus,
  type ManagedCatalog,
  type ManagedModelView,
  reportClientError,
  type PublicUser,
  requestPasswordReset,
  type SandboxView,
  devSandboxStatus,
  startDevSandbox,
  stopDevSandbox,
  setToken,
  exchangeOAuth,
  signup,
  startCheckout,
  streamChat,
  createLiveShare,
  listLiveShares,
  revokeLiveShare,
  type UsageReport,
  verifyEmail,
  type TraceUsageReceiptView,
  type TurnPolicyView,
  type TurnVerificationReply,
  createProject,
  deleteProject,
  listProjectTrash,
  restoreProject,
  getActiveProject,
  listProjects,
  PROJECT_GONE_EVENT,
  renameProject,
  setActiveProject,
  type WebProjectView,
} from "./api.js";
import { monthlyEquivalent, PLAN_CATALOG, PLAN_IDS, planPrice, type PlanId } from "../plans.js";
import type {
  AgentControlDetailView,
  AgentControlRowView,
  AgentJobStatus,
  AgentTimelineItemView,
  AgentTimelinePageView,
} from "@tavsin/desktop/channels";
import {
  agentsListLive,
  agentsPollIntervalMs,
  costLabel,
  groupAgentRows,
  mergeTimelineItems,
  modelLabel,
  payloadPreview,
  rowFromDetail,
  safeDisplayText,
  shouldReloadAgentDetail,
  webAgentActionGates,
} from "./agents.js";
import { mapConversationMessagesToTurns, type WebTurn } from "./conversationMap.js";
import { liveTokenFromHash, mountLiveView } from "./liveView.js";
import { phoneSection, registerServiceWorker, sessionFromUrl } from "./phone.js";
import { authErrorMessage, openSignInMethods, showSignIn, type SignInDeps } from "./signIn.js";
import { el, escapeHtml, fmtUsd, formatBody, relTime, toast } from "./dom.js";
import { EMPTY_DRAFT, type LiveDraftState, reduceDraft } from "./liveDraft.js";
import { type FileIo, findTokenFile, listenForPicks, pickedEditor, setPicking, themeEditor } from "./visualEdit.js";
import { diffSections } from "./hunks.js";
import { detectApiKey } from "@tavsin/desktop/keyDetect";
import {
  applyDiskRead,
  attachWebEditor,
  beginDiskRefresh,
  currentWebEditor,
  destroyWebEditor,
  invalidateDiskRefresh,
  isLatestDiskRefresh,
  resetWebEditorState,
  saveOpenFile,
  webLanguageLabel,
} from "./codeEditor.js";
import { readStoredTheme, THEME_KEY, type WebTheme } from "./theme.js";

const PROVIDERS = ["anthropic", "openai", "gemini", "openrouter", "local"] as const;

function estimateTokens(text: string): number { return Math.ceil(text.length / 4); }

/** Hosted playground: server-reported features that must not be offered as entry points. */
function playgroundUnavailable(feature: string): boolean {
  return state.playgroundCaps?.unavailable.includes(feature) ?? false;
}

const usageReceiptLoads = new Set<string>();

function ensureUsageReceipt(turn: Turn): void {
  if (turn.role !== "assistant" || !turn.traceId || turn.usageReceipt) return;
  if (usageReceiptLoads.has(turn.traceId)) return;
  usageReceiptLoads.add(turn.traceId);
  // A set of every trace this page ever showed: keep the recent ones.
  if (usageReceiptLoads.size > 500) usageReceiptLoads.delete(usageReceiptLoads.values().next().value!);
  void withAuth(
    () => invoke<TraceUsageReceiptView>("traces.usageReceipt", { traceId: turn.traceId! }),
    null,
  ).then((receipt) => {
    if (receipt) turn.usageReceipt = receipt;
    render();
  });
}
function contextWindowFor(provider: string): number {
  const defaults: Record<string, number> = { anthropic: 200_000, openai: 128_000, gemini: 1_000_000, openrouter: 128_000, local: 8_000 };
  return defaults[provider] ?? 128_000;
}
function fmtK(n: number): string { return n >= 1000 ? `${(n / 1000).toFixed(n >= 100_000 ? 0 : 1)}k` : String(n); }

type Tab = "changes" | "files" | "runs" | "team" | "deploy" | "intel" | "preview" | "orchestration";
type PreviewDevice = "web" | "iphone" | "android";

interface IntelSubsystem { name: string; fileCount: number; riskiestPath: string | null; riskiestFragility: number | null }
interface IntelFragile { key: string; path: string | null; name: string; fragility: number | null }
interface IntelEmbed { model: string; embedded: number; pending: number; total: number }
interface IntelMemoryRow { id: string; kind: string; title: string; approvalStatus: string }
interface IntelContradiction { stale: { id: string; title: string; kind: string }; similarity: number }
interface IntelStatus { scanned: boolean; at?: string; nodes?: number; edges?: number }
interface IntelData { subsystems: IntelSubsystem[]; fragile: IntelFragile[]; embed: IntelEmbed; memories: IntelMemoryRow[]; status: IntelStatus }
type Turn = WebTurn;
/** `models.list` (OpenRouter catalog) — prices are USD per token. */
interface CatalogModel { id: string; name: string; contextLength: number; promptUsdPerToken?: number; completionUsdPerToken?: number }
interface ModelCatalog { featured: CatalogModel[]; free: CatalogModel[]; paid: CatalogModel[]; defaultModelId: string }
const MODEL_PREF_KEY = "tavsin.web.model";
function readModelPref(): string {
  try { return globalThis.localStorage?.getItem(MODEL_PREF_KEY) ?? ""; } catch { return ""; }
}
function writeModelPref(id: string): void {
  try { globalThis.localStorage?.setItem(MODEL_PREF_KEY, id); } catch { /* memory-only */ }
}
/** A running turn, bound to its session and to that session's own turn list. */
interface ActiveRun {
  readonly sessionId: string;
  readonly turns: Turn[];
  abort: null | (() => void);
  /** Inject a follow-up at the run's next model-call boundary. */
  steer: null | ((text: string) => Promise<boolean>);
}
// ── active project (hosted: several projects per person) ─────────────────────
// A link may name the project (`/?project=…`, e.g. a phone notification for a
// session in another project); it wins over the stored one, read here BEFORE
// any per-project state below is loaded from storage. Switching projects
// reloads the page, so everything keyed by it is rebuilt from scratch.
{
  const linkedProject = new URLSearchParams(window.location.search).get("project");
  if (linkedProject && /^[a-z0-9][a-z0-9-]{0,47}$/.test(linkedProject)) setActiveProject(linkedProject);
}
/**
 * A browser-storage key scoped to the active project, so one project's queue,
 * open session and preview target never surface in another. "default" keeps
 * the bare key: it is the project every install had before there were more.
 */
function projectScopedKey(base: string, project = getActiveProject()): string {
  return project && project !== "default" ? `${base}:${project}` : base;
}
const PREVIEW_URL_KEY = projectScopedKey("tavsin.web.previewUrl");
const taskQueue = new TaskQueue(localQueueStorage(projectScopedKey("tavsin.taskQueue.v1")));
/** Is the session on screen the one streaming? */
function streamingHere(): boolean {
  return state.run !== null && state.run.sessionId === state.activeSession;
}
interface NotificationView { id: string; kind: string; level: "info" | "success" | "warn" | "error"; title: string; body: string; read: boolean; createdAt: string }

// ── inline prompt/confirm (replaces window.prompt / window.confirm) ──────────
function inlinePrompt(title: string, defaultValue = ""): Promise<string | null> {
  return new Promise((resolve) => {
    const input = el("input", { class: "modal-field", type: "text", placeholder: title }) as HTMLInputElement;
    input.value = defaultValue;
    const ok = el("button", { class: "btn btn-primary btn-sm" }, ["OK"]);
    const cancel = el("button", { class: "btn btn-ghost btn-sm" }, ["Cancel"]);
    cancel.onclick = () => { closeModal(); resolve(null); };
    ok.onclick = () => { closeModal(); resolve(input.value); };
    input.onkeydown = (e) => { if (e.key === "Enter") ok.click(); if (e.key === "Escape") cancel.click(); };
    showModal(title, el("div", {}, [input, el("div", { class: "modal-actions" }, [cancel, ok])]));
    setTimeout(() => { input.focus(); input.select(); }, 0);
  });
}
function inlineConfirm(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    const ok = el("button", { class: "btn btn-primary btn-sm" }, ["Confirm"]);
    const cancel = el("button", { class: "btn btn-ghost btn-sm" }, ["Cancel"]);
    cancel.onclick = () => { closeModal(); resolve(false); };
    ok.onclick = () => { closeModal(); resolve(true); };
    showModal("Confirm", el("div", {}, [el("p", { class: "modal-msg" }, [message]), el("div", { class: "modal-actions" }, [cancel, ok])]));
    setTimeout(() => ok.focus(), 0);
  });
}

interface PlanStepView { id: string; text: string; status: "todo" | "doing" | "done" }
interface PlanView { goal: string; steps: PlanStepView[]; updatedAt: string }
interface ToolGroups { read: boolean; write: boolean; run: boolean; graph: boolean; memory: boolean; github: boolean }
interface AgentCfgView { instructions: string; tools: ToolGroups }
function defaultAgentCfg(): AgentCfgView { return { instructions: "", tools: { read: true, write: true, run: true, graph: true, memory: true, github: true } }; }

/** A checkpoint as `checkpoints.list` returns it. */
interface CheckpointView { id: string; at: string; label: string; turn: number | null }

const state = {
  ready: false,
  authMode: "open" as AuthInfo["mode"],
  authRequired: false,
  allowsSignup: false,
  signInProviders: [] as Array<{ id: SignInProvider; label: string }>,
  passkeysOn: false,
  emailCodeOn: false,
  user: null as PublicUser | null,
  billingPlans: [] as PlanId[],
  billingYearly: [] as PlanId[],
  billingEnabled: false,
  teams: [] as { id: string; name: string; ownerUserId: string; createdAt: string }[],
  canCreateTeam: false,
  activeTeamId: null as string | null,
  teamMembers: [] as Array<{ userId: string; role: "owner" | "member"; email: string | null; displayName: string | null }>,
  teamMemories: [] as Array<{ id: string; kind: string; title: string; content: string; proposedBy: string; approvalStatus: "proposed" | "approved" | "rejected" }>,
  providerOk: false,
  managed: false,
  quota: null as QuotaStatus | null,
  // Managed inference (server models.managed): the operator's keys serve this
  // user — Auto across vendors, a token budget, no key to enter. Null until loaded.
  managedCatalog: null as ManagedCatalog | null,
  resolvedProvider: "—",
  providers: [] as ProviderStatus["providers"],
  autoMode: false,
  sessions: [] as ConversationSummary[],
  activeSession: null as string | null,
  turns: [] as Turn[],
  // The ONE turn this tab is running (see taskQueue.ts for why one), with
  // the session it belongs to — everything else waits in `taskQueue`.
  run: null as ActiveRun | null,
  // The model for this tab's turns ("" = Auto: the provider's default / free route).
  model: readModelPref(),
  modelCatalog: null as ModelCatalog | null,
  // Launched sites (server sites.ts): null until the Deploy panel loads them.
  sites: null as SitesInfo | null,
  sitesBusy: false,
  // Index of the user turn currently being edited (inline), else null.
  editingIdx: null as number | null,
  // Sessions sidebar collapsed to reclaim horizontal space (persisted).
  sidebarCollapsed: readSidebarCollapsed(),
  // Files/folders (repo-relative paths; folders end with "/") + whole-project,
  // GitHub issues/PRs, and image attachments pinned into the next request.
  contextFiles: [] as string[],
  contextProject: false,
  contextGithub: [] as Array<{ kind: "issue" | "pr"; number: number; title: string; state: string; url: string }>,
  contextGitlab: [] as Array<{ kind: "issue" | "mr"; iid: number; title: string; state: string; url: string }>,
  contextImages: [] as Array<{ name: string; mediaType: string; dataBase64: string; dataUrl: string }>,
  // Restore point captured before the last write-capable turn, for "undo".
  // `traceId` rides along so an undo can be recorded against the turn it threw
  // away — the one judgement a user gives without being asked.
  /** Rewind points of the session on screen (core git/checkpoints.ts), one per turn. */
  checkpoints: null as null | { sessionId: string; list: CheckpointView[] },
  /** The restore point of the last write-capable turn, and the session it belongs to. */
  undoSnapshot: null as null | { ref: string; untracked: string[]; traceId?: string; sessionId: string },
  undoBusy: false,
  // The agent-maintained plan for the active session (null = none/empty).
  plan: null as null | PlanView,
  planPoll: null as null | number,
  // Per-session agent config (custom instructions + enabled tool groups).
  agentCfg: defaultAgentCfg() as AgentCfgView,
  agentWrite: true,
  /** Hosted playground: full agentic capability only (no Ask/Build/Refactor split). */
  capability: "agent" as const,
  handoffDismissed: false,
  playgroundCaps: null as null | { unavailable: string[]; composer: { label: string } },
  autonomy: "normal" as "normal" | "full",
  boundaries: "",
  showBoundaries: false,
  tab: "preview" as Tab,
  changed: [] as ChangedFile[],
  openFile: null as { path: string; content: string } | null,
  // Live edit buffer for the Files tab. When non-null and different from
  // openFile.content, the file is dirty and the Save button activates.
  editBuffer: null as string | null,
  saving: false,
  // Independent of the kernel so markdown preview (no controller) can still
  // show "disk changed" and offer Reload / Keep without clobbering a draft.
  fileDiskChanged: false,
  // Rendered preview vs source for the open file (Markdown defaults to preview).
  filePreview: false,
  sessionSearch: "",
  openDiff: null as { path: string; diff: string } | null,
  tree: null as FileTreeNode[] | null,
  treeOpen: new Set<string>(),
  jobs: [] as JobView[],
  job: null as string | null,
  sandboxes: [] as SandboxView[],
  agentRows: [] as AgentControlRowView[],
  agentsLoading: false,
  agentsError: null as string | null,
  agentDetail: null as AgentControlDetailView | null,
  agentDetailError: null as string | null,
  agentTimeline: [] as AgentTimelineItemView[],
  agentTimelineMaxSeq: -1,
  agentDetailSync: null as { status: AgentJobStatus; eventCount: number } | null,
  agentSteerDraft: "",
  agentActionNote: null as string | null,
  agentActionError: null as string | null,
  agentSandboxDiff: null as string | null,
  agentBusyAction: null as string | null,
  usage: null as UsageReport | null,
  intel: null as IntelData | null,
  intelLoading: false,
  intelBusy: false,
  orchestration: null as OrchestrationData | null,
  orchestrationLoading: false,
  orchestrationBusy: false,
  orchestrationNote: null as string | null,
  intelAutoScanned: false,
  // Call-graph explorer (Intel tab): the file being explored, its symbols, and
  // the callers/callees of the picked symbol.
  intelFile: null as string | null,
  intelSymbols: [] as Array<{ name: string }>,
  intelSymbol: null as string | null,
  intelCalls: null as { callers: Array<{ name: string; path: string | null }>; callees: Array<{ name: string; path: string | null }> } | null,
  // Live device-framed preview of the running app.
  previewDevice: "web" as PreviewDevice,
  previewUrl: readPreviewUrl(),
  // The Look picked on the hero: it STYLES the brief the user writes rather
  // than replacing it with a canned demo. null = no look chosen.
  heroLook: null as string | null,
  // Starter templates (templates.list); null until first fetched.
  siteTemplates: null as SiteTemplateView[] | null,
  // Server-side live dev server (flag-gated backend). null = not yet probed.
  liveSandbox: null as { available: boolean; running: boolean } | null,
  previewAutoDetected: false,
  // Contradiction review (Intel tab): the memory being inspected + its proposals.
  intelContraFor: null as string | null,
  intelContra: null as IntelContradiction[] | null,
  intelContraBusy: false,
  // Project context shown in the topbar switcher, the Context row, and the
  // Project Repo card — all derived from real project/git/stack signals.
  projectName: null as string | null,
  // Hosted projects (server projects.ts). `projectsMulti` is false on a
  // single-tenant server, where the one project is the operator's directory.
  projects: [] as WebProjectView[],
  projectsMulti: false,
  projectsMax: 0,
  isRepo: false,
  gitBranch: null as string | null,
  branches: [] as string[],
  stack: [] as string[],
  lastCommitAt: null as string | null,
  githubRepo: null as { host: string; owner: string; repo: string; webUrl: string } | null,
  // Notification center (topbar bell) — backed by the notifications.* channels.
  notifications: [] as NotificationView[],
  notifUnread: 0,
  showNotif: false,
};

/**
 * Persisted preview target. Defaults to the agent-built STATIC site (served by
 * /preview), which needs no dev server — the norm on the hosted web, where
 * there is no localhost dev server to point at and an `http://localhost` iframe
 * would be blocked as mixed content anyway. A persisted http:// URL is ignored
 * when we're served over https, for the same reason (it would render empty).
 */
function readPreviewUrl(): string {
  try {
    const stored = localStorage.getItem(PREVIEW_URL_KEY);
    if (stored && !(location.protocol === "https:" && stored.startsWith("http://"))) return stored;
  } catch { /* ignore */ }
  return "/preview/index.html";
}

/** True when a dev-server URL can't load inside this page (http inside https). */
function isMixedContent(url: string): boolean {
  return location.protocol === "https:" && /^http:\/\//i.test(url);
}

/** Persisted sessions-sidebar collapsed state. */
function readSidebarCollapsed(): boolean {
  try { return localStorage.getItem("tavsin.web.sidebarCollapsed") === "1"; } catch { return false; }
}
function persistSidebarCollapsed(): void {
  try { localStorage.setItem("tavsin.web.sidebarCollapsed", state.sidebarCollapsed ? "1" : "0"); } catch { /* ignore */ }
}

/** URL for the sandboxed static-preview route, carrying the session token (an
 * iframe can't send an Authorization header). Path-scoped —
 * `/preview/t/<token>/<path>` — so the page's relative assets (`styles.css`,
 * `img/a.jpg`) resolve under the same prefix and inherit the token. */
function staticPreviewUrl(path: string): string {
  return withPreviewToken(`/preview/${path.replace(/^\/+/, "")}`);
}

/** Ensure a static /preview URL carries the CURRENT session token, in the
 * path-scoped form. Converts the old `/preview/<path>?t=<tok>` shape a
 * previous version stored, and swaps a stale token for the current one (a
 * stored URL outlives a re-login). The live dev-server proxy is left alone. */
function withPreviewToken(url: string): string {
  if (!/^\/preview(\/|$)/.test(url) || url.startsWith("/preview/live/")) return url;
  const q = url.indexOf("?");
  let path = q === -1 ? url : url.slice(0, q);
  const params = new URLSearchParams(q === -1 ? "" : url.slice(q + 1));
  params.delete("t");
  params.delete("project");
  path = path.replace(/^\/preview\/?/, "").replace(/^tp\/[^/]+\/[^/]+\//, "").replace(/^t\/[^/]+\//, "");
  const rest = params.toString();
  const t = getToken();
  // A hosted user's project rides in the prefix too, so the page's relative
  // assets load from the same project as the page.
  const project = getActiveProject();
  const prefix = t && project
    ? `/preview/tp/${encodeURIComponent(t)}/${encodeURIComponent(project)}/`
    : t ? `/preview/t/${encodeURIComponent(t)}/` : "/preview/";
  return `${prefix}${path}${rest ? `?${rest}` : ""}`;
}

const root = document.getElementById("app") as HTMLElement;
let agentsPollTimer: ReturnType<typeof setTimeout> | null = null;
let agentsPollStop = false;

// ── data ──────────────────────────────────────────────────────────────────
async function withAuth<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    if (e instanceof AuthError) {
      promptToken();
      return fallback;
    }
    throw e;
  }
}

async function loadStatus(): Promise<void> {
  const s = await withAuth(() => invoke<ProviderStatus>("providers.status"), null);
  if (!s) return;
  state.providers = s.providers;
  // Never nag: Tavsin ships a free open-source default (OpenRouter free tier),
  // so there is always a usable model. `providerOk` is true whenever a provider
  // resolves — the UI shows the active model rather than a "connect a model"
  // prompt. Configuring your own key just swaps which model is active.
  state.resolvedProvider = s.resolvedActive;
  // Managed = Tavsin supplies the key (server env) → the user builds with zero
  // config on the free tier. providerOk is true whenever there's a usable model.
  state.managed = s.managed === true || s.providers.some((p) => p.managed);
  state.providerOk = state.managed || s.providers.some((p) => p.configured) || s.resolvedActive === "openrouter";
  state.autoMode = s.autoMode;
  // This month's quota, for the composer's "N left" counter (managed/free).
  state.quota = await withAuth(() => invoke<QuotaStatus>("quota.status"), null);
  try {
    state.managedCatalog = await withAuth(() => invoke<ManagedCatalog>("models.managed"), null);
  } catch {
    state.managedCatalog = null; // an older server: keep the own-key flow
  }
  if (isManagedInference()) {
    state.managed = true;
    state.providerOk = true;
    // A model remembered from another setup (an OpenRouter slug) cannot run here.
    if (state.model && !managedModels().some((m) => m.id === state.model && m.available && m.inPlan)) {
      state.model = "";
      writeModelPref("");
    }
  }
}

/** The operator's vendor keys serve this user: Auto across vendors, a token budget. */
function isManagedInference(): boolean {
  return state.managedCatalog?.managed === true;
}
function managedModels(): ManagedModelView[] {
  return state.managedCatalog?.models ?? [];
}
/** "×0.04" — how fast a model draws the token budget. */
function fmtMultiplier(m: number | null): string {
  if (m === null) return "";
  return `×${m >= 1 ? m.toFixed(m >= 10 ? 0 : 1) : m.toFixed(2).replace(/0$/, "")}`;
}
async function loadSessions(): Promise<void> {
  state.sessions = await withAuth(() => invoke<ConversationSummary[]>("conversations.list"), []);
}
async function loadUsage(): Promise<void> {
  state.usage = await withAuth(() => invoke<UsageReport>("usage"), null);
  updateTopbar();
}
async function refreshChanges(): Promise<void> {
  const g = await withAuth(() => invoke<{ isRepo: boolean; branch: string | null; files: ChangedFile[] }>("git.status"), { isRepo: false, branch: null, files: [] });
  state.isRepo = g.isRepo;
  state.gitBranch = g.branch;
  state.changed = g.isRepo ? g.files : [];
  if (state.tab === "changes") updateRail();
  updateTopbar();
}

/**
 * Project identity + stack detection for the topbar switcher, Context row, and
 * Project Repo card. All from real signals: project.info (name), git.status
 * (repo/branch), git.branches (switcher), fs.allFiles + preview.detect (stack).
 */
async function loadProjectContext(): Promise<void> {
  const info = await withAuth(() => invoke<{ name: string } | null>("project.info"), null);
  if (info?.name) state.projectName = info.name;
  const g = await withAuth(() => invoke<{ isRepo: boolean; branch: string | null }>("git.status"), { isRepo: false, branch: null });
  state.isRepo = g.isRepo;
  state.gitBranch = g.branch;
  if (g.isRepo) {
    const b = await withAuth(() => invoke<{ current: string | null; all: string[] }>("git.branches"), { current: null, all: [] });
    state.branches = b.all;
    const commits = await withAuth(() => invoke<Array<{ date: string }>>("git.commits", { limit: 1 }), []);
    state.lastCommitAt = commits[0]?.date ?? null;
  }
  void detectStack();
  void loadRemote();
  updateTopbar();
}

/**
 * Parse a git remote URL into a host/owner/repo + browsable https URL.
 * Handles both https (`https://host/owner/repo.git`) and scp-style
 * (`git@host:owner/repo.git`) forms. Returns null if unrecognisable.
 */
function parseRemote(raw: string): { host: string; owner: string; repo: string; webUrl: string } | null {
  const url = raw.trim();
  if (!url) return null;
  let host: string; let path: string;
  const scp = url.match(/^git@([^:]+):(.+)$/);
  if (scp?.[1] && scp[2]) { host = scp[1]; path = scp[2]; }
  else {
    try { const u = new URL(url); host = u.hostname; path = u.pathname.replace(/^\//, ""); }
    catch { return null; }
  }
  path = path.replace(/\.git$/, "").replace(/\/$/, "");
  const segs = path.split("/").filter(Boolean);
  if (segs.length < 2) return null;
  const repo = segs[segs.length - 1]!;
  const owner = segs.slice(0, -1).join("/");
  return { host, owner, repo, webUrl: `https://${host}/${owner}/${repo}` };
}

/** Read the project's git remote (origin) for the Project Repo card link. */
async function loadRemote(): Promise<void> {
  if (!state.isRepo) { state.githubRepo = null; return; }
  const r = await withAuth(() => invoke<{ url: string | null }>("git.remote"), { url: null });
  state.githubRepo = r.url ? parseRemote(r.url) : null;
  if (state.tab === "changes") updateRail();
}

/** Detect the project's framework / language / styling for the Context row. */
async function detectStack(): Promise<void> {
  const [all, det] = await Promise.all([
    withAuth(() => invoke<{ files: string[] }>("fs.allFiles"), { files: [] }),
    withAuth(() => invoke<{ framework: string }>("preview.detect"), { framework: "unknown" }),
  ]);
  const files = all.files;
  const has = (re: RegExp): boolean => files.some((p) => re.test(p));
  const stack: string[] = [];
  if (det.framework && det.framework.toLowerCase() !== "unknown") stack.push(det.framework);
  if (has(/(^|\/)tsconfig\.json$/) || has(/\.tsx?$/)) stack.push("TypeScript");
  else if (has(/\.jsx?$/) || has(/(^|\/)package\.json$/)) stack.push("JavaScript");
  if (has(/tailwind\.config\.[cm]?[jt]s$/)) stack.push("Tailwind CSS");
  if (has(/(^|\/)requirements\.txt$/) || has(/(^|\/)pyproject\.toml$/) || has(/\.py$/)) stack.push("Python");
  if (has(/(^|\/)go\.mod$/)) stack.push("Go");
  if (has(/(^|\/)Cargo\.toml$/)) stack.push("Rust");
  state.stack = stack.slice(0, 4);
  updateCenter();
}

async function loadNotifications(): Promise<void> {
  state.notifications = await withAuth(() => invoke<NotificationView[]>("notifications.list", { limit: 30 }), []);
  const c = await withAuth(() => invoke<{ count: number }>("notifications.unreadCount"), { count: 0 });
  state.notifUnread = c.count ?? 0;
  updateTopbar();
}
async function refreshJobs(): Promise<void> {
  state.jobs = await withAuth(() => invoke<JobView[]>("jobs.list"), []);
  state.sandboxes = await withAuth(() => invoke<SandboxView[]>("sandbox.list"), []);
  if (state.tab === "changes") updateRail();
}

let agentDetailReq = 0;

async function loadAgentDetail(jobId: string, resetTimeline: boolean): Promise<void> {
  if (state.job !== jobId) return;
  const req = ++agentDetailReq;
  if (resetTimeline) {
    state.agentTimeline = [];
    state.agentTimelineMaxSeq = -1;
    state.agentSandboxDiff = null;
  }
  try {
    const d = await withAuth(() => invoke<AgentControlDetailView>("agents.detail", { id: jobId }), null);
    if (!d || req !== agentDetailReq || state.job !== jobId) return;
    state.agentDetail = d;
    state.agentDetailError = null;
    state.agentDetailSync = { status: d.status, eventCount: d.eventCount };
    const tail = d.timeline.length > 0 ? d.timeline[d.timeline.length - 1]!.seq : state.agentTimelineMaxSeq;
    state.agentTimelineMaxSeq = Math.max(state.agentTimelineMaxSeq, tail);
    state.agentTimeline = mergeTimelineItems(state.agentTimeline, d.timeline);
  } catch (e) {
    if (req !== agentDetailReq || state.job !== jobId) return;
    state.agentDetailError = e instanceof Error ? e.message : String(e);
  }
  if (state.tab === "runs") updateRail();
}

async function pollAgentTimeline(jobId: string): Promise<void> {
  if (state.job !== jobId) return;
  const row = state.agentRows.find((r) => r.id === jobId);
  if (!row || (row.status !== "running" && row.status !== "queued")) return;
  const afterSeq = state.agentTimelineMaxSeq;
  try {
    const page = await withAuth(
      () =>
        invoke<AgentTimelinePageView>(
          "agents.timeline",
          afterSeq >= 0 ? { jobId, afterSeq } : { jobId },
        ),
      null,
    );
    if (!page || state.job !== jobId) return;
    state.agentTimelineMaxSeq = Math.max(state.agentTimelineMaxSeq, page.maxSeq);
    state.agentTimeline = mergeTimelineItems(state.agentTimeline, page.timeline);
  } catch {
    /* retried on next poll */
  }
}

async function refreshAgents(): Promise<void> {
  state.agentsLoading = state.agentRows.length === 0 && state.agentsError === null;
  try {
    const rows = await withAuth(() => invoke<AgentControlRowView[]>("agents.list", { limit: 40 }), []);
    state.agentRows = rows;
    state.agentsError = null;
    if (state.job && !rows.some((r) => r.id === state.job)) {
      agentDetailReq += 1;
      state.job = null;
      state.agentDetail = null;
      state.agentDetailSync = null;
      state.agentTimeline = [];
      state.agentTimelineMaxSeq = -1;
      state.agentSteerDraft = "";
      state.agentSandboxDiff = null;
    }
    if (state.job) {
      const selected = rows.find((r) => r.id === state.job);
      if (selected) {
        if (!state.agentDetail || shouldReloadAgentDetail(state.agentDetailSync, selected)) {
          await loadAgentDetail(state.job, false);
        }
        await pollAgentTimeline(state.job);
      }
    }
  } catch (e) {
    state.agentsError = e instanceof Error ? e.message : String(e);
  } finally {
    state.agentsLoading = false;
    if (state.tab === "runs") updateRail();
  }
}

function stopAgentsPolling(): void {
  agentsPollStop = true;
  if (agentsPollTimer) clearTimeout(agentsPollTimer);
  agentsPollTimer = null;
}

function startAgentsPolling(on: boolean): void {
  stopAgentsPolling();
  if (!on) return;
  agentsPollStop = false;
  const tick = async (): Promise<void> => {
    if (agentsPollStop) return;
    await refreshAgents();
    if (agentsPollStop) return;
    agentsPollTimer = setTimeout(() => void tick(), agentsPollIntervalMs(agentsListLive(state.agentRows)));
  };
  void tick();
}

// ── sessions ────────────────────────────────────────────────────────────────
async function newSession(): Promise<void> {
  const c = await withAuth(() => invoke<{ id: string }>("conversations.create"), null);
  if (!c) return;
  state.activeSession = c.id;
  persistActiveSession();
  state.turns = [];
  state.plan = null;
  state.agentCfg = defaultAgentCfg();
  await loadSessions();
  render();
}
/** Opened-thread turn cache — re-opening a recent thread paints instantly. */
const sessionTurnCache = new Map<string, Turn[]>();
/** Opened sessions kept for an instant switch back; each holds its whole transcript. */
const SESSION_CACHE_MAX = 12;
function cacheSessionTurns(id: string, turns: Turn[]): void {
  sessionTurnCache.delete(id);
  sessionTurnCache.set(id, turns);
  while (sessionTurnCache.size > SESSION_CACHE_MAX) sessionTurnCache.delete(sessionTurnCache.keys().next().value!);
}

async function openSession(id: string): Promise<void> {
  state.activeSession = id;
  persistActiveSession();
  void loadCheckpoints(id);
  // A session with a turn in flight shows the run's own live list — fetching
  // would replace it with the persisted history and drop the streaming reply.
  if (state.run?.sessionId === id) {
    state.turns = state.run.turns;
    await loadPlan();
    render();
    return;
  }
  // Instant paint from cache, then reconcile with a background fetch.
  const cached = sessionTurnCache.get(id);
  if (cached) {
    state.turns = cached;
    render();
  }
  const d = await withAuth(() => invoke<ConversationDetail>("conversations.get", { id }), null);
  if (d) {
    const mapped = mapConversationMessagesToTurns(d.messages);
    cacheSessionTurns(id, mapped);
    state.turns = mapped;
  } else if (!cached) {
    state.turns = [];
  }
  await loadPlan();
  await loadAgentConfig();
  render();
}
/**
 * Remember which session was active across page reloads. Stored per-origin in
 * localStorage so a fresh login doesn't drop the user back into the empty
 * "no session" state — the SaaS-conversion blocker the audit flagged.
 */
const ACTIVE_SESSION_KEY = projectScopedKey("tavsin.web.activeSession");
function persistActiveSession(): void {
  try {
    if (state.activeSession) localStorage.setItem(ACTIVE_SESSION_KEY, state.activeSession);
    else localStorage.removeItem(ACTIVE_SESSION_KEY);
  } catch { /* private mode, etc. */ }
}
function readPersistedActiveSession(): string | null {
  try { return localStorage.getItem(ACTIVE_SESSION_KEY); } catch { return null; }
}
/**
 * Reduce a freeform first user message to a sidebar-friendly title.
 * Collapses whitespace, drops trailing punctuation, and caps at ~50 chars
 * with a trailing ellipsis. Pure — easy to keep aligned with the backend
 * preview rule if we ever centralise it.
 */
function deriveTitle(message: string): string {
  const flat = message.replace(/\s+/g, " ").trim().replace(/[.!?…]+$/, "");
  if (flat === "") return "New chat";
  return flat.length > 48 ? flat.slice(0, 45) + "…" : flat;
}
async function renameSession(id: string): Promise<void> {
  const current = state.sessions.find((s) => s.id === id)?.title ?? "";
  const title = await inlinePrompt("Rename session", current);
  if (!title) return;
  await withAuth(() => invoke("conversations.rename", { id, title }), undefined);
  await loadSessions();
  render();
}
async function deleteSession(id: string): Promise<void> {
  if (!(await inlineConfirm("Delete this session? This cannot be undone."))) return;
  await withAuth(() => invoke("conversations.delete", { id }), undefined);
  sessionTurnCache.delete(id);
  if (state.activeSession === id) {
    state.activeSession = null;
    state.turns = [];
    persistActiveSession();
  }
  await loadSessions();
  render();
}

async function exportSessions(): Promise<void> {
  const exported: Array<{ title: string; messages: ConversationMessage[] }> = [];
  for (const s of state.sessions) {
    try {
      const detail = await withAuth(() => invoke<ConversationDetail>("conversations.get", { id: s.id }), null);
      if (detail) exported.push({ title: detail.conversation.title, messages: detail.messages });
    } catch { /* skip broken sessions */ }
  }
  const blob = new Blob([JSON.stringify({ kind: "tavsin.sessions-export", version: 1, exportedAt: new Date().toISOString(), sessions: exported }, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `tavsin-sessions-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
  toast(`Exported ${exported.length} session${exported.length === 1 ? "" : "s"}`, "success");
}

function importSessions(): void {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".json";
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text) as { kind?: string; sessions?: Array<{ title: string; messages: Array<{ role: string; content: string }> }> };
      if (data.kind !== "tavsin.sessions-export" || !Array.isArray(data.sessions)) {
        toast("Invalid export file — expected a Tavsin sessions export.", "error");
        return;
      }
      let imported = 0;
      for (const s of data.sessions) {
        const c = await withAuth(() => invoke<{ id: string }>("conversations.create"), null);
        if (!c) continue;
        if (s.title) await withAuth(() => invoke("conversations.rename", { id: c.id, title: s.title }), undefined);
        for (const m of s.messages) {
          await withAuth(() => invoke("conversations.addMessage", { id: c.id, role: m.role, content: m.content }), undefined);
        }
        imported++;
      }
      await loadSessions();
      render();
      toast(`Imported ${imported} session${imported === 1 ? "" : "s"}`, "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Import failed", "error");
    }
  };
  input.click();
}

/**
 * After `loadSessions()` populates state.sessions, restore the user's last
 * active session (if it still exists). If we stashed an id for a session
 * that's since been deleted, fall back to the most-recently-updated session.
 * Either way we end up on a non-empty surface — never the dead "no session"
 * landing.
 */
async function autoResumeSession(): Promise<void> {
  if (state.activeSession || state.sessions.length === 0) return;
  const persisted = readPersistedActiveSession();
  const target = persisted && state.sessions.find((s) => s.id === persisted)
    ? persisted
    : state.sessions[0]!.id; // sessions are pre-sorted updated_at DESC
  await openSession(target);
}

// ── send a vibe ──────────────────────────────────────────────────────────────
/**
 * Route a message. The rules, in order:
 *  1. A turn is running in THIS session → steer it in (the agent folds a
 *     correction into the work, or sets a separate task aside with
 *     turn.defer). If the turn ended before the steer landed, it is queued.
 *  2. A turn is running in ANOTHER session → queue it for THIS session. It
 *     is never steered into whichever session happens to be streaming, and
 *     never run concurrently on the same working tree.
 *  3. Nothing is running → start a turn.
 */
async function send(message: string): Promise<void> {
  const goal = message.trim();
  if (!goal) return;
  if (!state.activeSession) {
    const c = await withAuth(() => invoke<{ id: string }>("conversations.create"), null);
    if (!c) return;
    state.activeSession = c.id;
    persistActiveSession();
  }
  const sessionId = state.activeSession;
  const run = state.run;
  if (run) {
    if (run.sessionId === sessionId && run.steer) {
      const accepted = await run.steer(goal);
      if (accepted) {
        // Show it where it happened: after the request, before the reply still streaming.
        run.turns.splice(Math.max(0, run.turns.length - 1), 0, { role: "user", content: goal, meta: "sent mid-turn" });
        render();
        return;
      }
    }
    taskQueue.enqueue(sessionId, goal, "waiting");
    const where = state.run && state.run.sessionId !== sessionId ? `after “${sessionTitle(state.run.sessionId)}” finishes` : "next";
    toast(`Queued — runs ${where}`, "info");
    render();
    // The turn may have ended while the steer was in flight.
    drainQueue();
    return;
  }
  await startRun(sessionId, goal);
}

function sessionTitle(id: string): string {
  return state.sessions.find((s) => s.id === id)?.title ?? "another session";
}

/** Start the next queued task, if nothing is running and the queue is not paused. */
function drainQueue(): void {
  if (state.run) return;
  const next = taskQueue.take();
  if (next) void startRun(next.sessionId, next.text);
  else render();
}

async function startRun(sessionId: string, goal: string): Promise<void> {
  const here = (): boolean => state.activeSession === sessionId;
  // The turn list this run writes into. For the active session it is the one
  // on screen; for a queued task in another session it is that session's own
  // list, so nothing it streams can appear in the wrong transcript.
  let turns: Turn[];
  if (here()) turns = state.turns;
  else {
    const cached = sessionTurnCache.get(sessionId);
    if (cached) turns = cached;
    else {
      const d = await withAuth(() => invoke<ConversationDetail>("conversations.get", { id: sessionId }), null);
      turns = d ? mapConversationMessagesToTurns(d.messages) : [];
    }
  }
  // Claim the slot SYNCHRONOUSLY, before any await below, so a message sent
  // in the meantime queues behind this run instead of starting a second one.
  if (state.run) {
    taskQueue.enqueue(sessionId, goal, "waiting", true);
    return;
  }
  const run: ActiveRun = { sessionId, turns, abort: null, steer: null };
  state.run = run;
  cacheSessionTurns(sessionId, turns);
  const firstUserMessageInSession = turns.filter((t) => t.role === "user").length === 0;
  if (firstUserMessageInSession) {
    void withAuth(() => invoke("conversations.rename", { id: sessionId, title: deriveTitle(goal) }), undefined)
      .then(() => loadSessions())
      .then(() => updateSidebar())
      .catch(() => undefined);
  }
  // A restore point so "undo" can revert exactly this turn's file changes.
  const snap = await withAuth(() => invoke<{ ref: string; untracked: string[] }>("git.snapshot", { conversationId: sessionId, label: goal, turn: turns.filter((t) => t.role === "user" && t.meta !== "sent mid-turn").length }), null);
  state.undoSnapshot = snap ? { ...snap, sessionId } : null;
  // Pinned context belongs to the composer the user typed into — only a turn
  // started from the session on screen carries it.
  const pinnedContext = here() ? await buildPinnedContext() : "";
  const images = here() ? state.contextImages : [];

  turns.push({ role: "user", content: goal });
  const assistant: Turn = { role: "assistant", content: "" };
  turns.push(assistant);
  render();

  if (state.planPoll !== null) clearInterval(state.planPoll);
  state.planPoll = window.setInterval(() => { if (here()) void loadPlan().then(refreshPlanCard); }, 1500);

  const history = turns.slice(0, -2).map((t) => ({ role: t.role, content: t.content }));
  const { done, abort, steer } = streamChat(
    {
      message: goal,
      mode: "code",
      capability: "agent",
      autonomy: state.autonomy,
      ...(state.boundaries.trim() ? { boundaries: state.boundaries.trim() } : {}),
      ...(pinnedContext ? { context: pinnedContext } : {}),
      ...(images.length ? { images: images.map((i) => ({ mediaType: i.mediaType, dataBase64: i.dataBase64 })) } : {}),
      language: (document.documentElement.lang || "en").toLowerCase().split(/[-_]/)[0],
      tools: true,
      agentWrite: true,
      conversationId: sessionId,
      history,
      ...(state.model ? { model: state.model } : {}),
    },
    (text) => {
      assistant.content += text;
      if (here()) paintStream(assistant.content);
    },
    (e) => {
      // A refused tool call / a budget pause, live — as a chip, not ⛔ prose.
      const cur = assistant.policy ?? { blocks: [], paused: null };
      assistant.policy = e.t === "blocked"
        ? { ...cur, blocks: [...cur.blocks, { tool: e.tool, rule: e.rule ?? "policy", reason: e.reason, target: null }].slice(-12) }
        : { ...cur, paused: { reason: e.reason, scope: e.scope, spentUsd: e.spentUsd, budgetUsd: e.budgetUsd } };
      if (here()) paintPolicy(assistant.policy);
    },
    (by, text) => toast(`${by} steered this turn: ${text.slice(0, 120)}`, "info"),
    (frame) => {
      const next = reduceDraft(liveDrafts.get(assistant) ?? EMPTY_DRAFT, frame);
      liveDrafts.set(assistant, next);
      if (here()) paintDraft(next);
    },
    (q) => {
      // The agent asked something (ask.user). Options answer by steering.
      const prev = liveQuestions.get(assistant);
      liveQuestions.set(assistant, { q, askedAt: prev && prev.q.id === q.id ? prev.askedAt : Date.now() });
      if (here()) paintQuestion(assistant, (text) => { void run.steer?.(text); });
    },
  );
  run.abort = abort;
  run.steer = steer;
  let settled: "ok" | "stopped" | "failed" = "ok";
  try {
    const result = await done;
    assistant.verification = result.verification ?? null;
    assistant.confidence = result.confidence ?? null;
    if (result.questions && result.questions.length > 0) assistant.questions = result.questions;
    liveQuestions.delete(assistant);
    document.getElementById("live-question")?.remove();
    assistant.traceId = result.traceId ?? null;
    assistant.provider = result.provider;
    assistant.model = result.model;
    if (result.decisions?.length) assistant.decisions = result.decisions;
    if (result.policy) assistant.policy = result.policy;
    if (result.traceId) ensureUsageReceipt(assistant);
    if (state.undoSnapshot && result.traceId) state.undoSnapshot = { ...state.undoSnapshot, traceId: result.traceId };
    // Work the turn handed back runs NEXT, ahead of anything else waiting:
    // the tasks the agent set aside, then messages that arrived as it closed.
    // Neither is persisted in this turn's history; each becomes its own turn.
    handBack(sessionId, turns, { deferred: result.deferred ?? [], undelivered: result.undelivered ?? [], teammate: result.teammate ?? [] });
  } catch (e) {
    const aborted = (e instanceof DOMException && e.name === "AbortError") || (e instanceof TurnEndedError && e.stopped);
    // Nothing sent mid-turn is lost to a Stop or a failure: it is queued
    // (and, with the queue paused below, waits for the user to resume it).
    const hb = handbackOf(e);
    if (hb) handBack(sessionId, turns, hb);
    settled = aborted ? "stopped" : "failed";
    if (e instanceof AuthError) promptToken();
    const msg = e instanceof Error ? e.message : String(e);
    if (aborted) {
      assistant.content += assistant.content ? "\n\n_Stopped._" : "_Stopped before any output._";
    } else if (e instanceof LimitError) {
      assistant.content += `\n\n${e.message}`;
      if (e.kind === "credits") {
        // Say it where the user is looking, and put the way out in front of them.
        if (state.billingEnabled) showBuyCredits(e.message);
        else toast("Plan allowance used up", "error");
      }
    } else if (/missing API key|no model specified|PROVIDER_CONFIG|401|invalid.*key|no auth/i.test(msg)) {
      if (isManagedInference()) {
        assistant.content += `\n\nThe model service is briefly unavailable. Try again in a moment.`;
      } else if (state.managed) {
        assistant.content += `\n\nThe free model is briefly unavailable (or the free-tier limit was hit). Try again in a moment, or add your own key in **Settings** for uninterrupted use.`;
      } else {
        assistant.content += `\n\n**Add an API key to start generating.** Tavsin defaults to a free OpenRouter model — paste a free key from [openrouter.ai/keys](https://openrouter.ai/keys) in **Settings → Model provider**. Your key is stored locally and never appears in chat.`;
        toast("Add your OpenRouter API key", "error", { label: "Add key", onClick: () => openSettings("openrouter") });
      }
    } else {
      assistant.content += `\n\nThe turn ended with an error: ${msg}`;
    }
  } finally {
    // Release the slot, then hand it straight to the next task — before any
    // await below, so nothing typed in between can jump the queue or be
    // steered into a turn it was not written for.
    state.run = null;
    if (state.planPoll !== null) { clearInterval(state.planPoll); state.planPoll = null; }
    // A stopped or failed turn PAUSES the queue: whoever pressed Stop did not ask for the next task to begin.
    if (settled !== "ok") taskQueue.pause();
    drainQueue();
    render();
    if (here()) await loadPlan();
    try { await refreshChanges(); } catch { /* keep the last change list */ }
    void loadUsage();
    void loadSessions().then(updateSidebar);
    void loadCheckpoints(sessionId);
    state.tree = null;
    if (state.tab === "files") void loadTree(true);
    if (here() && settled === "ok") await maybeSurfacePreview();
    void withAuth(() => invoke<QuotaStatus>("quota.status"), null).then((qq) => { if (qq) { state.quota = qq; render(); } });
  }
}

/**
 * Apply an inline edit to an already-sent user message: drop that turn and
 * everything after it (client + server-persisted history), then resend the
 * edited text so the conversation forks cleanly from that point.
 */
async function applyEdit(idx: number, newText: string): Promise<void> {
  const text = newText.trim();
  state.editingIdx = null;
  if (!text) { render(); return; }
  state.turns = state.turns.slice(0, idx);
  if (state.activeSession) {
    await withAuth(() => invoke("conversations.truncate", { id: state.activeSession, keepCount: idx }), undefined);
  }
  void send(text);
}

/** An icon-only "+" that opens the attachment menu, plus a chip per attachment. */
function contextStrip(): HTMLElement {
  const chips: HTMLElement[] = [];
  const chip = (label: string, onRemove: () => void, cls = ""): HTMLElement => {
    const x = el("button", { class: "ctx-chip-x", title: "Remove" }, ["✕"]);
    x.onclick = () => { onRemove(); render(); };
    return el("div", { class: `ctx-chip ${cls}` }, [el("span", { class: "ctx-chip-name" }, [label]), x]);
  };
  if (state.contextProject) chips.push(chip("📦 Whole project", () => { state.contextProject = false; }, "ctx-chip-project"));
  for (const p of state.contextFiles) chips.push(chip(`${p.endsWith("/") ? "📁" : "📄"} ${(p.replace(/\/$/, "").split("/").pop() ?? p)}${p.endsWith("/") ? "/" : ""}`, () => { state.contextFiles = state.contextFiles.filter((f) => f !== p); }));
  for (const g of state.contextGithub) chips.push(chip(`${g.kind === "pr" ? "🔀" : "🐛"} #${g.number} ${g.title}`, () => { state.contextGithub = state.contextGithub.filter((x) => !(x.kind === g.kind && x.number === g.number)); }));
  for (const g of state.contextGitlab) chips.push(chip(`🦊 ${g.kind === "mr" ? "!" : "#"}${g.iid} ${g.title}`, () => { state.contextGitlab = state.contextGitlab.filter((x) => !(x.kind === g.kind && x.iid === g.iid)); }));
  for (const img of state.contextImages) {
    const x = el("button", { class: "ctx-chip-x", title: "Remove" }, ["✕"]);
    x.onclick = () => { state.contextImages = state.contextImages.filter((i) => i !== img); render(); };
    chips.push(el("div", { class: "ctx-chip ctx-chip-img" }, [el("img", { src: img.dataUrl, alt: img.name, class: "ctx-thumb" }), el("span", { class: "ctx-chip-name" }, [img.name]), x]));
  }
  const add = el("button", { class: "ctx-add ctx-add-icon", title: "Add attachments — files, folders, issues, PRs, images, instructions, tools" }, [el("span", { class: "glyph", html: ICON.plus })]);
  add.onclick = () => openAttachMenu(add);
  return el("div", { class: "ctx-strip" }, [add, ...chips]);
}

/** The "+" attachment menu: pick what kind of context to add. */
function openAttachMenu(anchor: HTMLElement): void {
  popover(anchor, [
    menuItem("📄", "Files & folders", () => void openContextPicker()),
    menuItem("🔀", "Pull request", () => void openGithubPicker("pr")),
    menuItem("🐛", "Issue", () => void openGithubPicker("issue")),
    menuItem("🦊", "GitLab issue / MR", () => void openGitlabPicker("issue")),
    menuItem("🖼", "Image / screenshot", () => pickContextImage()),
    menuItem("📐", "Instructions", () => openAgentPanel()),
    menuItem("🛠", "Tools", () => openAgentPanel()),
  ]);
}

/** Attach one or more images (e.g. a screenshot) as vision input for the next turn. */
function pickContextImage(): void {
  const inp = el("input", { type: "file", accept: "image/*", multiple: "" }) as HTMLInputElement;
  inp.onchange = async () => {
    for (const file of Array.from(inp.files ?? [])) {
      if (!file.type.startsWith("image/")) continue;
      const dataUrl = await new Promise<string>((res) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.readAsDataURL(file); });
      const comma = dataUrl.indexOf(",");
      state.contextImages.push({ name: file.name, mediaType: file.type, dataBase64: comma >= 0 ? dataUrl.slice(comma + 1) : "", dataUrl });
    }
    render();
  };
  inp.click();
}

/** Parse "#42", "42", or a github.com issue/PR URL into a ref. */
function parseGithubRef(raw: string): { owner?: string; repo?: string; number: number; kind?: "issue" | "pr" } | null {
  const s = raw.trim();
  const m = s.match(/github\.com\/([^/\s]+)\/([^/\s]+)\/(issues|pull)\/(\d+)/i);
  if (m && m[1] && m[2] && m[4]) return { owner: m[1], repo: m[2], number: Number(m[4]), kind: (m[3] ?? "").toLowerCase() === "pull" ? "pr" : "issue" };
  const n = s.replace(/^#/, "");
  if (/^\d+$/.test(n)) return { number: Number(n) };
  return null;
}

function pinGithub(kind: "issue" | "pr", r: { number: number; title: string; state: string; url: string }): void {
  if (!state.contextGithub.some((x) => x.kind === kind && x.number === r.number)) {
    state.contextGithub.push({ kind, number: r.number, title: r.title, state: r.state, url: r.url });
  }
  closeModal();
  render();
}

/** Pick a GitHub issue or pull request to pin into the request's context. */
async function openGithubPicker(kind: "issue" | "pr"): Promise<void> {
  const label = kind === "pr" ? "pull requests" : "issues";
  const status = await withAuth(() => invoke<{ github: { configured: boolean; owner: string | null; repo: string | null } }>("connectors.status"), null);
  if (!status?.github.configured) {
    toast("Connect your GitHub in Settings to attach issues or PRs.", "error", { label: "Open Settings", onClick: () => openAppSettings() });
    return;
  }

  // Direct entry: attach by number or URL.
  const refInput = el("input", { class: "field", placeholder: "Number (42) or GitHub URL…" }) as HTMLInputElement;
  const refBtn = el("button", { class: "btn btn-primary btn-sm" }, ["Attach"]);
  const refErr = el("div", { class: "drawer-note", style: "color:var(--amber);margin:4px 0 0" }, []);
  const attachByRef = async (): Promise<void> => {
    const parsed = parseGithubRef(refInput.value);
    if (!parsed) { refErr.textContent = "Enter a number (e.g. 42) or a GitHub issue/PR URL."; return; }
    refErr.textContent = "";
    const payload: Record<string, unknown> = { number: parsed.number };
    if (parsed.owner && parsed.repo) { payload["owner"] = parsed.owner; payload["repo"] = parsed.repo; }
    const item = await withAuth(() => invoke<{ number: number; title: string; state: string; url: string; isPull: boolean }>("connectors.github.issue", payload), null);
    if (!item) { refErr.textContent = `Couldn't find #${parsed.number}.`; return; }
    pinGithub(parsed.kind ?? (item.isPull ? "pr" : "issue"), item);
  };
  refBtn.onclick = () => void attachByRef();
  refInput.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); void attachByRef(); } });

  const listBox = el("div", { class: "gh-list" }, [el("p", { class: "drawer-note" }, [`Loading ${label}…`])]);
  showModal(kind === "pr" ? "Attach a pull request" : "Attach an issue", el("div", { class: "gh-modal" }, [
    el("p", { class: "drawer-note", style: "margin-top:0" }, [`${status.github.owner}/${status.github.repo}`]),
    el("div", { class: "ctx-modal-field" }, [refInput, refBtn]),
    refErr,
    el("div", { class: "gh-sep" }, [`or pick from open ${label}`]),
    listBox,
  ]));
  const rows = await withAuth(() => invoke<Array<{ number: number; title: string; state: string; url: string }>>(kind === "pr" ? "connectors.github.pulls" : "connectors.github.issues"), null);
  listBox.replaceChildren();
  if (!rows || rows.length === 0) { listBox.append(el("p", { class: "drawer-note" }, [`No open ${label}.`])); return; }
  for (const r of rows) {
    const row = el("button", { class: "gh-row" }, [
      el("span", { class: "gh-num" }, [`#${r.number}`]),
      el("span", { class: "gh-title" }, [r.title]),
      el("span", { class: `gh-state gh-state-${r.state}` }, [r.state]),
    ]);
    row.onclick = () => pinGithub(kind, r);
    listBox.append(row);
  }
}

function pinGitlab(kind: "issue" | "mr", r: { iid: number; title: string; state: string; url: string }): void {
  if (!state.contextGitlab.some((x) => x.kind === kind && x.iid === r.iid)) {
    state.contextGitlab.push({ kind, iid: r.iid, title: r.title, state: r.state, url: r.url });
  }
  closeModal();
  render();
}

/** Pick a GitLab issue or merge request to pin into the request's context. */
async function openGitlabPicker(initial: "issue" | "mr"): Promise<void> {
  const status = await withAuth(() => invoke<{ gitlab?: { configured: boolean; project: string | null } }>("connectors.status"), null);
  if (!status?.gitlab?.configured) {
    toast("Connect your GitLab in Settings to attach issues or MRs.", "error", { label: "Open Settings", onClick: () => openAppSettings() });
    return;
  }
  let kind: "issue" | "mr" = initial;
  const listBox = el("div", { class: "gh-list" }, []);
  const iidInput = el("input", { class: "field", placeholder: "IID (e.g. 42)…" }) as HTMLInputElement;
  const attachByIid = async (): Promise<void> => {
    const n = Number(iidInput.value.trim().replace(/^[#!]/, ""));
    if (!Number.isFinite(n)) return;
    const item = await withAuth(() => invoke<{ iid: number; title: string; state: string; url: string }>(kind === "mr" ? "connectors.gitlab.mr" : "connectors.gitlab.issue", { iid: n }), null);
    if (item) pinGitlab(kind, item);
  };
  const iidBtn = el("button", { class: "btn btn-primary btn-sm" }, ["Attach"]);
  iidBtn.onclick = () => void attachByIid();
  iidInput.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); void attachByIid(); } });

  const load = async (): Promise<void> => {
    listBox.replaceChildren(el("p", { class: "drawer-note" }, ["Loading…"]));
    const rows = await withAuth(() => invoke<Array<{ iid: number; title: string; state: string; url: string }>>(kind === "mr" ? "connectors.gitlab.mergeRequests" : "connectors.gitlab.issues"), null);
    listBox.replaceChildren();
    if (!rows || rows.length === 0) { listBox.append(el("p", { class: "drawer-note" }, [`No open ${kind === "mr" ? "merge requests" : "issues"}.`])); return; }
    for (const r of rows) {
      const row = el("button", { class: "gh-row" }, [
        el("span", { class: "gh-num" }, [`${kind === "mr" ? "!" : "#"}${r.iid}`]),
        el("span", { class: "gh-title" }, [r.title]),
        el("span", { class: `gh-state gh-state-${r.state === "opened" ? "open" : r.state}` }, [r.state]),
      ]);
      row.onclick = () => pinGitlab(kind, r);
      listBox.append(row);
    }
  };

  const seg = el("div", { class: "seg" });
  const syncSeg = (): void => seg.querySelectorAll(".seg-btn").forEach((b) => b.classList.toggle("seg-on", (b as HTMLElement).dataset["v"] === kind));
  ([["issue", "Issues"], ["mr", "Merge requests"]] as const).forEach(([v, lbl]) => {
    const b = el("button", { class: "seg-btn", "data-v": v }, [lbl]);
    (b as HTMLElement).dataset["v"] = v;
    b.onclick = () => { kind = v; syncSeg(); void load(); };
    seg.append(b);
  });
  syncSeg();

  showModal("Attach from GitLab", el("div", { class: "gh-modal" }, [
    el("p", { class: "drawer-note", style: "margin-top:0" }, [status.gitlab.project ?? ""]),
    seg,
    el("div", { class: "ctx-modal-field" }, [iidInput, iidBtn]),
    listBox,
  ]));
  void load();
}

/** Modal to pin specific files or the whole project into the next request. */
async function openContextPicker(): Promise<void> {
  const tree = await withAuth(() => invoke<FileTreeNode[]>("fs.tree"), null);
  const files: string[] = [];
  if (tree) { const walk = (ns: FileTreeNode[]): void => { for (const n of ns) { files.push(n.type === "dir" ? `${n.path}/` : n.path); if (n.children) walk(n.children); } }; walk(tree); }

  const projRow = (() => {
    const cb = el("input", { type: "checkbox" }) as HTMLInputElement;
    cb.checked = state.contextProject;
    cb.onchange = () => { state.contextProject = cb.checked; render(); };
    return el("label", { class: "ctx-modal-row" }, [cb, el("span", {}, ["📦 Pin the whole project (structure + your file tools)"])]);
  })();

  const input = el("input", { class: "field", placeholder: "path/to/file.ts  or  path/to/folder/", list: "ctx-file-list" }) as HTMLInputElement;
  const datalist = el("datalist", { id: "ctx-file-list" }, files.slice(0, 1000).map((f) => el("option", { value: f }))) as HTMLElement;
  const attached = el("div", { class: "ctx-modal-attached" });
  const renderAttached = (): void => {
    attached.replaceChildren(...state.contextFiles.map((p) => {
      const x = el("button", { class: "ctx-chip-x", title: "Remove" }, ["✕"]);
      x.onclick = () => { state.contextFiles = state.contextFiles.filter((f) => f !== p); renderAttached(); render(); };
      return el("div", { class: "ctx-chip" }, [el("span", { class: "ctx-chip-name" }, [p]), x]);
    }));
  };
  renderAttached();
  const addFile = (): void => {
    const p = input.value.trim();
    if (p && !state.contextFiles.includes(p)) { state.contextFiles.push(p); input.value = ""; renderAttached(); render(); }
  };
  const addBtn = el("button", { class: "btn btn-primary btn-sm" }, ["Add"]);
  addBtn.onclick = addFile;
  input.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); addFile(); } });

  showModal("Add files & folders", el("div", { class: "ctx-modal" }, [
    el("p", { class: "drawer-note", style: "margin-top:0" }, ["Pinned files are included verbatim; a pinned folder (path ending in /) includes its files; a pinned project adds the tree and keeps the agent's tools in scope."]),
    projRow,
    el("div", { class: "ctx-modal-field" }, [input, addBtn, datalist]),
    attached,
  ]));
}

/** Assemble the context blob from pinned files + optional project structure. */
async function buildPinnedContext(): Promise<string> {
  const parts: string[] = [];
  if (state.contextProject) {
    const tree = await withAuth(() => invoke<FileTreeNode[]>("fs.tree"), null);
    if (tree) {
      const paths: string[] = [];
      const walk = (nodes: FileTreeNode[]): void => { for (const n of nodes) { paths.push(n.type === "dir" ? `${n.path}/` : n.path); if (n.children) walk(n.children); } };
      walk(tree);
      parts.push(`The whole project is pinned as context. Top-level structure:\n${paths.slice(0, 300).join("\n")}\nUse your file tools to read any file you need.`);
    }
  }
  // Expand any pinned folders (trailing "/") to the files beneath them, capped.
  const folders = state.contextFiles.filter((p) => p.endsWith("/"));
  const explicitFiles = state.contextFiles.filter((p) => !p.endsWith("/"));
  const filePaths = new Set(explicitFiles);
  if (folders.length > 0) {
    const tree = await withAuth(() => invoke<FileTreeNode[]>("fs.tree"), null);
    if (tree) {
      const all: string[] = [];
      const walk = (ns: FileTreeNode[]): void => { for (const n of ns) { if (n.type === "file") all.push(n.path); if (n.children) walk(n.children); } };
      walk(tree);
      for (const folder of folders) {
        const prefix = folder;
        for (const f of all.filter((f) => f.startsWith(prefix)).slice(0, 40)) filePaths.add(f);
      }
    }
  }
  for (const path of filePaths) {
    const r = await withAuth(() => invoke<{ content: string }>("fs.read", { path }), null);
    if (r) parts.push(`Pinned file ${path}:\n\`\`\`\n${r.content.slice(0, 12_000)}\n\`\`\``);
  }
  // Attached GitHub issues / pull requests.
  for (const g of state.contextGithub) {
    if (g.kind === "pr") {
      let files: Array<{ filename: string; additions: number; deletions: number }> | null = null;
      try { files = await invoke<Array<{ filename: string; additions: number; deletions: number }>>("connectors.github.pullFiles", { number: g.number }); } catch { files = null; }
      const fileList = files ? files.slice(0, 60).map((f) => `  ${f.filename} (+${f.additions}/-${f.deletions})`).join("\n") : "";
      parts.push(`Pinned pull request #${g.number} — "${g.title}" (${g.state})\n${g.url}${fileList ? `\nChanged files:\n${fileList}` : ""}`);
    } else {
      parts.push(`Pinned issue #${g.number} — "${g.title}" (${g.state})\n${g.url}`);
    }
  }
  // Attached GitLab issues / merge requests.
  for (const g of state.contextGitlab) {
    if (g.kind === "mr") {
      const files = await withAuth(() => invoke<Array<{ path: string }>>("connectors.gitlab.mrChanges", { iid: g.iid }), null);
      const fileList = files ? files.slice(0, 60).map((f) => `  ${f.path}`).join("\n") : "";
      parts.push(`Pinned GitLab merge request !${g.iid} — "${g.title}" (${g.state})\n${g.url}${fileList ? `\nChanged files:\n${fileList}` : ""}`);
    } else {
      parts.push(`Pinned GitLab issue #${g.iid} — "${g.title}" (${g.state})\n${g.url}`);
    }
  }
  return parts.join("\n\n");
}

/**
 * Put what a turn handed back into this session's queue, ahead of anything
 * else: the agent's deferred tasks, then messages that arrived as it closed.
 * Their "sent mid-turn" rows come out of the transcript (each becomes its own
 * turn). A teammate's leftover message is SHOWN, never queued: running it
 * would spend the owner's tokens on something the owner did not ask for.
 */
function handBack(sessionId: string, turns: Turn[], hb: TurnHandback): void {
  const handedBack = [...hb.deferred, ...hb.undelivered];
  if (handedBack.length > 0) {
    const steered = new Set(handedBack);
    for (let i = turns.length - 1; i >= 0; i--) {
      if (turns[i]!.role === "user" && turns[i]!.meta === "sent mid-turn" && steered.has(turns[i]!.content)) turns.splice(i, 1);
    }
    // Front-inserted one batch at a time: deferred last, so it runs first.
    taskQueue.enqueue(sessionId, [...hb.undelivered], "undelivered", true);
    taskQueue.enqueue(sessionId, [...hb.deferred], "deferred", true);
    toast(`${String(handedBack.length)} follow-up task${handedBack.length === 1 ? "" : "s"} queued to run next`, "info");
  }
  for (const m of hb.teammate) {
    toast(`${m.from} sent this as the turn ended — not run: ${m.text.slice(0, 160)}`, "info", { label: "Use it", onClick: () => setComposer(m.text) });
  }
}

/** This session's rewind points. Failure leaves none, never an error. */
async function loadCheckpoints(sessionId: string): Promise<void> {
  const r = await withAuth(() => invoke<{ checkpoints: CheckpointView[] }>("checkpoints.list", { conversationId: sessionId }), null);
  state.checkpoints = { sessionId, list: r?.checkpoints ?? [] };
  if (state.activeSession === sessionId) render();
}

/** The checkpoint taken as the given user turn began: same text, else the same position. */
function checkpointFor(t: Turn, idx: number): CheckpointView | null {
  if (!state.checkpoints || state.checkpoints.sessionId !== state.activeSession) return null;
  const label = t.content.replace(/\s+/g, " ").trim().slice(0, 160);
  const n = state.turns.slice(0, idx).filter((x) => x.role === "user" && x.meta !== "sent mid-turn").length;
  const list = [...state.checkpoints.list].reverse();
  return list.find((c) => c.label !== "" && c.label === label) ?? list.find((c) => c.turn === n) ?? null;
}

/** Put every file back the way it was before a turn. The current state is saved first, so it can be undone. */
async function rewindTo(cp: CheckpointView, label: string, scope: "session" | "all" = "session"): Promise<void> {
  const sessionId = state.activeSession;
  if (!sessionId || state.run) return;
  const ask = scope === "all"
    ? `Also put back the files changed outside this session since "${label.slice(0, 80)}"? The current state is saved first, so you can undo this.`
    : `Put back the files this session changed since "${label.slice(0, 80)}"? The current state is saved first, so you can undo this.`;
  if (!(await inlineConfirm(ask))) return;
  const r = await withAuth(() => invoke<{ ok: boolean; restored: string[]; leftAlone?: string[]; undoCheckpoint: string | null; error: string | null }>("checkpoints.restore", { conversationId: sessionId, id: cp.id, ...(scope === "all" ? { scope: "all" } : {}) }), null);
  if (!r) return;
  const undoId = r.undoCheckpoint;
  const files = (n: number): string => `${String(n)} file${n === 1 ? "" : "s"}`;
  if (r.ok) {
    toast(`Rewound ${files(r.restored.length)}`, "success", undoId ? {
      label: "Undo",
      onClick: () => void withAuth(() => invoke("checkpoints.restore", { conversationId: sessionId, id: undoId }), null).then(async () => { await refreshChanges(); void loadCheckpoints(sessionId); }),
    } : undefined);
    // Only this session's files are put back; say what was left and offer the rest.
    const others = r.leftAlone?.length ?? 0;
    if (others > 0) toast(`${files(others)} changed outside this session left as ${others === 1 ? "it is" : "they are"}`, "info", { label: "Rewind those too", onClick: () => void rewindTo(cp, label, "all") });
  } else toast(r.error ?? "Could not rewind", "error");
  await refreshChanges();
  state.tree = null;
  void loadCheckpoints(sessionId);
}

/** Put text in the composer for the user to send (never sends it). */
function setComposer(text: string): void {
  const ta = document.querySelector<HTMLTextAreaElement>("textarea.composer-input");
  if (!ta) return;
  ta.value = text;
  ta.focus();
}

/** Undo the file changes made by the most recent write-capable turn. */
async function undoLastEdit(): Promise<void> {
  // Only the session that made the edits can undo them, and never mid-run:
  // a restore while another turn writes would revert work it is doing.
  if (!state.undoSnapshot || state.undoBusy || state.run !== null || state.undoSnapshot.sessionId !== state.activeSession) return;
  if (!(await inlineConfirm("Undo the last edit? This reverts the files that turn changed to their previous state."))) return;
  state.undoBusy = true;
  render();
  const paths = state.changed.map((f) => f.path);
  const r = await withAuth(() => invoke<{ ok: boolean; restored: number; error: string | null }>("git.undo", {
    ref: state.undoSnapshot!.ref,
    preUntracked: state.undoSnapshot!.untracked,
    paths,
    ...(state.undoSnapshot!.traceId ? { traceId: state.undoSnapshot!.traceId } : {}),
  }), null);
  state.undoBusy = false;
  state.undoSnapshot = null;
  if (r?.ok) toast(`Undid the last edit — ${r.restored} file${r.restored === 1 ? "" : "s"} restored`, "success");
  else toast(r?.error ?? "Could not undo the last edit", "error");
  await refreshChanges();
  state.tree = null;
  if (state.tab === "files") void loadTree(true);
  render();
}

// ── goals & plans ────────────────────────────────────────────────────────────

/** Load the active session's plan into state (null when there's nothing to show). */
async function loadPlan(): Promise<void> {
  if (!state.activeSession) { state.plan = null; return; }
  const p = await withAuth(() => invoke<PlanView>("plan.get", { conversationId: state.activeSession }), null);
  state.plan = p && (p.steps.length > 0 || p.goal.trim() !== "") ? p : null;
}

/** Repaint just the plan card in place (used by the live poll during streaming). */
function refreshPlanCard(): void {
  const wrap = document.getElementById("plan-wrap");
  if (!wrap) return;
  const card = planCard();
  wrap.replaceChildren(...(card ? [card] : []));
}

/** Persist the current plan's steps (full replace), keeping ids/status. */
async function savePlanSteps(steps: PlanStepView[], goal?: string): Promise<void> {
  if (!state.activeSession) return;
  const payload: Record<string, unknown> = { conversationId: state.activeSession, steps: steps.map((s) => ({ id: s.id, text: s.text, status: s.status })) };
  if (goal !== undefined) payload["goal"] = goal;
  state.plan = await withAuth(() => invoke<PlanView>("plan.set", payload), state.plan);
  refreshPlanCard();
}

const NEXT_STATUS: Record<PlanStepView["status"], PlanStepView["status"]> = { todo: "doing", doing: "done", done: "todo" };

async function cyclePlanStep(id: string): Promise<void> {
  if (!state.activeSession || !state.plan) return;
  const step = state.plan.steps.find((s) => s.id === id);
  if (!step) return;
  const status = NEXT_STATUS[step.status];
  state.plan = await withAuth(() => invoke<PlanView>("plan.setStep", { conversationId: state.activeSession, id, status }), state.plan);
  refreshPlanCard();
}

async function addPlanStep(text: string): Promise<void> {
  const t = text.trim();
  if (!t || !state.activeSession) return;
  const steps: PlanStepView[] = [...(state.plan?.steps ?? []), { id: "", text: t, status: "todo" }];
  await savePlanSteps(steps, state.plan?.goal);
}

async function deletePlanStep(id: string): Promise<void> {
  if (!state.plan) return;
  await savePlanSteps(state.plan.steps.filter((s) => s.id !== id), state.plan.goal);
}

async function clearPlan(): Promise<void> {
  if (!state.activeSession) return;
  if (!(await inlineConfirm("Clear the plan for this session?"))) return;
  await withAuth(() => invoke("plan.clear", { conversationId: state.activeSession }), undefined);
  state.plan = null;
  refreshPlanCard();
}

const PLAN_ICON: Record<PlanStepView["status"], string> = { todo: "○", doing: "◐", done: "✓" };

/** The visible, tickable plan checklist shown above the transcript. */
function planCard(): HTMLElement | null {
  const p = state.plan;
  if (!p || (p.steps.length === 0 && p.goal.trim() === "")) return null;
  const done = p.steps.filter((s) => s.status === "done").length;
  const clear = el("button", { class: "plan-clear", title: "Clear plan" }, ["✕"]);
  clear.onclick = () => void clearPlan();
  const head = el("div", { class: "plan-head" }, [
    el("div", { class: "plan-title" }, [el("span", { class: "plan-ico" }, ["🎯"]), el("span", {}, [p.goal.trim() || "Plan"])]),
    el("div", { class: "plan-progress" }, [`${done}/${p.steps.length}`]),
    clear,
  ]);
  const rows = p.steps.map((s) => {
    const box = el("button", { class: `plan-box plan-${s.status}`, title: "Cycle: to-do → doing → done" }, [PLAN_ICON[s.status]]);
    box.onclick = () => void cyclePlanStep(s.id);
    const del = el("button", { class: "plan-del", title: "Remove step" }, ["✕"]);
    del.onclick = () => void deletePlanStep(s.id);
    return el("div", { class: `plan-step plan-step-${s.status}` }, [box, el("span", { class: "plan-step-text" }, [s.text]), del]);
  });
  const input = el("input", { class: "plan-add-input", placeholder: "Add a step…", "aria-label": "Add a plan step" }) as HTMLInputElement;
  input.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); const v = input.value; input.value = ""; void addPlanStep(v); } });
  return el("div", { class: "plan-card" }, [head, el("div", { class: "plan-steps" }, rows), el("div", { class: "plan-add" }, [input])]);
}

// ── agent & tools configuration ──────────────────────────────────────────────

const TOOL_GROUP_META: Array<{ key: keyof ToolGroups; label: string; hint: string }> = [
  { key: "read", label: "Read files", hint: "read, list, search files + code search" },
  { key: "write", label: "Write files", hint: "create / overwrite files" },
  { key: "run", label: "Run commands", hint: "run shell commands (tests, builds)" },
  { key: "graph", label: "Code graph", hint: "fragility, blast radius, deps, tests" },
  { key: "memory", label: "Memory", hint: "record reusable learnings" },
  { key: "github", label: "GitHub / GitLab", hint: "issues & PRs/MRs — read, comment, open" },
];

async function loadAgentConfig(): Promise<void> {
  if (!state.activeSession) { state.agentCfg = defaultAgentCfg(); return; }
  const c = await withAuth(() => invoke<AgentCfgView>("agentConfig.get", { conversationId: state.activeSession }), null);
  state.agentCfg = c ?? defaultAgentCfg();
}

async function saveAgentConfig(patch: { instructions?: string; tools?: Partial<ToolGroups> }): Promise<void> {
  if (!state.activeSession) return;
  const c = await withAuth(() => invoke<AgentCfgView>("agentConfig.set", { conversationId: state.activeSession, ...patch }), null);
  if (c) state.agentCfg = c;
}

/** The consolidated "Agent & tools" panel: persona, tool sets, and chat defaults. */
function openAgentPanel(): void {
  const cfg = state.agentCfg;

  const instructions = el("textarea", { class: "field agent-instructions", rows: "4", placeholder: "e.g. Prefer functional style. Never add dependencies without asking. Keep replies terse." }) as HTMLTextAreaElement;
  instructions.value = cfg.instructions;
  instructions.onblur = () => { if (instructions.value !== state.agentCfg.instructions) void saveAgentConfig({ instructions: instructions.value }); };

  const toolRows = TOOL_GROUP_META.map(({ key, label, hint }) => {
    const cb = el("input", { type: "checkbox" }) as HTMLInputElement;
    cb.checked = cfg.tools[key];
    cb.onchange = () => void saveAgentConfig({ tools: { [key]: cb.checked } });
    return el("label", { class: "agent-tool-row" }, [cb, el("span", { class: "agent-tool-label" }, [label]), el("span", { class: "agent-tool-hint" }, [hint])]);
  });

  const autonomy = el("input", { type: "checkbox" }) as HTMLInputElement;
  autonomy.checked = state.autonomy === "full";
  autonomy.onchange = () => { state.autonomy = autonomy.checked ? "full" : "normal"; render(); };

  showModal("Agent & tools", el("div", { class: "agent-panel" }, [
    el("div", { class: "set-section" }, [
      el("div", { class: "set-label" }, ["Custom instructions"]),
      el("p", { class: "drawer-note", style: "margin-top:0" }, ["Steer the agent's behaviour for this session — persona, style, hard preferences. Applied to every message here."]),
      instructions,
    ]),
    el("div", { class: "set-section" }, [
      el("div", { class: "set-label" }, ["Tools the agent may use"]),
      el("div", { class: "agent-tools" }, toolRows),
    ]),
    el("div", { class: "set-section" }, [
      el("div", { class: "set-label" }, ["Chat defaults"]),
      el("label", { class: "agent-tool-row" }, [autonomy, el("span", { class: "agent-tool-label" }, ["Full autonomy"]), el("span", { class: "agent-tool-hint" }, ["run straight through without mid-task pauses (Pro)"])]),
      el("div", { class: "agent-model" }, [el("span", { class: "agent-tool-label" }, ["Model"]), composerModelPicker()]),
    ]),
  ]));
}

/**
 * After a build, open the Preview stage on what was just made. A listening
 * dev server wins when the browser can frame it; otherwise the built site
 * (`/preview/index.html`) is shown inside the app. A turn that changed no
 * files and has no live server leaves the conversation where it is.
 */
async function maybeSurfacePreview(): Promise<void> {
  const r = await withAuth(() => invoke<{ url: string; source: string; framework: string }>("preview.detect"), null);
  const live = r?.url && r.source.includes("listening") && !isMixedContent(r.url) ? r : null;
  // A turn that changed nothing, with no live server, stays on the conversation.
  // Opening the preview then would hide the answer they just received.
  if (state.changed.length === 0 && !live) return;
  state.previewUrl = live ? live.url : staticPreviewUrl("index.html");
  state.previewAutoDetected = true;
  try { localStorage.setItem(PREVIEW_URL_KEY, state.previewUrl); } catch { /* ignore */ }
  setTab("preview");
}

/** Silent detect when the Preview tab first opens — snaps to a listening port. */
async function autoDetectPreview(): Promise<void> {
  const r = await withAuth(() => invoke<{ url: string; source: string }>("preview.detect"), null);
  if (!r?.url || !r.source.includes("listening") || isMixedContent(r.url)) return;
  state.previewUrl = r.url;
  try { localStorage.setItem(PREVIEW_URL_KEY, r.url); } catch { /* ignore */ }
  if (state.tab === "preview") updateRail();
}

// ── autonomous runs ───────────────────────────────────────────────────────────
async function launchRun(goal: string, sandbox: boolean): Promise<void> {
  const g = goal.trim();
  if (!g) return;
  if (playgroundUnavailable("jobsBackground")) {
    toast(
      "Background autonomous runs are desktop-only. Use the composer here for a full agent turn in this session.",
      "info",
    );
    return;
  }
  // Autonomous BACKGROUND runs execute a caller-supplied command server-side, so
  // they're gated off the hosted web (RCE containment) until sandboxed execution
  // lands. Be honest instead of silently doing nothing: try it, and on the
  // gate's rejection, point the user at the in-session Build agent (which runs
  // right here over SSE) or the desktop app for true fire-and-forget runs.
  try {
    const job = await invoke<JobView>("jobs.task", { goal: g, sandbox });
    selectAgentJob(job.id);
    toast("Autonomous run started", "success");
    await refreshJobs();
    await refreshAgents();
  } catch {
    toast(
      "Background autonomous runs aren't available on the hosted web yet. Use Build here to run the agent in this session, or the desktop app for fire-and-forget runs.",
      "error",
      { label: "Build here", onClick: () => { state.capability = "agent"; render(); } },
    );
  }
}
function selectAgentJob(jobId: string): void {
  agentDetailReq += 1;
  state.job = jobId;
  state.agentDetail = null;
  state.agentDetailSync = null;
  state.agentDetailError = null;
  state.agentTimeline = [];
  state.agentTimelineMaxSeq = -1;
  state.agentSteerDraft = "";
  state.agentSandboxDiff = null;
  state.agentActionNote = null;
  state.agentActionError = null;
  void loadAgentDetail(jobId, true);
  updateRail();
}

async function runAgentAction(name: string, work: () => Promise<void>): Promise<void> {
  state.agentBusyAction = name;
  state.agentActionError = null;
  state.agentActionNote = null;
  updateRail();
  try {
    await work();
  } catch (e) {
    state.agentActionError = e instanceof Error ? e.message : String(e);
  } finally {
    state.agentBusyAction = null;
    updateRail();
  }
}

// ── files & diffs ─────────────────────────────────────────────────────────────
async function openFile(path: string): Promise<void> {
  // Guard: warn before discarding unsaved edits.
  if (state.openFile && state.editBuffer !== null && state.editBuffer !== state.openFile.content) {
    if (!(await inlineConfirm(`You have unsaved changes in ${state.openFile.path}. Discard?`))) return;
  }
  invalidateDiskRefresh();
  destroyWebEditor();
  state.openDiff = null;
  state.editBuffer = null;
  state.fileDiskChanged = false;
  const f = await withAuth(() => invoke<{ path: string; content: string }>("fs.read", { path }), null);
  state.openFile = f;
  if (f) state.editBuffer = f.content;
  // Markdown + rich-text formats open in rendered preview by default; code opens as source.
  const ext = path.includes(".") ? path.slice(path.lastIndexOf(".") + 1).toLowerCase() : "";
  state.filePreview = ["md", "markdown", "mdx"].includes(ext);
  updateRail();
}

/**
 * Snapshot the live kernel (fallback: editBuffer), write once, then mark the
 * written snapshot saved. Typing during the await stays a newer dirty draft.
 */
async function saveFile(): Promise<void> {
  const result = await saveOpenFile(state, async (path, content) => {
    await invoke("fs.write", { path, content });
  }, { onSaving: () => updateRail() });
  if (result.kind === "written") {
    toast(`Saved ${result.path}`, "success");
    void refreshChanges();
  } else if (result.kind === "failed") {
    toast(result.error, "error");
  }
  if (result.kind !== "skipped") updateRail();
}
async function openDiff(path: string): Promise<void> {
  invalidateDiskRefresh();
  destroyWebEditor();
  state.openFile = null;
  state.editBuffer = null;
  state.fileDiskChanged = false;
  const d = await withAuth(() => invoke<{ diff: string }>("git.diff", { path }), null);
  state.openDiff = { path, diff: d?.diff?.trim() || "No textual diff (binary or unchanged)." };
  updateRail();
}
/** Reject a proposed change — restore the file to HEAD (or remove a new file). */
async function discardFile(path: string): Promise<void> {
  if (!(await inlineConfirm(`Discard changes to ${path}? This restores it to the last commit and cannot be undone.`))) return;
  const r = await withAuth(() => invoke<{ ok: boolean; error: string | null }>("git.revertFile", { path }), { ok: false, error: "failed" });
  if (!r.ok) { toast(r.error ?? "Could not discard the change", "error"); return; }
  if (state.openDiff?.path === path) state.openDiff = null;
  toast(`Discarded ${path}`, "success");
  await refreshChanges();
  updateRail();
}
async function loadTree(force = false): Promise<void> {
  if (state.tree && !force) return;
  // fs.tree returns an array of root-level entries (not a wrapper node) —
  // type matches the server's IPC handler at packages/desktop/src/ipc.ts.
  state.tree = await withAuth(() => invoke<FileTreeNode[]>("fs.tree"), null);
  await refreshOpenEditorFromDisk();
  updateRail();
}

/** Agent / external write: clean buffer tracks disk; dirty buffer is kept. */
async function refreshOpenEditorFromDisk(): Promise<void> {
  if (!state.openFile) return;
  const path = state.openFile.path;
  const token = beginDiskRefresh(path);
  const f = await withAuth(() => invoke<{ path: string; content: string }>("fs.read", { path }), null);
  if (!f) return;
  const result = applyDiskRead(state, token, f);
  if (result === "ignored") return;
  paintFileEditorChrome();
}

/** Explicit Reload from disk. Confirms when the live buffer is dirty; never silent. */
async function reloadOpenFileFromDisk(): Promise<void> {
  if (!state.openFile) return;
  const path = state.openFile.path;
  const editor = currentWebEditor();
  const buffer = editor && editor.path === path ? editor.getDocument() : (state.editBuffer ?? state.openFile.content);
  const dirty = buffer !== state.openFile.content;
  if (dirty) {
    if (!(await inlineConfirm(`Reload ${path} from disk? Unsaved edits will be discarded.`))) return;
  }
  const token = beginDiskRefresh(path);
  const f = await withAuth(() => invoke<{ path: string; content: string }>("fs.read", { path }), null);
  if (!f || !isLatestDiskRefresh(token, state.openFile?.path)) return;
  const live = currentWebEditor();
  if (live && live.path === path) live.applyFromDisk(f.content);
  state.openFile = f;
  state.editBuffer = f.content;
  state.fileDiskChanged = false;
  updateRail();
}

function keepDiskVersion(): void {
  currentWebEditor()?.dismissDiskChanged();
  state.fileDiskChanged = false;
  paintFileEditorChrome();
}

// ── tabs / providers ───────────────────────────────────────────────────────
function setTab(tab: Tab): void {
  state.tab = tab;
  if (tab === "files") void loadTree(true); // always fetch fresh — files change as the agent works
  if (tab === "runs") {
    void refreshJobs();
    void refreshAgents();
  }
  if (tab === "changes") { void refreshChanges(); void refreshJobs(); }
  if (tab === "team") void refreshTeams();
  // First time the Preview tab opens, point it at a live dev server if one is up.
  if (tab === "preview" && !state.previewAutoDetected) { state.previewAutoDetected = true; void autoDetectPreview(); }
  startAgentsPolling(tab === "runs");
  render();
}

async function refreshTeams(): Promise<void> {
  const r = await withAuth(() => listTeams(), { teams: [], canCreate: false });
  state.teams = r.teams;
  state.canCreateTeam = r.canCreate;
  if (!state.activeTeamId && r.teams.length > 0) state.activeTeamId = r.teams[0]!.id;
  if (state.activeTeamId) {
    state.teamMembers = await withAuth(() => listTeamMembers(state.activeTeamId!), []);
    state.teamMemories = await withAuth(() => listTeamMemories(state.activeTeamId!), []);
  }
  updateRail();
}
async function activateProvider(id: string): Promise<void> {
  await withAuth(() => invoke("providers.activate", { provider: id }), undefined);
  await loadStatus();
  updateTopbar();
}

// ════════════════════════════════ rendering ════════════════════════════════
/** Topbar project/branch switcher — project name over the active git branch. */
function projectSwitcher(): HTMLElement {
  const name = activeProjectView()?.name ?? state.projectName ?? "Workspace";
  const env = state.gitBranch ?? (state.isRepo ? "detached" : "local");
  const pill = el("button", { class: "projpill", title: state.projectsMulti ? "Projects & branch" : "Project & branch" }, [
    el("span", { class: "projpill-icon", html: ICON.box }),
    el("span", { class: "projpill-text" }, [
      el("span", { class: "projpill-name" }, [name]),
      el("span", { class: "projpill-env" }, [env]),
    ]),
    el("span", { class: "projpill-caret", html: ICON.chevron }),
  ]);
  pill.onclick = () => showBranchMenu(pill);
  return pill;
}

/** The project this page is working in (hosted multi-user), else null. */
function activeProjectView(): WebProjectView | null {
  if (!state.projectsMulti) return null;
  const id = getActiveProject();
  return state.projects.find((p) => p.id === id) ?? null;
}

/**
 * Settle which project this page works in, BEFORE anything per-project
 * loads. The stored id is checked against the server's list: one that is
 * gone (deleted in another tab, another account on this browser) is
 * replaced by the server's most recently used project. A single-tenant
 * server has one project and no header. Returns false when the page must
 * reload, because state keyed by the old id was already read at load.
 */
async function settleActiveProject(): Promise<boolean> {
  const before = getActiveProject();
  try {
    const list = await listProjects();
    state.projects = list.projects;
    state.projectsMulti = list.multi;
    state.projectsMax = list.max;
    const after = list.multi ? (list.projects.some((p) => p.id === before) ? before : list.active) : "";
    setActiveProject(after);
    if (projectScopedKey("x", before) !== projectScopedKey("x", after)) {
      window.location.reload();
      return false;
    }
  } catch {
    // An older server without /api/projects: one project, as before.
    state.projectsMulti = false;
  }
  return true;
}

async function refreshProjects(): Promise<void> {
  try {
    const list = await listProjects();
    state.projects = list.projects;
    state.projectsMulti = list.multi;
    state.projectsMax = list.max;
  } catch { /* keep what we have */ }
  updateTopbar();
}

/**
 * Open another project. A page reload rebuilds sessions, files, preview and
 * changes from the new project — nothing from the old one can leak into it.
 * Refused while this tab is running a turn: its stream belongs to the
 * project it started in.
 */
function switchProject(id: string): void {
  if (id === getActiveProject()) return;
  if (state.run) {
    toast("A turn is running — stop it or let it finish before switching projects.", "error");
    return;
  }
  setActiveProject(id);
  window.location.assign(window.location.pathname);
}

async function newProjectFlow(): Promise<void> {
  if (state.projectsMax > 0 && state.projects.length >= state.projectsMax) {
    toast(`Your plan allows ${String(state.projectsMax)} projects — delete one or upgrade to add another.`, "error");
    return;
  }
  const name = (await inlinePrompt("New project name"))?.trim();
  if (!name) return;
  try {
    const { project } = await createProject(name);
    toast(`Created ${project.name}`, "success");
    switchProject(project.id);
  } catch (e) {
    if (e instanceof AuthError) { promptToken(); return; }
    toast(e instanceof Error ? e.message : "could not create the project", "error");
  }
}

async function renameProjectFlow(p: WebProjectView): Promise<void> {
  const name = (await inlinePrompt("Rename project", p.name))?.trim();
  if (!name || name === p.name) return;
  try {
    await renameProject(p.id, name);
    await refreshProjects();
  } catch (e) {
    if (e instanceof AuthError) { promptToken(); return; }
    toast(e instanceof Error ? e.message : "could not rename the project", "error");
  }
}

async function deleteProjectFlow(p: WebProjectView): Promise<void> {
  if (state.projects.length <= 1) {
    toast("You cannot delete your only project.", "error");
    return;
  }
  const ok = await inlineConfirm(`Delete "${p.name}"? Its files move to the trash, and you can restore it from this menu. Its sessions close and any live shares of them end.`);
  if (!ok) return;
  try {
    const out = await deleteProject(p.id);
    toast(`Deleted ${p.name}`, "success");
    if (p.id === getActiveProject()) {
      setActiveProject(out.active);
      window.location.assign(window.location.pathname);
      return;
    }
    await refreshProjects();
  } catch (e) {
    if (e instanceof AuthError) { promptToken(); return; }
    toast(e instanceof Error ? e.message : "could not delete the project", "error");
  }
}

/** Deleted projects, newest first; picking one brings it back and opens it. */
async function restoreProjectFlow(): Promise<void> {
  // The menu that offered this has closed; anchor to the project pill.
  const anchor = document.querySelector<HTMLElement>(".projpill") ?? document.body;
  let trash: Awaited<ReturnType<typeof listProjectTrash>>["trash"];
  try {
    trash = (await listProjectTrash()).trash;
  } catch (e) {
    if (e instanceof AuthError) { promptToken(); return; }
    toast(e instanceof Error ? e.message : "could not read the trash", "error");
    return;
  }
  if (trash.length === 0) { toast("There is no deleted project to restore.", "info"); return; }
  popover(anchor, [
    el("div", { class: "menu-head" }, ["Restore a deleted project"]),
    ...trash.slice(0, 12).map((t) =>
      menuItem("↺", `${t.name} · deleted ${new Date(t.deletedAt).toLocaleDateString()}`, () => {
        void (async () => {
          try {
            const { project } = await restoreProject(t.key);
            toast(`Restored ${project.name}`, "success");
            switchProject(project.id);
          } catch (e) {
            if (e instanceof AuthError) { promptToken(); return; }
            toast(e instanceof Error ? e.message : "could not restore the project", "error");
          }
        })();
      }),
    ),
  ]);
}

/** The project rows at the top of the switcher (hosted multi-user only). */
function projectMenuRows(): HTMLElement[] {
  if (!state.projectsMulti) return [];
  const active = activeProjectView();
  const rows: HTMLElement[] = [
    el("div", { class: "menu-head" }, [`Projects · ${String(state.projects.length)} of ${String(state.projectsMax)}`]),
  ];
  for (const p of state.projects) {
    const on = p.id === active?.id;
    const b = el("button", { class: `menu-item proj-row ${on ? "proj-on" : ""}`, title: on ? "Current project" : `Open ${p.name}` }, [
      el("span", { class: "menu-item-icon" }, [on ? "●" : "○"]),
      el("span", { class: "proj-name" }, [p.name]),
      ...(on ? [el("span", { class: "branch-cur" }, ["current"])] : []),
    ]);
    b.onclick = () => { closePopover(); switchProject(p.id); };
    rows.push(b);
  }
  rows.push(menuItem("+", "New project…", () => void newProjectFlow()));
  rows.push(menuItem("↺", "Restore a deleted project…", () => void restoreProjectFlow()));
  if (active) {
    rows.push(menuItem("✎", "Rename this project…", () => void renameProjectFlow(active)));
    if (state.projects.length > 1) {
      const del = menuItem("✕", "Delete this project…", () => void deleteProjectFlow(active));
      del.classList.add("menu-danger");
      rows.push(del);
    }
  }
  rows.push(el("div", { class: "menu-sep" }));
  return rows;
}

/**
 * Project/branch popover. Read-only on web by design: branch *switching*
 * (git.checkout) can fire post-checkout hooks server-side and is deliberately
 * off the web allowlist, so we surface the branches for context and point at
 * the desktop app / CLI to switch.
 */
function showBranchMenu(anchor: HTMLElement): void {
  const projectRows = projectMenuRows();
  if (!state.isRepo) {
    popover(anchor, [
      ...projectRows,
      el("div", { class: "menu-note" }, ["Local project — not a git repository."]),
      menuItem("◆", "Project profile…", () => void openProjectProfile()),
      menuItem("▤", "Create slides…", () => openSlides()),
    ]);
    return;
  }
  const rows = state.branches.length
    ? state.branches.map((b) => el("div", { class: `branch-row ${b === state.gitBranch ? "branch-on" : ""}` }, [
        el("span", { class: "branch-mark" }, [b === state.gitBranch ? "●" : "○"]),
        el("span", { class: "branch-name" }, [b]),
        ...(b === state.gitBranch ? [el("span", { class: "branch-cur" }, ["current"])] : []),
      ]))
    : [el("div", { class: "menu-note" }, ["No branches found."])];
  popover(anchor, [
    ...projectRows,
    el("div", { class: "menu-head" }, [`Branches · ${state.branches.length || "—"}`]),
    ...rows,
    el("div", { class: "menu-note" }, ["Switch branches from the Tavsin desktop app or CLI."]),
    menuItem("◆", "Project profile…", () => void openProjectProfile()),
    menuItem("▤", "Create slides…", () => openSlides()),
  ]);
}

/** Slides: topic in → real .pptx out (saved to the project + downloaded). */
function openSlides(): void {
  const topic = el("input", { class: "field", placeholder: "What should the deck be about?" }) as HTMLInputElement;
  const count = el("select", { class: "field" }, [4, 6, 8, 10, 12].map((n) => el("option", { value: String(n) }, [`${n} content slides`]))) as HTMLSelectElement;
  count.value = "6";
  const note = el("p", { class: "drawer-note" }, ["Tavsin drafts the deck with your model and saves a real .pptx into the project's slides/ folder — it also downloads here. Works in PowerPoint, Keynote, and Google Slides."]);
  const err = el("p", { class: "drawer-note", style: "color:var(--red);display:none" }, []);
  const go = el("button", { class: "btn btn-primary" }, ["Generate deck"]) as HTMLButtonElement;
  go.onclick = async () => {
    const t = topic.value.trim();
    if (!t) return;
    go.disabled = true;
    go.textContent = "Drafting your deck…";
    err.style.display = "none";
    try {
      const r = await withAuth(
        () => invoke<{ fileName: string; base64: string; slideCount: number; path: string }>("slides.generate", { topic: t, count: Number(count.value) }),
        null,
      );
      if (!r) throw new Error("generation failed");
      const bytes = Uint8Array.from(atob(r.base64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.presentationml.presentation" });
      const a = el("a", { href: URL.createObjectURL(blob), download: r.fileName }) as HTMLAnchorElement;
      a.click();
      URL.revokeObjectURL(a.href);
      closeModal();
      toast(`Deck saved — ${r.slideCount} slides → ${r.path} (and downloaded)`, "success");
      void loadTree(true);
    } catch (e) {
      err.textContent = e instanceof Error ? e.message : "generation failed";
      err.style.display = "block";
      go.disabled = false;
      go.textContent = "Generate deck";
    }
  };
  topic.onkeydown = (e) => { if (e.key === "Enter") go.click(); };
  showModal("Create slides", el("div", { class: "settings" }, [note, topic, count, err, el("div", { style: "display:flex;justify-content:flex-end" }, [go])]));
}

function providerPill(): HTMLElement {
  const ok = state.providerOk;
  if (!ok) {
    const btn = el("button", { class: "connect-btn", title: "Connect a model provider" }, [el("span", { class: "glyph", html: ICON.wave }), "Connect model", el("span", { class: "connect-dot" })]);
    btn.onclick = () => openSettings("openrouter");
    return btn;
  }
  const pill = el("button", { class: "pill pill-ok", title: `Model provider: ${state.resolvedProvider}` }, [el("span", { class: "dot" }), state.resolvedProvider]);
  pill.onclick = () => showProviderMenu(pill);
  return pill;
}
/** Switch/activate a configured provider, or open Settings to add a key. */
function showProviderMenu(anchor: HTMLElement): void {
  const configured = state.providers.filter((p) => p.configured);
  const rows = configured.map((p) => menuItem(p.id === state.resolvedProvider ? "●" : "○", p.defaultModel ? `${p.id} · ${p.defaultModel}` : p.id, () => void activateProvider(p.id)));
  rows.push(menuItem("🔑", "Add / change API key…", () => openSettings(state.resolvedProvider !== "—" ? state.resolvedProvider : "openrouter")));
  popover(anchor, [el("div", { class: "menu-head" }, ["Active model"]), ...rows]);
}
/** Days until an ISO reset instant (0 if past/absent). */
function daysUntil(iso: string | undefined): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.ceil((t - Date.now()) / 86_400_000));
}

/** What the always-visible pill shows: prefer "remaining", fall back to spend. */
function usageHeadline(): { label: string; low: boolean } {
  const q = state.quota;
  const u = state.usage;
  // Managed: what the plan actually meters. Credits take over once an
  // allowance is used up, so the pill shows the balance then. (The old
  // "N left" counted only premium-model requests, so the free model never
  // moved it — a number that did not mean what it said.)
  if (q && !q.byok) {
    const bal = q.balanceUsd ?? 0;
    if (q.metered && (q.onCredits || q.exceeded)) {
      const t = q.balanceTokens ?? 0;
      return { label: t > 0 ? `${fmtTokens(t)} purchased tokens` : "Out of tokens", low: t < 100_000 };
    }
    if (q.onCredits || q.exceeded) return { label: `${fmtUsd(bal)} credits`, low: bal < 1 };
    if (typeof q.tokensUsed === "number" && typeof q.tokensLimit === "number" && q.tokensLimit > 0) {
      const left = Math.max(0, q.tokensLimit - q.tokensUsed);
      return { label: `${fmtTokens(left)} tokens left`, low: left / q.tokensLimit <= 0.1 };
    }
    if (q.costLimit !== null) {
      const left = Math.max(0, q.costLimit - q.costUsed);
      return { label: `${fmtUsd(left)} left`, low: q.costLimit > 0 && left / q.costLimit <= 0.1 };
    }
  }
  // User-set monthly budget cap.
  if (u && u.monthlyBudgetUsd) {
    const left = Math.max(0, u.monthlyBudgetUsd - u.month.costUsd);
    return { label: `${fmtUsd(left)} left`, low: left / u.monthlyBudgetUsd <= 0.1 };
  }
  // BYOK / unlimited: show spend this month.
  return { label: u ? `${fmtUsd(u.month.costUsd)} this mo` : "—", low: false };
}

function fmtTokens(n: number): string {
  return n >= 1_000_000 ? `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M` : n >= 1_000 ? `${Math.round(n / 1_000)}k` : String(n);
}

/**
 * BUY CREDITS — one dialog, every plan. Credits are a prepaid balance that
 * never expires and pays for usage beyond the plan, on any priced model, at
 * the model's real cost. Free users can buy them too: making someone upgrade
 * a subscription to finish one more task is the wrong default.
 */
function showBuyCredits(reason?: string): void {
  if (!state.billingEnabled) {
    toast("Buying credits is not available on this server — add your own API key in Settings instead.", "info");
    return;
  }
  // On a managed host credits are sold in the plan's unit: tokens.
  const perUsd = isManagedInference() ? state.managedCatalog?.creditTokensPerUsd ?? 0 : 0;
  if (perUsd > 0) {
    showBuyTokens(perUsd, reason);
    return;
  }
  const bal = state.quota?.balanceUsd ?? 0;
  const amounts = [5, 10, 25, 50, 100];
  const grid = el("div", { class: "credit-grid" }, amounts.map((usd) => {
    const b = el("button", { class: "credit-pack", type: "button" }, [el("span", { class: "credit-amt" }, [`$${String(usd)}`]), el("span", { class: "credit-sub" }, ["credits"])]);
    b.onclick = async () => {
      try {
        window.location.href = await buyCredits(usd);
      } catch (e) {
        toast(e instanceof Error ? e.message : String(e), "error");
      }
    };
    return b;
  }));
  showModal("Buy credits", el("div", {}, [
    el("p", { class: "drawer-note", style: "margin-top:0" }, [`Balance: ${fmtUsd(bal)}. Credits never expire. They are used only after your plan's included usage runs out, at each model's real price — you can see every call on the usage receipt under each answer.`]),
    grid,
    el("p", { class: "drawer-note" }, ["Paid securely with Stripe. Prefer your own provider bill? Add your own API key in Settings — then nothing is metered."]),
  ]));
}

/**
 * BUY TOKENS — the managed host's top-up. Shown on its own when a turn is
 * refused for an empty allowance (the reason leads), and from the usage pill
 * and Settings. Each pack says how many tokens it adds; purchased tokens never
 * expire and are used only once the plan's monthly tokens are gone.
 */
function showBuyTokens(perUsd: number, reason?: string): void {
  const q = state.quota;
  const packs = [5, 10, 25, 50, 100];
  const grid = el("div", { class: "credit-grid" }, packs.map((usd) => {
    const b = el("button", { class: "credit-pack", type: "button" }, [
      el("span", { class: "credit-amt" }, [`${fmtTokens(usd * perUsd)} tokens`]),
      el("span", { class: "credit-sub" }, [`$${String(usd)}`]),
    ]);
    b.onclick = async () => {
      try {
        window.location.href = await buyCredits(usd);
      } catch (e) {
        toast(e instanceof Error ? e.message : String(e), "error");
      }
    };
    return b;
  }));
  const planLine = q && typeof q.tokensLimit === "number"
    ? `This month: ${fmtTokens(q.tokensUsed ?? 0)} of ${fmtTokens(q.tokensLimit)} plan tokens used · resets in ${String(daysUntil(q.monthEndIso))} day${daysUntil(q.monthEndIso) === 1 ? "" : "s"}. Purchased: ${fmtTokens(q.balanceTokens ?? 0)}.`
    : `Purchased tokens: ${fmtTokens(q?.balanceTokens ?? 0)}.`;
  // Free: pick a plan. Paid: a bigger plan is a change to the existing
  // subscription, which the Stripe portal does without a second checkout.
  const upgrade = !state.billingEnabled || state.billingPlans.length === 0 || !state.user
    ? []
    : state.user.tier === "local"
      ? [(() => { const b = el("button", { class: "btn btn-primary" }, ["See plans — more tokens every month"]); b.onclick = () => showPlans(); return b; })()]
      : state.user.tier === "enterprise" || state.user.tier === "ultra"
        ? []
        : [(() => { const b = el("button", { class: "btn" }, ["Move to a bigger plan"]); b.onclick = async () => { try { window.location.href = await openBillingPortal(); } catch (e) { toast(e instanceof Error ? e.message : String(e), "error"); } }; return b; })()];
  showModal(reason ? "You're out of tokens" : "Buy tokens", el("div", {}, [
    ...(reason ? [el("p", { class: "drawer-note", style: "margin-top:0" }, [reason])] : []),
    el("p", { class: "drawer-note", style: reason ? "" : "margin-top:0" }, [planLine]),
    grid,
    ...upgrade,
    el("p", { class: "drawer-note" }, ["Purchased tokens never expire and work on every model. They are used only after your plan's monthly tokens run out. Paid securely with Stripe."]),
  ]));
}

function usagePill(): HTMLElement {
  const { label, low } = usageHeadline();
  const pill = el("button", { class: `pill pill-spend${low ? " pill-spend-low" : ""}`, title: "Usage & spend — click for the breakdown" }, [
    el("span", { class: "spend-ico", html: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><circle cx="12" cy="12" r="9"/><path d="M12 7v10M9.5 9.2c0-1 1.1-1.7 2.5-1.7s2.5.7 2.5 1.7-1.1 1.6-2.5 1.6-2.5.7-2.5 1.7 1.1 1.7 2.5 1.7 2.5-.7 2.5-1.7" stroke-linecap="round"/></svg>` }),
    el("span", {}, [label]),
  ]);
  pill.onclick = () => showUsagePopover(pill);
  return pill;
}

/** The always-available usage breakdown: remaining, weekly, monthly, resets. */
function showUsagePopover(anchor: HTMLElement): void {
  const q = state.quota;
  const u = state.usage;
  const rows: HTMLElement[] = [el("div", { class: "menu-head" }, ["Usage & spend"])];

  const line = (k: string, v: string): HTMLElement =>
    el("div", { class: "usage-line" }, [el("span", { class: "usage-k" }, [k]), el("span", { class: "usage-v" }, [v])]);

  if (q && !q.byok) {
    rows.push(line("Plan", q.tier.charAt(0).toUpperCase() + q.tier.slice(1)));
    if (typeof q.tokensUsed === "number" && typeof q.tokensLimit === "number") rows.push(line("Plan tokens", `${fmtTokens(q.tokensUsed)} of ${fmtTokens(q.tokensLimit)}`));
    if (q.metered) rows.push(line("Purchased tokens", `${fmtTokens(q.balanceTokens ?? 0)}${q.onCredits ? " · in use" : ""}`));
    else rows.push(line("Credits", `${fmtUsd(q.balanceUsd ?? 0)}${q.onCredits ? " · in use" : ""}`));
    rows.push(line("Resets in", `${daysUntil(q.monthEndIso)} day${daysUntil(q.monthEndIso) === 1 ? "" : "s"}`));
  } else if (q && q.byok) {
    rows.push(line("Plan", "Your own API key (no limit)"));
  }
  if (q && q.costLimit !== null) {
    rows.push(line("Spend cap", `${fmtUsd(q.costUsed)} of ${fmtUsd(q.costLimit)}`));
  }
  if (u) {
    if (u.week) rows.push(line("This week", fmtUsd(u.week.costUsd)));
    rows.push(line("This month", fmtUsd(u.month.costUsd)));
    rows.push(line("Today", fmtUsd(u.today.costUsd)));
    if (u.monthlyBudgetUsd) rows.push(line("Monthly budget", fmtUsd(u.monthlyBudgetUsd)));
    const saved = u.compression?.savedTokens ?? 0;
    if (saved > 0) rows.push(line("Compression saved", `~${saved.toLocaleString()} tokens (−${u.compression?.savedTokenPct ?? 0}%)`));
  }
  if (rows.length === 1) rows.push(el("div", { class: "menu-note" }, ["No usage recorded yet."]));

  // "Open Usage & spend" pointed at a tab with no usage in it. The two things
  // a reader of this popover can actually do: buy credits, or use their own key.
  const actions = [
    ...(q && !q.byok && state.billingEnabled ? [menuItem("+", q.metered ? "Buy tokens" : "Buy credits", () => showBuyCredits())] : []),
    menuItem("↗", q?.byok ? "Model & API key" : "Use your own API key", () => openSettings()),
  ];
  popover(anchor, [...rows, ...actions]);
}
function bellBtn(): HTMLElement {
  const btn = el("button", { class: "iconbtn", title: "Notifications", "aria-label": "Notifications" }, [
    el("span", { class: "glyph", html: ICON.bell }),
    ...(state.notifUnread > 0 ? [el("span", { class: "iconbtn-dot" })] : []),
  ]);
  btn.onclick = () => showNotifPanel(btn);
  return btn;
}
function helpBtn(): HTMLElement {
  const btn = el("button", { class: "iconbtn", title: "Help & shortcuts", "aria-label": "Help" }, [el("span", { class: "glyph", html: ICON.help })]);
  btn.onclick = () => showHelpMenu(btn);
  return btn;
}
function topbar(): HTMLElement {
  return el("header", { class: "topbar", id: "topbar" }, topbarChildren());
}
function topbarChildren(): Array<Node> {
  const children: Array<Node> = [
    el("div", { class: "brand" }, [el("span", { class: "logo" }, [logoMark(26)]), el("span", { class: "brand-name" }, ["TAVSIN"]), el("span", { class: "brand-sub" }, ["Vibe Coding"])]),
    projectSwitcher(),
    el("div", { class: "spacer" }),
    usagePill(),
    providerPill(),
    bellBtn(),
    helpBtn(),
  ];
  // Show real user identity + tier when known (multi-user mode).
  if (state.user) {
    const u = state.user;
    const display = u.displayName?.trim() || u.email.split("@")[0] || "you";
    const initials = display.split(/\s+/).map((p) => p[0] ?? "").join("").slice(0, 2).toUpperCase() || "T";
    const tierLabel = u.tier === "local" ? "Free" : u.tier.charAt(0).toUpperCase() + u.tier.slice(1);

    const userPill = el("button", { class: `pill pill-user pill-tier-${u.tier}`, title: `${u.email} · ${tierLabel}` }, [
      el("span", { class: "avatar" }, [initials]),
      el("span", { class: "pill-user-name" }, [display]),
      el("span", { class: "tier" }, [tierLabel]),
    ]);
    userPill.onclick = () => showAccountMenu(u);
    children.push(userPill);
  } else if (state.authRequired) {
    // Static-token / owner-password mode — no user object, just a sign-out.
    const out = el("button", { class: "pill", title: "Sign out" }, ["⎋ Sign out"]);
    out.onclick = () => void signOut();
    children.push(out);
  }
  return children;
}

/** Notification center popover — real notifications.* channels. */
function showNotifPanel(anchor: HTMLElement): void {
  const header = el("div", { class: "notif-head" }, [
    el("span", {}, ["Notifications"]),
    (() => {
      const clear = el("button", { class: "notif-action" }, ["Mark all read"]);
      clear.onclick = async () => {
        await withAuth(() => invoke("notifications.markAllRead"), undefined);
        await loadNotifications();
        closePopover();
      };
      return clear;
    })(),
  ]);
  const rows = state.notifications.length
    ? state.notifications.map((n) =>
        el("div", { class: `notif ${n.read ? "" : "notif-unread"}` }, [
          el("span", { class: `notif-dot notif-${n.level}` }),
          el("div", { class: "notif-body" }, [
            el("div", { class: "notif-title" }, [n.title]),
            ...(n.body ? [el("div", { class: "notif-text" }, [n.body])] : []),
            el("div", { class: "notif-time" }, [relTime(n.createdAt)]),
          ]),
        ]),
      )
    : [el("div", { class: "menu-note" }, ["You're all caught up."])];
  popover(anchor, [header, el("div", { class: "notif-list" }, rows), phoneSection()]);
  // Opening the center marks everything read.
  if (state.notifUnread > 0) {
    void withAuth(() => invoke("notifications.markAllRead"), undefined).then(() => loadNotifications());
  }
}

/** Help menu — docs, MCP, keyboard shortcuts. */
function showHelpMenu(anchor: HTMLElement): void {
  popover(anchor, [
    menuItem("🔑", "Add / change API key", () => openSettings(state.resolvedProvider !== "—" ? state.resolvedProvider : "openrouter")),
    menuItem("👤", "Your profile", () => void openProfile()),
    menuItem("⌨", "Keyboard shortcuts", () => showShortcuts()),
    menuItem("⚗", "About Tavsin", () => openAppSettings()),
    menuItem("🔌", "Connect over MCP", () => showModal("Plugins & tools", el("div", { class: "drawer" }, [
      el("p", { class: "drawer-note" }, ["Tavsin exposes its graph, memory, and outcome tools over MCP — connect from Claude Desktop, Cursor, or Zed to drive this project's intelligence."]),
    ]))),
    menuItem("↗", "Docs & pricing", () => window.open("https://tavsin.xyz/pricing", "_blank", "noopener")),
  ]);
}
function updateTopbar(): void {
  const t = document.getElementById("topbar");
  if (t) t.replaceChildren(...topbarChildren());
}

// ── far-left icon rail (workspace navigation) ──
function iconRail(): HTMLElement {
  return el("nav", { class: "iconrail", id: "iconrail" }, iconRailChildren());
}
function railIcon(icon: string, label: string, onClick: () => void, active: boolean): HTMLElement {
  const b = el("button", { class: `railbtn ${active ? "railbtn-on" : ""}`, title: label, "aria-label": label }, [
    el("span", { class: "railbtn-glyph", html: icon }),
  ]);
  b.onclick = onClick;
  return b;
}
function iconRailChildren(): HTMLElement[] {
  const onHome = state.turns.length === 0;
  return [
    el("div", { class: "iconrail-brand" }, [logoMark(26)]),
    el("div", { class: "iconrail-group" }, [
      railIcon(ICON.home, "Home — start a new build", () => { void newSession(); }, onHome),
      railIcon(ICON.code, "Files", () => setTab("files"), state.tab === "files"),
      railIcon(ICON.graph, "Intelligence graph", () => setTab("intel"), state.tab === "intel"),
      ...(playgroundUnavailable("jobsBackground") ? [] : [railIcon(ICON.clock, "Runs", () => setTab("runs"), state.tab === "runs")]),
      railIcon(ICON.gauge, "Orchestration — performance, decisions, procedures", () => setTab("orchestration"), state.tab === "orchestration"),
      railIcon(ICON.git, "Changes", () => setTab("changes"), state.tab === "changes"),
      railIcon(ICON.preview, "Live preview", () => setTab("preview"), state.tab === "preview"),
      railIcon(ICON.rocket, "Deploy", () => setTab("deploy"), state.tab === "deploy"),
    ]),
    el("div", { class: "spacer" }),
    railIcon(ICON.sliders, "Settings", openAppSettings, false),
    accountRailBtn(),
    themeToggleRailBtn(),
  ];
}

/** Account avatar at the bottom of the icon rail (opens the account menu). */
function accountRailBtn(): HTMLElement {
  const u = state.user;
  const display = u ? (u.displayName?.trim() || u.email.split("@")[0] || "you") : "You";
  const initials = display.split(/\s+/).map((p) => p[0] ?? "").join("").slice(0, 2).toUpperCase() || "T";
  const b = el("button", { class: "rail-avatar", title: u ? u.email : "Account" }, [initials]);
  b.onclick = () => { if (u) showAccountMenu(u); else openAppSettings(); };
  return b;
}

/** Light/dark toggle at the very bottom of the icon rail. */
function themeToggleRailBtn(): HTMLElement {
  const isLight = currentTheme() === "light";
  const b = el("button", { class: "railbtn rail-theme", title: isLight ? "Switch to dark theme" : "Switch to light theme" }, [
    el("span", { class: "railbtn-glyph", html: isLight ? ICON.moon : ICON.sun }),
  ]);
  b.onclick = () => { applyTheme(isLight ? "dark" : "light"); render(); };
  return b;
}

// Inline stroke icons (currentColor) — crisp at rail size, theme-agnostic.
const ICON = {
  home: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V20h5v-6h4v6h5V9.5"/></svg>`,
  code: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m8 8-5 4 5 4"/><path d="m16 8 5 4-5 4"/></svg>`,
  graph: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>`,
  clock: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>`,
  gauge: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 18a8 8 0 1 1 16 0"/><path d="m12 14 4-4"/></svg>`,
  git: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="6" r="2.5"/><circle cx="6" cy="18" r="2.5"/><circle cx="18" cy="8" r="2.5"/><path d="M6 8.5v7"/><path d="M18 10.5c0 4-6 1.5-6 5"/></svg>`,
  preview: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2.5" y="4" width="19" height="13" rx="2"/><path d="M8 21h8M12 17v4"/></svg>`,
  rocket: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3c3.5 1.5 5.5 4.5 5.5 9 0 2-1 4-2.5 5.5H9C7.5 16 6.5 14 6.5 12 6.5 7.5 8.5 4.5 12 3Z"/><circle cx="12" cy="10" r="1.6"/><path d="M9 17.5 7 21m8-3.5L17 21"/></svg>`,
  sliders: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h8M16 18h4"/><circle cx="15" cy="6" r="2"/><circle cx="7" cy="12" r="2"/><circle cx="13" cy="18" r="2"/></svg>`,
  bell: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6"/><path d="M10.5 19a1.8 1.8 0 0 0 3 0"/></svg>`,
  help: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 .9-1 1.7"/><path d="M12 16.5h.01"/></svg>`,
  wave: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12c2.5-4 5-4 7.5 0s5 4 7.5 0 3-3 5-1.5"/></svg>`,
  chevron: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>`,
  chevronLeft: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 6-6 6 6 6"/></svg>`,
  chevronRight: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 6 6 6-6 6"/></svg>`,
  pencil: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>`,
  ask: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.6 9.5a2.4 2.4 0 1 1 3.3 2.2c-.7.4-.9.8-.9 1.5"/><path d="M12 16h.01"/></svg>`,
  build: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m8 8-4 4 4 4"/><path d="m16 8 4 4-4 4"/></svg>`,
  refactor: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3.5 14 9l5.5 2-5.5 2-2 5.5-2-5.5L4.5 11 10 9Z"/></svg>`,
  spark: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.5 13.7 9 20 10.7 13.7 12.4 12 19l-1.7-6.6L4 10.7 10.3 9Z"/></svg>`,
  plus: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>`,
  refresh: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 0 1 15.5-6.3L21 8"/><path d="M21 4v4h-4"/><path d="M21 12a9 9 0 0 1-15.5 6.3L3 16"/><path d="M3 20v-4h4"/></svg>`,
  box: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 4 7v10l8 4 8-4V7Z"/><path d="M4 7l8 4 8-4M12 21V11"/></svg>`,
  github: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 0 0-3.16 19.49c.5.09.68-.22.68-.48v-1.7c-2.78.6-3.37-1.34-3.37-1.34-.45-1.16-1.11-1.47-1.11-1.47-.9-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.9 1.53 2.34 1.09 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.65 0 0 .84-.27 2.75 1.02a9.5 9.5 0 0 1 5 0c1.91-1.29 2.75-1.02 2.75-1.02.55 1.38.2 2.4.1 2.65.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.68-4.57 4.93.36.31.68.92.68 1.85v2.74c0 .27.18.58.69.48A10 10 0 0 0 12 2Z"/></svg>`,
  branch: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="6" r="2.5"/><circle cx="6" cy="18" r="2.5"/><circle cx="18" cy="8" r="2.5"/><path d="M6 8.5v7"/><path d="M18 10.5c0 4-6 1.5-6 5"/></svg>`,
  doc: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3h8l4 4v14H6Z"/><path d="M14 3v4h4M9 12h6M9 16h6"/></svg>`,
  lock: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="10.5" width="14" height="9" rx="2"/><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5"/></svg>`,
  check: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11.5l2.5 2.5L20 5.5"/><path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9"/></svg>`,
  dots: `<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg>`,
  stop: `<svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>`,
  arrowR: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>`,
  sun: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>`,
  moon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z"/></svg>`,
} as const;

// ── sidebar (sessions) ──
function sidebar(): HTMLElement {
  if (state.sidebarCollapsed) {
    const expand = el("button", { class: "sidebar-expand", title: "Show sessions", "aria-label": "Show sessions" }, [el("span", { class: "glyph", html: ICON.chevronRight })]);
    expand.onclick = () => { state.sidebarCollapsed = false; persistSidebarCollapsed(); render(); };
    return el("aside", { class: "sidebar sidebar-collapsed", id: "sidebar" }, [expand]);
  }
  return el("aside", { class: "sidebar", id: "sidebar" }, sidebarChildren());
}
function sidebarChildren(): HTMLElement[] {
  // Primary action: New session, with a caret menu for import/export.
  const newBtn = el("button", { class: "newsession", title: "Start a new session (⌘N)" }, [
    el("span", { class: "newsession-plus", html: ICON.plus }),
    el("span", {}, ["New session"]),
  ]);
  newBtn.onclick = () => void newSession();
  const caret = el("button", { class: "newsession-caret", title: "Session options", "aria-label": "Session options" }, [el("span", { class: "glyph", html: ICON.chevron })]);
  caret.onclick = (e) => { e.stopPropagation(); showSessionMenu(caret); };
  const newRow = el("div", { class: "newsession-row" }, [newBtn, caret]);

  const collapseBtn = el("button", { class: "side-collapse", title: "Collapse sidebar", "aria-label": "Collapse sidebar" }, [el("span", { class: "glyph", html: ICON.chevronLeft })]);
  collapseBtn.onclick = () => { state.sidebarCollapsed = true; persistSidebarCollapsed(); render(); };
  const sectionHead = el("div", { class: "side-section" }, [el("span", {}, ["Sessions"]), collapseBtn]);

  const search = el("div", { class: "side-search" }, [
    (() => {
      const inp = el("input", { class: "side-search-input", type: "text", placeholder: "Search sessions…" }) as HTMLInputElement;
      inp.value = state.sessionSearch;
      inp.oninput = () => {
        state.sessionSearch = inp.value;
        updateSidebar();
        const next = document.querySelector(".side-search-input") as HTMLInputElement | null;
        if (next) { next.focus(); next.selectionStart = next.selectionEnd = next.value.length; }
      };
      return inp;
    })(),
  ]);
  const q = state.sessionSearch.toLowerCase().trim();
  const filtered = q ? state.sessions.filter((s) => s.title.toLowerCase().includes(q) || s.preview?.toLowerCase().includes(q)) : state.sessions;
  const list =
    filtered.length === 0
      ? [el("div", { class: "side-empty" }, [state.sessions.length === 0 ? "No sessions yet — start one above." : "No matching sessions."])]
      : filtered.map((s) => {
          const titleChildren: HTMLElement[] = [el("div", { class: "session-title" }, [
            ...(state.activeSession === s.id ? [el("span", { class: "session-live" })] : []),
            s.title,
          ])];
          if (s.preview) titleChildren.push(el("div", { class: "session-preview" }, [s.preview]));
          titleChildren.push(el("div", { class: "session-meta" }, [`${s.messageCount} msg${s.messageCount === 1 ? "" : "s"} · ${relTime(s.updatedAt)}`]));
          const row = el("div", { class: `session ${state.activeSession === s.id ? "session-active" : ""}` }, [
            el("button", { class: "session-main" }, titleChildren),
            el("button", { class: "session-x", title: "Delete" }, ["✕"]),
          ]);
          (row.querySelector(".session-main") as HTMLElement).onclick = () => void openSession(s.id);
          (row.querySelector(".session-main") as HTMLElement).ondblclick = () => void renameSession(s.id);
          (row.querySelector(".session-x") as HTMLElement).onclick = (e) => {
            e.stopPropagation();
            void deleteSession(s.id);
          };
          return row;
        });

  const kids: Array<HTMLElement | null> = [
    newRow,
    sectionHead,
    ...(state.sessions.length > 4 ? [search] : []),
    el("div", { class: "sessions" }, list),
    el("div", { class: "spacer" }),
    proCard(),
  ];
  return kids.filter((x): x is HTMLElement => x !== null);
}

/** Import/export session menu, anchored to the New-session caret. */
function showSessionMenu(anchor: HTMLElement): void {
  popover(anchor, [
    menuItem("＋", "New session", () => void newSession()),
    menuItem("⤓", "Export all sessions", () => void exportSessions()),
    menuItem("⤒", "Import sessions", () => importSessions()),
    // Team tier, hosted only: the desktop has no relay to a team server, so
    // a live share can only exist for a turn THIS server is running.
    ...(state.user && state.teams.length > 0 && state.activeSession
      ? [menuItem("◉", "Share live with team…", () => void showLiveShareDialog(state.activeSession!))]
      : []),
  ]);
}

/** Create / list / revoke live shares of one session (Team tier). */
async function showLiveShareDialog(conversationId: string): Promise<void> {
  const teamSel = el("select", { class: "mode-select" }, state.teams.map((t) => el("option", { value: t.id }, [t.name]))) as HTMLSelectElement;
  if (state.activeTeamId) teamSel.value = state.activeTeamId;
  const scopeSel = el("select", { class: "mode-select" }, [
    el("option", { value: "view" }, ["View only"]),
    el("option", { value: "steer" }, ["View + steer"]),
  ]) as HTMLSelectElement;
  const ttlSel = el("select", { class: "mode-select" }, [
    el("option", { value: "60" }, ["1 hour"]),
    el("option", { value: "1440" }, ["24 hours"]),
    el("option", { value: "10080" }, ["7 days"]),
  ]) as HTMLSelectElement;
  ttlSel.value = "1440";
  const result = el("div", { class: "live-share-result" }, []);
  const list = el("div", { class: "live-share-list" }, []);
  const refresh = async (): Promise<void> => {
    try {
      const shares = (await listLiveShares(conversationId)).filter((x) => !x.revoked);
      list.replaceChildren(...(shares.length === 0
        ? [el("div", { class: "muted" }, ["No active shares."])]
        : shares.map((x) => {
            const revoke = el("button", { class: "btn btn-ghost btn-sm" }, ["Revoke"]);
            revoke.onclick = async () => {
              try { await revokeLiveShare(x.id); toast("Share revoked — viewers were disconnected", "success"); } catch (e) { toast(e instanceof Error ? e.message : String(e), "error"); }
              void refresh();
            };
            const team = state.teams.find((t) => t.id === x.teamId)?.name ?? x.teamId;
            return el("div", { class: "live-share-row" }, [
              el("span", {}, [`${x.scope === "steer" ? "View + steer" : "View only"} · ${team} · ${String(x.viewers)} watching · until ${new Date(x.expiresAt).toLocaleString()}`]),
              revoke,
            ]);
          })));
    } catch (e) {
      list.replaceChildren(el("div", { class: "muted" }, [e instanceof Error ? e.message : String(e)]));
    }
  };
  const create = el("button", { class: "btn btn-primary btn-sm" }, ["Create link"]) as HTMLButtonElement;
  create.onclick = async () => {
    create.disabled = true;
    try {
      const share = await createLiveShare({ conversationId, teamId: teamSel.value, scope: scopeSel.value === "steer" ? "steer" : "view", ttlMinutes: Number(ttlSel.value) });
      const link = el("input", { class: "live-share-link", readonly: "true", value: share.url }) as HTMLInputElement;
      const copy = el("button", { class: "btn btn-sm" }, ["Copy"]);
      copy.onclick = () => { void navigator.clipboard?.writeText(share.url).then(() => toast("Link copied", "success")); };
      result.replaceChildren(
        el("div", { class: "muted" }, ["Only members of the selected team who are signed in can open this link. It is shown once."]),
        el("div", { class: "live-share-row" }, [link, copy]),
      );
      void refresh();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), "error");
    } finally {
      create.disabled = false;
    }
  };
  showModal("Share this session live", el("div", { class: "live-share-dialog" }, [
    el("div", { class: "muted" }, ["Teammates watch this session as it runs. With “View + steer” they can course-correct a running turn; the agent sees it as their note, never as yours, and it can never approve anything."]),
    el("div", { class: "live-share-row" }, [teamSel, scopeSel, ttlSel, create]),
    result,
    el("div", { class: "turn-role" }, ["Active shares"]),
    list,
  ]));
  void refresh();
}

/** Upgrade card in the sidebar (Free users only, when billing is live). */
function proCard(): HTMLElement | null {
  const u = state.user;
  const isFree = !u || u.tier === "local";
  if (!isFree || !state.billingEnabled || state.billingPlans.length === 0) return null;
  const card = el("div", { class: "procard" }, [
    el("div", { class: "procard-head" }, [
      el("span", { class: "procard-title" }, ["Tavsin Pro"]),
      el("span", { class: "procard-spark", html: ICON.spark }),
    ]),
    el("div", { class: "procard-sub" }, ["Pro, Pro+ and Ultra — from $20/mo."]),
    (() => {
      const up = el("button", { class: "procard-btn" }, ["Upgrade ", el("span", { class: "glyph", html: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>` })]);
      up.onclick = () => showPlans();
      return up;
    })(),
  ]);
  return card;
}

function updateSidebar(): void {
  const s = document.getElementById("sidebar");
  if (s) s.replaceChildren(...sidebarChildren());
}

// ── lightweight anchored popover menu (New-session menu, project switcher…) ──
function menuItem(icon: string, label: string, onClick: () => void): HTMLElement {
  const b = el("button", { class: "menu-item" }, [el("span", { class: "menu-item-icon" }, [icon]), el("span", {}, [label])]);
  b.onclick = () => { closePopover(); onClick(); };
  return b;
}
function closePopover(): void {
  document.getElementById("popover-layer")?.remove();
}
/** Render a popover of rows anchored under `anchor`; click-away closes it. */
function popover(anchor: HTMLElement, rows: HTMLElement[]): void {
  closePopover();
  const rect = anchor.getBoundingClientRect();
  const layer = el("div", { class: "popover-layer", id: "popover-layer" });
  const menu = el("div", { class: "popover" }, rows);
  layer.append(menu);
  layer.onclick = (e) => { if (e.target === layer) closePopover(); };
  document.body.append(layer);
  // Anchor bottom-left, but keep on-screen.
  const top = Math.min(rect.bottom + 6, window.innerHeight - menu.offsetHeight - 8);
  const left = Math.min(rect.left, window.innerWidth - menu.offsetWidth - 8);
  menu.style.top = `${Math.max(8, top)}px`;
  menu.style.left = `${Math.max(8, left)}px`;
}

// ── composer ──
// One full-capability agent surface (aligned with desktop Chat): the model
// decides whether to answer, edit, or run checks; the harness enforces verify.

/** Compact model picker inside the composer — green dot + active model. */
function composerModelPicker(): HTMLElement {
  if (isManagedInference()) {
    const picked = managedModels().find((m) => m.id === state.model);
    const q = state.quota;
    const b = el("button", { class: "cmodel", title: "Runs on Tavsin — no API key needed. Draws on your plan's tokens, then purchased tokens." }, [
      el("span", { class: "cmodel-dot" }),
      el("span", { class: "cmodel-label" }, [picked ? picked.label : "Auto"]),
      ...(picked?.multiplier !== null && picked?.multiplier !== undefined ? [el("span", { class: "cmodel-free" }, [fmtMultiplier(picked.multiplier)])] : q?.onCredits ? [el("span", { class: "cmodel-free" }, ["Purchased"])] : []),
      el("span", { class: "cmodel-caret", html: ICON.chevron }),
    ]);
    b.onclick = () => showManagedModelMenu(b);
    return b;
  }
  if (!state.providerOk) {
    const b = el("button", { class: "cmodel cmodel-warn", title: "Connect a model provider" }, [el("span", { class: "cmodel-dot" }), "Connect model", el("span", { class: "cmodel-caret", html: ICON.chevron })]);
    b.onclick = () => openSettings("openrouter");
    return b;
  }
  const active = state.providers.find((p) => p.id === state.resolvedProvider);
  const managedNoOwnKey = (state.managed || active?.managed) && !active?.ownKey;
  if (!managedNoOwnKey && !active?.hasKey) {
    const b = el("button", { class: "cmodel cmodel-warn", title: "Add an API key to use the free model" }, [el("span", { class: "cmodel-dot" }), "Add API key", el("span", { class: "cmodel-caret", html: ICON.chevron })]);
    b.onclick = () => openSettings(state.resolvedProvider !== "—" ? state.resolvedProvider : "openrouter");
    return b;
  }
  const label = state.model || active?.defaultModel || "Auto";
  const q = state.quota;
  const tag = managedNoOwnKey ? (q?.onCredits ? "Credits" : "Included") : null;
  const b = el("button", { class: "cmodel", title: managedNoOwnKey ? "Runs on Tavsin's key — included in your plan, then credits" : "Your own key" }, [
    el("span", { class: "cmodel-dot" }),
    el("span", { class: "cmodel-label" }, [label]),
    ...(tag ? [el("span", { class: "cmodel-free" }, [tag])] : []),
    el("span", { class: "cmodel-caret", html: ICON.chevron }),
  ]);
  b.onclick = () => showModelMenu(b);
  return b;
}

const CLASS_TITLE: Record<ManagedModelView["class"], string> = {
  fast: "Fast — quick answers, small edits",
  balanced: "Balanced — everyday building",
  frontier: "Frontier — hardest problems",
};

/**
 * The managed picker: Auto (Tavsin picks the best model per task across
 * Anthropic, OpenAI and Google), then every model by class with its vendor and
 * how fast it draws the token budget. A model outside the plan is shown, with
 * the plan that includes it — hiding it would hide what upgrading buys.
 */
function showManagedModelMenu(anchor: HTMLElement): void {
  const choose = (id: string, label: string): void => {
    state.model = id;
    writeModelPref(id);
    closePopover();
    render();
    toast(`Model: ${label}`, "info");
  };
  const rows: HTMLElement[] = [el("div", { class: "menu-head" }, ["Model"])];
  const auto = el("button", { class: `model-row${state.model === "" ? " model-row-on" : ""}`, type: "button" }, [
    el("span", { class: "model-name" }, ["Auto"]),
    el("span", { class: "model-price" }, ["best model per task"]),
  ]);
  auto.onclick = () => choose("", "Auto");
  rows.push(auto);
  for (const cls of ["fast", "balanced", "frontier"] as const) {
    const ms = managedModels().filter((m) => m.class === cls && m.available);
    if (ms.length === 0) continue;
    rows.push(el("div", { class: "menu-head" }, [CLASS_TITLE[cls]]));
    for (const m of ms) {
      const r = el("button", { class: `model-row${state.model === m.id ? " model-row-on" : ""}${m.inPlan ? "" : " model-row-locked"}`, type: "button", title: m.inputPerM !== null && m.outputPerM !== null ? `${m.vendor} · $${String(m.inputPerM)} / $${String(m.outputPerM)} per 1M tokens in / out` : m.vendor }, [
        el("span", { class: "model-name" }, [m.label, el("span", { class: "model-vendor" }, [` · ${m.vendor}`])]),
        el("span", { class: "model-price" }, [m.inPlan ? fmtMultiplier(m.multiplier) : "Pro"]),
      ]);
      r.onclick = () => {
        if (m.inPlan) choose(m.id, m.label);
        else {
          closePopover();
          if (state.billingEnabled && state.billingPlans.length > 0 && state.user?.tier === "local") showPlans();
          else toast(`${m.label} is not included in your plan.`, "info");
        }
      };
      rows.push(r);
    }
  }
  rows.push(el("div", { class: "menu-note" }, ["×1 uses one token of your plan per token; ×0.05 uses one per twenty."]));
  popover(anchor, rows);
}

const perMillion = (usdPerToken: number | undefined): string => (usdPerToken === undefined ? "?" : `$${(usdPerToken * 1_000_000).toFixed(usdPerToken * 1_000_000 < 1 ? 2 : 1)}`);

/**
 * Pick ANY model the provider offers, with its price beside it — the free
 * ones, and on a managed key the paid ones too (they draw on the plan's
 * allowance, then credits, at the real price). The catalog is OpenRouter's;
 * another provider keeps provider switching and its own default model.
 */
function showModelMenu(anchor: HTMLElement): void {
  const isOpenRouter = state.resolvedProvider === "openrouter";
  const list = el("div", { class: "model-list" });
  const search = el("input", { class: "field model-search", placeholder: isOpenRouter ? "Search models…" : "Model id (optional)", "aria-label": "Search models" }) as HTMLInputElement;
  const choose = (id: string): void => {
    state.model = id;
    writeModelPref(id);
    closePopover();
    render();
    toast(id ? `Model: ${id}` : "Model: Auto", "info");
  };
  const row = (id: string, name: string, price: string): HTMLElement => {
    const r = el("button", { class: `model-row${state.model === id ? " model-row-on" : ""}`, type: "button" }, [
      el("span", { class: "model-name" }, [name]),
      el("span", { class: "model-price" }, [price]),
    ]);
    r.onclick = () => choose(id);
    return r;
  };
  const paint = (): void => {
    list.replaceChildren();
    list.append(row("", "Auto — Tavsin picks", "default"));
    const cat = state.modelCatalog;
    if (!isOpenRouter) {
      if (search.value.trim()) list.append(row(search.value.trim(), `Use “${search.value.trim()}”`, ""));
      return;
    }
    if (!cat) {
      list.append(el("div", { class: "menu-note" }, ["Loading models…"]));
      return;
    }
    const needle = search.value.trim().toLowerCase();
    const match = (m: CatalogModel): boolean => !needle || m.id.toLowerCase().includes(needle) || m.name.toLowerCase().includes(needle);
    const section = (title: string, ms: CatalogModel[], price: (m: CatalogModel) => string): void => {
      const hits = ms.filter(match).slice(0, 25);
      if (hits.length === 0) return;
      list.append(el("div", { class: "menu-head" }, [title]));
      for (const m of hits) list.append(row(m.id, m.name || m.id, price(m)));
    };
    section("Free", cat.free, () => "Free");
    section("Paid · per 1M tokens in / out", cat.paid, (m) => `${perMillion(m.promptUsdPerToken)} / ${perMillion(m.completionUsdPerToken)}`);
  };
  search.oninput = paint;
  search.onkeydown = (e) => { if (e.key === "Enter" && !isOpenRouter && search.value.trim()) choose(search.value.trim()); };
  const providers = state.providers.filter((p) => p.configured).map((p) => menuItem(p.id === state.resolvedProvider ? "●" : "○", p.id, () => void activateProvider(p.id)));
  popover(anchor, [
    el("div", { class: "menu-head" }, ["Model"]),
    search,
    list,
    ...(providers.length > 1 ? [el("div", { class: "menu-head" }, ["Provider"]), ...providers] : []),
    menuItem("↗", "Add / change API key…", () => openSettings(state.resolvedProvider !== "—" ? state.resolvedProvider : "openrouter")),
  ]);
  paint();
  setTimeout(() => search.focus(), 0);
  if (isOpenRouter && !state.modelCatalog) {
    void withAuth(() => invoke<ModelCatalog>("models.list"), null).then((cat) => {
      if (cat) { state.modelCatalog = cat; paint(); }
    });
  }
}

const HANDOFF_SUGGEST_PCT = 85;

async function continueInNewSession(): Promise<void> {
  if (!state.activeSession) return;
  const next = await withAuth(
    () =>
      invoke<{ conversationId: string; title: string; brief: { text: string } }>("conversations.continue", {
        conversationId: state.activeSession!,
      }),
    null,
  );
  if (!next) return;
  state.activeSession = next.conversationId;
  state.handoffDismissed = false;
  persistActiveSession();
  await loadSessions();
  updateSidebar();
  const detail = await withAuth(() => invoke<ConversationDetail>("conversations.get", { id: next.conversationId }), null);
  state.turns = (detail?.messages ?? []).map((m) => ({
    role: m.role,
    content: m.content,
    ...(m.meta ? { meta: m.meta } : {}),
  }));
  render();
}

function composer(big: boolean): HTMLElement {
  const heroLook = big ? LOOKS.find((l) => l.key === state.heroLook) ?? null : null;
  const placeholder = big ? (heroLook ? `Describe your page — it will be styled ${heroLook.label} (${heroLook.blurb.toLowerCase()})…` : "Describe the page you want to see…") : "Message Tavsin…";
  const ta = el("textarea", { class: "composer-input", rows: big ? "3" : "2", placeholder, "aria-label": "Message Tavsin" }) as HTMLTextAreaElement;
  if (big) ta.id = "hero-brief";
  const submit = (): void => {
    const v = ta.value;
    ta.value = "";
    // A chosen Look styles the user's own brief; with no brief it runs the
    // Look's example, so one click still shows what the Look does.
    void send(heroLook ? (v.trim() ? styledBrief(v, heroLook) : heroLook.prompt) : v);
  };
  ta.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  });

  const agentExtras: HTMLElement[] = [];
  const autonomyBtn = el("button", { class: `mode-extra ${state.autonomy === "full" ? "mode-warn" : ""}`, title: "Toggle fully-autonomous mode (no mid-task pauses)" }, [state.autonomy === "full" ? "⚠ Full autonomy" : "Autonomy: normal"]);
  autonomyBtn.onclick = () => { state.autonomy = state.autonomy === "full" ? "normal" : "full"; render(); };
  const boundariesBtn = el("button", { class: `mode-extra ${state.boundaries.trim() ? "mode-ok" : ""}`, title: "Strict boundaries the agent must respect" }, [state.boundaries.trim() ? "🛡 Boundaries · set" : "🛡 Boundaries"]);
  boundariesBtn.onclick = () => { state.showBoundaries = !state.showBoundaries; render(); };
  agentExtras.push(autonomyBtn, boundariesBtn);

  // Here: Steer (the agent folds it in, or sets a separate task aside).
  // Elsewhere busy: Queue (it runs in THIS session when the other finishes).
  const busyElsewhere = state.run !== null && !streamingHere();
  const cta = el("button", { class: "vibe-btn", title: streamingHere() ? "Send to the running task — a correction is folded in, a separate task is queued next" : busyElsewhere ? `Queue — runs in this session after “${sessionTitle(state.run!.sessionId)}” finishes` : "Send" }, [
    ...(streamingHere() || busyElsewhere ? [el("span", { class: "vibe-btn-ico", html: ICON.plus })] : []),
    streamingHere() ? "Steer" : busyElsewhere ? "Queue" : "Send",
    ...(streamingHere() || busyElsewhere ? [] : [el("span", { class: "vibe-btn-ico", html: ICON.spark })]),
  ]) as HTMLButtonElement;
  // Enabled while streaming so a typed follow-up can be steered into the running turn (Enter works too).
  cta.disabled = !state.providerOk;
  cta.onclick = submit;
  const stop = el("button", { class: "iconbtn iconbtn-stop", title: "Stop" }, [el("span", { class: "glyph", html: ICON.stop })]);
  stop.onclick = () => state.run?.abort?.();

  const composerChildren: HTMLElement[] = [contextStrip(), ta];
  if (state.showBoundaries) {
    const tab = el("textarea", { class: "boundaries-input", rows: "3", placeholder: "Hard rules — e.g. never touch src/billing, never run commands that write to prod DB, stop and ask before installing deps" }) as HTMLTextAreaElement;
    tab.value = state.boundaries;
    tab.oninput = () => { state.boundaries = tab.value; };
    composerChildren.push(el("div", { class: "boundaries-panel" }, [el("div", { class: "boundaries-head" }, ["🛡 Strict boundaries"]), tab]));
  }

  const agentBtn = el("button", { class: "mode-extra", title: "Agent & tools — custom instructions, tool sets, chat defaults" }, [el("span", { class: "glyph", html: ICON.sliders }), " Agent"]);
  agentBtn.onclick = openAgentPanel;
  const capsHint = state.playgroundCaps
    ? el("span", { class: "playground-cap-hint", title: state.playgroundCaps.composer.label }, ["Playground"])
    : null;
  const rowLeft = el("div", { class: "composer-left" }, [...(capsHint ? [capsHint] : []), ...agentExtras, agentBtn]);
  const rowRight = el("div", { class: "composer-right" }, [
    ...(big ? [] : [ctxMeter(ta)]),
    composerModelPicker(),
    ...(streamingHere() ? [stop] : []),
    cta,
  ]);
  composerChildren.push(el("div", { class: "composer-row" }, [rowLeft, el("div", { class: "spacer" }), rowRight]));
  return el("div", { class: `composer ${big ? "composer-big" : ""}` }, composerChildren);
}

/** Context-window meter (shown in the streaming composer). */
function ctxMeter(ta: HTMLTextAreaElement): HTMLElement {
  const usedTokens = state.turns.reduce((n, t) => n + estimateTokens(t.content), 0) + estimateTokens(ta.value);
  const ctxWindow = contextWindowFor(state.resolvedProvider);
  const ctxPct = Math.min(100, Math.round((usedTokens / ctxWindow) * 100));
  const ctxColor = ctxPct >= 90 ? "ctx-danger" : ctxPct >= 70 ? "ctx-warn" : "ctx-ok";
  return el("div", { class: "ctx-meter", title: `Context: ~${usedTokens.toLocaleString()} / ${ctxWindow.toLocaleString()} tokens (${ctxPct}%)` }, [
    el("div", { class: "ctx-bar" }, [el("div", { class: `ctx-fill ${ctxColor}`, style: `width:${ctxPct}%` })]),
    el("span", { class: `ctx-label ${ctxColor}` }, [`${fmtK(usedTokens)}/${fmtK(ctxWindow)}`]),
  ]);
}

/** A look is a territory. The prompt names the skill so the agent loads it. */
const LOOKS: Array<{ key: string; label: string; blurb: string; prompt: string }> = [
  { key: "luxury", label: "Luxury", blurb: "Quiet wealth — metal, paper, one ornament", prompt: "Use the look-luxury and pro-decoration skills. Build a private atelier site for a watchmaker in this project: metal and paper, one ornament, generous space, a real product. Open the in-app preview when files change." },
  { key: "cinematic", label: "Cinematic", blurb: "One light, one still, almost no chrome", prompt: "Use the look-cinematic and pro-decoration skills. Build a single-still launch page for a night record in this project: one light, large type, almost no chrome. Open the in-app preview when files change." },
  { key: "dense", label: "Dense", blurb: "A console of facts — no marketing hero", prompt: "Use the look-dense and pro-decoration skills. Build an ops console or ledger in this project: tables, filters, tabular figures, no marketing hero. Open the in-app preview when files change." },
  { key: "tactile", label: "Tactile", blurb: "A thing you can almost hold", prompt: "Use the look-tactile and pro-decoration skills. Build a product page for a physical object in this project: one large photograph, named materials, a shop action that looks like a tag. Open the in-app preview when files change." },
  { key: "editorial", label: "Editorial", blurb: "An issue — folio, dateline, pull quote", prompt: "Use the look-editorial and pro-decoration skills. Build an issue page in this project: folio, dateline, a pull quote from real copy, columns on the wide width. Open the in-app preview when files change." },
  { key: "industrial", label: "Industrial", blurb: "A plate on a machine — specs first", prompt: "Use the look-industrial and pro-decoration skills. Build a parts-and-spec page for a workshop instrument in this project: stamped type, a schematic, revision letter. Open the in-app preview when files change." },
  { key: "civic", label: "Civic", blurb: "Wayfinding that cannot be misread", prompt: "Use the look-civic and pro-decoration skills. Build a wayfinding or timetable page in this project: numbered system, high contrast, a next-action that cannot be misread. Open the in-app preview when files change." },
  { key: "garden", label: "Garden", blurb: "Living matter and a season, not sage", prompt: "Use the look-garden and pro-decoration skills. Build a seasonal nursery or field-notes page in this project: living matter, named plants, a bed rather than a grid. Open the in-app preview when files change." },
];
const POSSIBILITIES = {
  key: "possibilities",
  label: "Possibilities",
  blurb: "Pick one territory from the brief, then decorate",
  prompt: "Use the design-possibilities skill first: name the job, choose ONE territory (luxury, cinematic, dense, tactile, editorial, industrial, civic, garden), then load that look skill and pro-decoration. Build the page in this project and open the in-app preview when files change. Do not stack looks.",
};

// Quick-start actions — each expands to a real, self-contained build prompt.
const QUICK_ACTIONS: Array<{ icon: string; label: string; prompt: string }> = [
  { icon: ICON.doc, label: "Luxury atelier", prompt: LOOKS[0]!.prompt },
  { icon: ICON.preview, label: "Cinematic still", prompt: LOOKS[1]!.prompt },
  { icon: ICON.gauge, label: "Dense console", prompt: LOOKS[2]!.prompt },
  { icon: ICON.check, label: "Write tests", prompt: "Add a focused automated test suite covering the core logic, then run it and make it pass." },
];
const MORE_ACTIONS: Array<{ label: string; prompt: string }> = [
  { label: "Tactile product", prompt: LOOKS[3]!.prompt },
  { label: "Editorial issue", prompt: LOOKS[4]!.prompt },
  { label: "Industrial spec", prompt: LOOKS[5]!.prompt },
  { label: "Civic wayfinding", prompt: LOOKS[6]!.prompt },
  { label: "Garden season", prompt: LOOKS[7]!.prompt },
  { label: "Add authentication", prompt: "Add authentication to this project: sign up, log in, session handling, and protected routes." },
  { label: "Refactor API", prompt: "Review the API layer and refactor it for clarity, consistent error handling, and input validation — keep behaviour identical." },
  { label: "Add a /health endpoint + test", prompt: "Add a /health endpoint that returns 200 with a small JSON body, and a test proving it." },
  { label: "Fix the riskiest file", prompt: "Find the riskiest file in this project and refactor it safely, keeping behaviour identical and adding a regression test." },
  { label: "Write a README", prompt: "Write a README that explains what this project does and how to run, build, and test it." },
  { label: "Set up CI", prompt: "Set up a CI workflow that installs dependencies, lints, type-checks, and runs the tests on every push." },
];

/** The user's brief with a Look applied: the brief is the job, the Look is the style. */
function styledBrief(brief: string, look: { key: string; label: string }): string {
  return `${brief.trim()}\n\nStyle: use the look-${look.key} and pro-decoration skills (${look.label}). The brief above decides what the page is FOR and what it says; the look decides only how it looks. Build it in this project and open the in-app preview when files change.`;
}

interface SiteTemplateView {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly category: string;
  readonly defaultLook: string;
}

async function loadSiteTemplates(): Promise<void> {
  if (state.siteTemplates !== null) return;
  const r = await withAuth(() => invoke<{ templates: SiteTemplateView[] }>("templates.list"), null);
  state.siteTemplates = r?.templates ?? [];
  if (state.turns.length === 0) render();
}

/**
 * Start from a real starter instead of an empty folder: the template is
 * written by code (tokens, SEO head, accessible nav, a design contract the
 * gates enforce), then the agent tailors it to the brief, if there is one.
 */
async function applySiteTemplate(t: SiteTemplateView): Promise<void> {
  const brief = (document.getElementById("hero-brief") as HTMLTextAreaElement | null)?.value.trim() ?? "";
  const title = window.prompt(`Site name for “${t.name}”`, brief ? brief.split(/[.\n]/)[0]!.slice(0, 60) : "");
  if (title === null || !title.trim()) return;
  const look = state.heroLook ?? t.defaultLook;
  const apply = (overwrite: boolean): Promise<{ written: string[] } | null> =>
    withAuth(() => invoke<{ written: string[] }>("templates.apply", { templateId: t.id, title: title.trim(), look, ...(overwrite ? { overwrite: true } : {}) }), null);
  let r: { written: string[] } | null;
  try {
    r = await apply(false);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (!/already/.test(msg)) {
      toast(`Couldn't start from the template: ${msg}`, "error");
      return;
    }
    if (!window.confirm(`${msg}.\n\nReplace those files with the “${t.name}” starter?`)) return;
    try {
      r = await apply(true);
    } catch (e2) {
      toast(`Couldn't start from the template: ${e2 instanceof Error ? e2.message : String(e2)}`, "error");
      return;
    }
  }
  if (!r) return;
  toast(`Started from “${t.name}” — ${String(r.written.length)} files`, "success");
  state.previewUrl = staticPreviewUrl("index.html");
  try { localStorage.setItem(PREVIEW_URL_KEY, state.previewUrl); } catch { /* ignore */ }
  setTab("preview");
  if (brief) {
    const ta = document.getElementById("hero-brief") as HTMLTextAreaElement | null;
    if (ta) ta.value = "";
    void send(`This project now holds the “${t.name}” starter (${t.category}). Tailor it to this brief, keeping its design tokens, its tavsin/design.md and its data-tavsin-id attributes:\n\n${brief}`);
  }
}

// ── center (transcript / hero) ──
function hero(): HTMLElement {
  if (state.siteTemplates === null) void loadSiteTemplates();
  const quick = el("div", { class: "quickrow" }, [
    ...QUICK_ACTIONS.map((a) => {
      const c = el("button", { class: "quick" }, [el("span", { class: "quick-ico", html: a.icon }), a.label]);
      c.onclick = () => void send(a.prompt);
      return c;
    }),
    (() => {
      const more = el("button", { class: "quick quick-more", title: "More starters" }, [el("span", { class: "quick-ico", html: ICON.dots })]);
      more.onclick = () => popover(more, [el("div", { class: "menu-head" }, ["More starters"]), ...MORE_ACTIONS.map((a) => menuItem("✦", a.label, () => void send(a.prompt)))]);
      return more;
    })(),
  ]);
  const stackPills = state.stack.map((s) => el("span", { class: "ctxpill" }, [s]));
  const gitPill = el("span", { class: `ctxpill ${state.isRepo ? "ctxpill-ok" : ""}` }, [
    el("span", { class: `ctxpill-dot ${state.isRepo ? "on" : ""}` }),
    state.isRepo ? "Git connected" : "No git",
  ]);
  const contextRow = el("div", { class: "contextrow" }, [
    el("span", { class: "contextrow-label" }, ["Context"]),
    ...stackPills,
    gitPill,
  ]);
  const looks = el("div", { class: "looks" }, [
    el("span", { class: "looks-label" }, ["Looks"]),
    (() => {
      const pick = el("button", { class: "look look-pick", type: "button", "data-look": POSSIBILITIES.key, title: POSSIBILITIES.blurb, "aria-label": POSSIBILITIES.blurb }, [POSSIBILITIES.label]);
      pick.onclick = () => {
        const ta = document.getElementById("hero-brief") as HTMLTextAreaElement | null;
        const brief = ta?.value.trim() ?? "";
        if (ta) ta.value = "";
        void send(brief ? `${brief}\n\n${POSSIBILITIES.prompt}` : POSSIBILITIES.prompt);
      };
      return pick;
    })(),
    ...LOOKS.map((look) => {
      const on = state.heroLook === look.key;
      const b = el("button", { class: `look${on ? " look-on" : ""}`, type: "button", "data-look": look.key, title: `${look.blurb} — styles your brief`, "aria-pressed": String(on) }, [look.label]);
      b.onclick = () => {
        // Keep what was typed across the re-render.
        const typed = (document.getElementById("hero-brief") as HTMLTextAreaElement | null)?.value ?? "";
        state.heroLook = on ? null : look.key;
        render();
        const ta = document.getElementById("hero-brief") as HTMLTextAreaElement | null;
        if (ta) { ta.value = typed; ta.focus(); }
      };
      return b;
    }),
  ]);
  const templates = state.siteTemplates && state.siteTemplates.length > 0
    ? el("div", { class: "starters" }, [
        el("div", { class: "starters-head" }, [
          el("span", { class: "looks-label" }, ["Start from a template"]),
          el("span", { class: "starters-note" }, ["SEO head, accessible nav, light + dark, a design contract the checks enforce"]),
        ]),
        el("div", { class: "starters-grid" }, state.siteTemplates.map((t) => {
          const card = el("button", { class: "starter", type: "button", title: t.description }, [
            el("span", { class: "starter-cat" }, [t.category]),
            el("span", { class: "starter-name" }, [t.name]),
            el("span", { class: "starter-desc" }, [t.description]),
          ]);
          card.onclick = () => void applySiteTemplate(t);
          return card;
        })),
      ])
    : null;
  return el("div", { class: "hero" }, [
    el("p", { class: "hero-kicker" }, ["Pro studio"]),
    el("h1", { class: "hero-title" }, ["Write the brief. Watch the page."]),
    el("p", { class: "hero-sub" }, [
      "Pick a look, or write your own. Tavsin reads this project, writes the files, and runs the checks. The preview beside you is the page itself.",
    ]),
    composer(true),
    looks,
    templates,
    quick,
    contextRow,
    el("div", { class: "hero-guard" }, [el("span", { class: "glyph", html: ICON.lock }), "Tavsin never commits without your review."]),
  ]);
}
/** Live drafts per running assistant turn (provisional; never the answer). */
const liveDrafts = new WeakMap<Turn, LiveDraftState>();

function draftView(d: LiveDraftState): HTMLElement {
  const box = el("div", { class: "live-draft", id: "live-draft" });
  for (const n of d.notes) {
    box.append(el("div", { class: `draft-note${n.outcome === "revised" ? " draft-revised" : ""}`, title: n.text }, [n.outcome === "revised" ? "Sent back by a check · " : "▸ ", n.text]));
  }
  if (d.current) {
    box.append(el("div", { class: "draft-current" }, [el("div", { class: "draft-label" }, ["Drafting · not yet checked"]), d.current.text]));
  }
  return box;
}

/** Repaint the running turn's draft in place, beside the thinking indicator or above the answer. */
function paintDraft(d: LiveDraftState): void {
  const t = document.getElementById("transcript");
  const turn = t?.lastElementChild;
  if (!t || !turn) return;
  const fresh = draftView(d);
  const old = document.getElementById("live-draft");
  if (old) old.replaceWith(fresh);
  else {
    const anchor = turn.querySelector("#thinking") ?? turn.querySelector(".turn-body");
    if (anchor) anchor.before(fresh);
    else turn.append(fresh);
  }
  t.scrollTop = t.scrollHeight;
}

function thinkingIndicator(): HTMLElement {
  return el("div", { class: "thinking", id: "thinking" }, [
    el("span", { class: "spinner" }),
    el("span", { class: "thinking-label" }, ["Thinking…"]),
  ]);
}
function renderTurnEditor(t: Turn, idx: number): HTMLElement {
  const editor = el("textarea", { class: "turn-edit-input", rows: "3" }) as HTMLTextAreaElement;
  editor.value = t.content;
  const save = el("button", { class: "btn btn-primary btn-sm" }, ["Save & resend"]);
  save.onclick = () => void applyEdit(idx, editor.value);
  const cancel = el("button", { class: "btn btn-sm" }, ["Cancel"]);
  cancel.onclick = () => { state.editingIdx = null; render(); };
  editor.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void applyEdit(idx, editor.value); }
    if (e.key === "Escape") { e.preventDefault(); state.editingIdx = null; render(); }
  });
  setTimeout(() => { editor.focus(); editor.selectionStart = editor.selectionEnd = editor.value.length; }, 0);
  return el("div", { class: "turn turn-user turn-editing" }, [
    el("div", { class: "turn-header" }, [el("div", { class: "turn-role" }, ["You · editing"])]),
    editor,
    el("div", { class: "turn-edit-actions" }, [save, cancel, el("span", { class: "turn-edit-hint" }, ["⌘↵ to resend · Esc to cancel"])]),
  ]);
}

function renderTurn(t: Turn, idx: number): HTMLElement {
  if (t.role === "user" && state.editingIdx === idx) return renderTurnEditor(t, idx);
  const isLastAssistant = streamingHere() && t.role === "assistant" && idx === state.turns.length - 1 && t.content === "";
  const draft = isLastAssistant ? liveDrafts.get(t) : undefined;
  const body = isLastAssistant
    ? (draft && (draft.current || draft.notes.length > 0) ? el("div", {}, [draftView(draft), thinkingIndicator()]) : thinkingIndicator())
    : el("div", { class: "turn-body", html: formatBody(t.content) });
  const roleLabel = t.role === "user" ? "You" : "Tavsin";

  const actions: HTMLElement[] = [];
  if (t.role === "assistant" && t.content && !isLastAssistant) {
    const copyBtn = el("button", { class: "turn-action", title: "Copy to clipboard" }, ["Copy"]);
    copyBtn.onclick = () => {
      void navigator.clipboard?.writeText(t.content).then(() => {
        copyBtn.textContent = "Copied";
        setTimeout(() => { copyBtn.textContent = "Copy"; }, 1200);
      });
    };
    actions.push(copyBtn);

    if ("speechSynthesis" in window) {
      const speakBtn = el("button", { class: "turn-action", title: "Read aloud" }, ["Listen"]);
      speakBtn.onclick = () => {
        if (speechSynthesis.speaking) { speechSynthesis.cancel(); speakBtn.textContent = "Listen"; return; }
        const u = new SpeechSynthesisUtterance(t.content.replace(/[#*`_~[\]]/g, ""));
        u.rate = 1.1;
        u.onend = () => { speakBtn.textContent = "Listen"; };
        speakBtn.textContent = "Stop";
        speechSynthesis.speak(u);
      };
      actions.push(speakBtn);
    }

    const isLast = idx === state.turns.length - 1;
    if (isLast && !streamingHere()) {
      const regenBtn = el("button", { class: "turn-action", title: "Regenerate response" }, ["Retry"]);
      regenBtn.onclick = () => {
        const lastUserIdx = state.turns.findLastIndex((x) => x.role === "user");
        if (lastUserIdx >= 0) {
          const lastUserMsg = state.turns[lastUserIdx]!.content;
          state.turns = state.turns.slice(0, lastUserIdx);
          void send(lastUserMsg);
        }
      };
      actions.push(regenBtn);
    }

    // Undo the file changes this (most recent) turn made — only when a restore
    // point was captured and there are changes to revert.
    if (isLast && state.run === null && state.undoSnapshot?.sessionId === state.activeSession && state.changed.length > 0) {
      const undoBtn = el("button", { class: "turn-action", title: "Undo the file changes from this turn" }, [state.undoBusy ? "Undoing…" : "Undo edits"]);
      undoBtn.onclick = () => void undoLastEdit();
      actions.push(undoBtn);
    }
  }

  // Rewind the files to before this turn (any turn, not only the last).
  const cp = t.role === "user" && state.run === null ? checkpointFor(t, idx) : null;
  if (cp) {
    const rewindBtn = el("button", { class: "turn-action", title: "Put the files back the way they were before this message" }, ["Rewind to here"]);
    rewindBtn.onclick = () => void rewindTo(cp, t.content);
    actions.push(rewindBtn);
  }

  // Edit an already-sent prompt: forks the conversation from that turn.
  if (t.role === "user" && !streamingHere() && state.editingIdx === null) {
    const editBtn = el("button", { class: "turn-action", title: "Edit & resend" }, [el("span", { class: "glyph", html: ICON.pencil })]);
    editBtn.onclick = () => { state.editingIdx = idx; render(); };
    actions.push(editBtn);
  }

  const header = el("div", { class: "turn-header" }, [
    el("div", { class: "turn-role" }, [roleLabel]),
    ...(actions.length > 0 ? [el("div", { class: "turn-actions" }, actions)] : []),
  ]);
  if (t.role === "assistant") ensureUsageReceipt(t);
  const policy = t.role === "assistant" ? policyChips(t.policy) : null;
  const verify = t.role === "assistant" ? verificationStrip(t.verification) : null;
  const confidence = t.role === "assistant" ? confidenceStrip(t.confidence) : null;
  const questions = t.role === "assistant" ? questionsStrip(t.questions) : null;
  const usage = t.role === "assistant" ? usageReceiptStrip(t.usageReceipt) : null;
  const tail = [policy, verify, questions, confidence, usage].filter((n): n is HTMLElement => n !== null);
  return el("div", { class: `turn turn-${t.role}` }, tail.length ? [header, body, ...tail] : [header, body]);
}

function usageReceiptStrip(receipt: TraceUsageReceiptView | null | undefined): HTMLElement | null {
  if (!receipt || receipt.callCount === 0) return null;
  const priced =
    receipt.unpricedCalls === 0
      ? fmtUsd(receipt.totalUsd)
      : `${fmtUsd(receipt.totalUsd)} (${String(receipt.unpricedCalls)} unpriced)`;
  return el("div", { class: "usage-receipt", title: "Trace-level usage from model_call rows" }, [
    el("span", { class: "usage-receipt-label" }, ["Usage"]),
    el("span", {}, [
      `${String(receipt.callCount)} call${receipt.callCount === 1 ? "" : "s"} · ~${fmtK(receipt.tokensIn + receipt.tokensOut)} tokens · ${priced}`,
    ]),
  ]);
}

/** How a refusing rule reads; mirrored from desktop Chat's `policyRuleLabel`. */
function policyRuleLabel(rule: string): string {
  if (rule.startsWith("admin:")) return `team rule ${rule.slice(6)}`;
  const known: Record<string, string> = {
    "destructive-command": "destructive command",
    "secret-access": "credential access",
    "data-exfiltration": "data exfiltration",
    "user-boundaries": "your boundaries",
    budget: "budget",
    "write-scope": "write scope",
  };
  return known[rule] ?? rule;
}

/**
 * What the policy engine refused this turn, and whether the turn paused on a
 * budget — a dedicated chip row, not inline ⛔ prose a reader cannot tell
 * apart from the model's words. Rebuilt from the trace on reload.
 */
function policyChips(p: TurnPolicyView | null | undefined): HTMLElement | null {
  if (!p || (p.blocks.length === 0 && !p.paused)) return null;
  const rows: HTMLElement[] = [];
  if (p.paused) {
    const where = p.paused.scope === "month" ? "monthly budget" : "task cost cap";
    rows.push(el("div", { class: "policy-paused", role: "status" }, [
      el("span", { class: "policy-glyph", "aria-hidden": "true" }, ["⏸"]),
      el("strong", {}, ["Paused — budget reached"]),
      el("span", { class: "policy-detail" }, [`${where} · ${fmtUsd(p.paused.spentUsd)} of ${fmtUsd(p.paused.budgetUsd)}`]),
    ]));
  }
  if (p.blocks.length > 0) {
    rows.push(el("div", { class: "policy-chip-row" }, p.blocks.map((b) =>
      el("span", { class: "policy-chip", title: `${b.reason}${b.target ? `\n${b.target}` : ""}\nNot run — refused by ${policyRuleLabel(b.rule)}.` }, [
        el("span", { class: "policy-glyph", "aria-hidden": "true" }, ["⊘"]),
        el("strong", {}, ["Blocked"]),
        el("code", {}, [b.tool]),
        el("span", { class: "policy-detail" }, [`· ${policyRuleLabel(b.rule)}`]),
      ]),
    )));
  }
  return el("div", { class: "policy-chips" }, rows);
}

/** Repaint the live policy chips on the streaming turn. */
function paintPolicy(p: TurnPolicyView): void {
  const turnEl = document.getElementById("transcript")?.lastElementChild;
  if (!turnEl) return;
  turnEl.querySelector(".policy-chips")?.remove();
  const chips = policyChips(p);
  if (chips) turnEl.append(chips);
}

const VERIFY_TEXT: Readonly<Record<string, { label: string; cls: string }>> = {
  verified: { label: "Verified", cls: "ok" },
  failing: { label: "Checks failing", cls: "bad" },
  unverified: { label: "Changes unverified", cls: "warn" },
  ran: { label: "Ran", cls: "muted" },
};

/** Mirrored from core's GATE_LABEL; an unknown kind falls through to its name. */
const GATE_TEXT: Readonly<Record<string, string>> = {
  verify: "edits were unverified",
  design: "the design contract was violated",
  progress: "the change had not actually been made",
  browser: "a UI change was never checked in a browser",
  scope: "a public API changed without confirmation",
  probe: "debug instrumentation was still in the tree",
  plan: "plan steps were still open",
  evaluator: "an independent reviewer disagreed",
  grounding: "the answer cited something the turn never saw",
  target: "it did not work on the verification target",
  site: "a page had a broken link, image or unreadable text",
};

const CONFIDENCE_TEXT: Readonly<Record<string, string>> = { high: "High", medium: "Medium", low: "Low" };
const RUNG_TEXT: Readonly<Record<string, string>> = { unit: "Unit", e2e: "E2E", target: "Target" };
const RUNG_MARK: Readonly<Record<string, [string, string]>> = {
  passed: ["✓", "passed"], partial: ["◐", "partial"], failed: ["✗", "failed"], not_run: ["–", "not run"], unavailable: ["?", "could not run here"],
};

/** The verification ladder in one row: what ran after the last edit. 100 needs all three. */
function ladderChips(l: VerificationLadderView): HTMLElement {
  return el("span", { class: "ladder", "aria-label": "Verification ladder" }, [
    ...l.rungs.map((r) => {
      const [mark, word] = RUNG_MARK[r.status] ?? ["–", r.status];
      const name = r.layer === "target" && l.aim ? `Target (${l.aim})` : (RUNG_TEXT[r.layer] ?? r.layer);
      return el("span", { class: `rung rung-${r.status}`, title: r.detail }, [el("b", {}, [mark]), ` ${name}`, el("span", { class: "sr-only" }, [` ${word}`])]);
    }),
    ...(l.full ? [el("span", { class: "rung-full" }, ["fully verified"])] : []),
  ]);
}

/** Questions the agent asked this turn, and the answer or default it used. */
function questionsStrip(qs: SettledQuestionView[] | undefined): HTMLElement | null {
  if (!qs || qs.length === 0) return null;
  return el("div", { class: "qstrip", "aria-label": "Questions and assumptions" }, [
    el("div", { class: "qstrip-head" }, [qs.some((q) => q.answeredBy === "default") ? "Questions and assumptions" : "Questions"]),
    el("ul", {}, qs.map((q) => el("li", {}, [`${q.question} — `, q.answeredBy === "user" ? `you said “${q.answer}”` : `assumed “${q.answer}” (no answer)`]))),
  ]);
}

/** Live questions, per streaming assistant turn. */
const liveQuestions = new WeakMap<object, { q: QuestionFrame; askedAt: number }>();
let questionTimer: number | null = null;

/** Paint (or repaint) the agent's live question into the streaming turn. */
function paintQuestion(turn: object, answer: (text: string) => void): void {
  const live = liveQuestions.get(turn);
  const t = document.getElementById("transcript");
  const last = t?.lastElementChild;
  if (!live || !t || !last) return;
  const { q, askedAt } = live;
  const waiting = q.phase === "asked" && q.waitMs > 0;
  const left = Math.max(0, Math.ceil((askedAt + q.waitMs - Date.now()) / 1000));
  const card = el("div", { class: "live-q", id: "live-question", "aria-label": "Question from the agent" }, [
    el("div", { class: "live-q-text" }, [q.question]),
    ...(q.why ? [el("div", { class: "live-q-why" }, [`Why it matters: ${q.why}`])] : []),
    ...(waiting && q.options.length > 0
      ? [el("div", { class: "live-q-opts" }, q.options.map((o) => {
          const b = el("button", { class: "btn btn-ghost btn-sm" }, [o]);
          b.onclick = () => answer(o);
          return b;
        }))]
      : []),
    el("div", { class: "live-q-why" }, [
      waiting
        ? `Reply below, or it goes ahead with “${q.default}” in ${String(left)}s.`
        : q.phase === "answered" ? `You answered: ${q.answer ?? ""}` : `Went ahead with “${q.default}” — say so if that is wrong.`,
    ]),
  ]);
  const old = document.getElementById("live-question");
  if (old) old.replaceWith(card);
  else last.append(card);
  t.scrollTop = t.scrollHeight;
  if (questionTimer !== null) { clearTimeout(questionTimer); questionTimer = null; }
  if (waiting && left > 0) questionTimer = window.setTimeout(() => paintQuestion(turn, answer), 1000);
}

/** "Your profile": what the agent knows about this user, across their projects — editable and forgettable. */
async function openProfile(): Promise<void> {
  const r = await withAuth(() => invoke<{ available: boolean; profile?: UserProfileView }>("user.profile.get"), null);
  if (!r?.available || !r.profile) {
    toast("Profiles need an account on this server.", "info");
    return;
  }
  const body = el("div", { class: "drawer profile" });
  const draw = (p: UserProfileView): void => {
    const about = el("textarea", { class: "field", rows: "3", maxlength: "2000" }) as HTMLTextAreaElement;
    about.value = p.about;
    const pick = (label: string, key: string, value: string, opts: Array<[string, string]>): HTMLElement => {
      const sel = el("select", { class: "field" }, opts.map(([v, n]) => el("option", { value: v }, [n]))) as HTMLSelectElement;
      sel.value = value;
      sel.onchange = () => void save({ [key]: sel.value === "" ? null : sel.value });
      return el("label", { class: "profile-pick" }, [label, sel]);
    };
    const notes = el("textarea", { class: "field", rows: "3" }) as HTMLTextAreaElement;
    notes.value = p.preferences.notes.join("\n");
    const saveBtn = el("button", { class: "btn btn-primary btn-sm" }, ["Save"]);
    saveBtn.onclick = () => void save({ about: about.value, notes: notes.value.split("\n").map((n) => n.trim()).filter(Boolean) });
    const o = p.observed;
    const top = (rec: Record<string, number>): string => Object.entries(rec).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([k, v]) => `${k} (${String(v)})`).join(", ");
    const lessons = p.lessons.length === 0
      ? [el("p", { class: "drawer-note" }, ["None yet. Your answers to the agent's questions, your mid-task corrections, your ratings and the turns you undo become lessons here."])]
      : [...p.lessons].reverse().map((l) => {
          const forget = el("button", { class: "btn btn-ghost btn-sm" }, ["Forget"]);
          forget.onclick = () => void withAuth(() => invoke<{ profile: UserProfileView }>("user.profile.forget", { lessonId: l.id }), null).then((x) => { if (x) draw(x.profile); });
          return el("div", { class: "profile-lesson" }, [el("span", {}, [l.text, el("span", { class: "drawer-note" }, [` · ${l.source}${l.count > 1 ? ` ×${String(l.count)}` : ""}`])]), forget]);
        });
    const reset = el("button", { class: "btn btn-ghost btn-sm" }, ["Reset what was learned"]);
    reset.onclick = () => void withAuth(() => invoke<{ profile: UserProfileView }>("user.profile.reset"), null).then((x) => { if (x) draw(x.profile); });
    body.replaceChildren(
      el("p", { class: "drawer-note" }, ["What the agent knows about you, in every project. It learns from what your work shows and from your own answers and corrections — never from guesses about you."]),
      el("label", { class: "profile-pick profile-col" }, ["About you (role, stack, how you like to work)", about]),
      el("div", { class: "profile-row" }, [
        pick("Questions", "askPolicy", p.preferences.askPolicy, [["never", "Never ask — decide and list assumptions"], ["rarely", "Only when truly blocked"], ["when_unsure", "When a choice is uncertain"]]),
        pick("Answers", "verbosity", p.preferences.verbosity, [["brief", "Brief"], ["normal", "Normal"], ["detailed", "Detailed"]]),
        pick("Autonomy by default", "autonomy", p.preferences.autonomy ?? "", [["", "As each chat says"], ["full", "Full — run straight through"], ["normal", "Normal"]]),
      ]),
      el("label", { class: "profile-pick profile-col" }, ["Standing instructions, one per line", notes]),
      saveBtn,
      el("h4", {}, ["What your work shows"]),
      o.changeTurns === 0
        ? el("p", { class: "drawer-note" }, ["Nothing yet — this fills in as the agent works for you."])
        : el("ul", {}, [
            el("li", {}, [`${String(o.changeTurns)} changes; ${String(o.fullyVerified)} fully verified (unit, end-to-end and target); average confidence ${String(Math.round(o.confidenceSum / o.changeTurns))}`]),
            ...(Object.keys(o.languages).length ? [el("li", {}, [`Languages: ${top(o.languages)}`])] : []),
            ...(Object.keys(o.checks).length ? [el("li", {}, [`Checks you run: ${top(o.checks)}`])] : []),
            ...(Object.keys(o.targets).length ? [el("li", {}, [`Verified on: ${top(o.targets)}`])] : []),
          ]),
      el("h4", {}, ["Lessons learned with you"]),
      ...lessons,
      reset,
    );
  };
  const save = async (patch: Record<string, unknown>): Promise<void> => {
    const x = await withAuth(() => invoke<{ profile: UserProfileView }>("user.profile.set", patch), null);
    if (x) { draw(x.profile); toast("Profile saved", "success"); }
  };
  draw(r.profile);
  showModal("Your profile", body);
}

/**
 * How far this answer can be trusted — MEASURED from what the turn did
 * (checks run after the last edit, files read, sources retrieved, claims
 * nothing backs), never the model's own say-so. When the answer claimed
 * more than the evidence supports, it says so.
 */
function confidenceStrip(c: TurnConfidenceView | null | undefined): HTMLElement | null {
  if (!c) return null;
  const src = c.sources;
  const bits = [
    src.filesRead > 0 ? `${String(src.filesRead)} file${src.filesRead === 1 ? "" : "s"} read` : "",
    src.urlsRetrieved > 0 ? `${String(src.urlsRetrieved)} source${src.urlsRetrieved === 1 ? "" : "s"} fetched` : "",
    src.checksPassed > 0 ? `${String(src.checksPassed)} check${src.checksPassed === 1 ? "" : "s"} passed` : "",
  ].filter(Boolean);
  const details = el("details", { class: "conf-why" }, [
    el("summary", {}, ["Why"]),
    el("ul", {}, c.reasons.map((r) => el("li", {}, [r]))),
  ]);
  return el("div", { class: `conf conf-${c.level}`, title: "Measured from what this turn actually did — not the model's own claim" }, [
    el("span", { class: "conf-dot", "aria-hidden": "true" }),
    el("span", { class: "conf-label" }, [`Confidence · ${CONFIDENCE_TEXT[c.level] ?? c.level} · ${String(Math.round(c.score))}`]),
    c.ladder ? ladderChips(c.ladder) : el("span", { class: "conf-src" }, [bits.length > 0 ? bits.join(" · ") : "no sources this turn"]),
    ...(c.capped ? [el("span", { class: "conf-capped" }, [`claimed ${c.claimed ?? "more"}`])] : []),
    details,
  ]);
}

/**
 * What Tavsin OBSERVED this turn — commands and their exit codes, and every
 * gate that refused to let the turn finish.
 *
 * Nothing here comes from the reply. A turn that neither edited nor ran
 * anything gets no strip at all: an empty one would itself be a claim.
 */
function verificationStrip(v: TurnVerificationReply | null | undefined): HTMLElement | null {
  if (!v) return null;
  const tone = VERIFY_TEXT[v.status] ?? VERIFY_TEXT["ran"]!;
  const rows: HTMLElement[] = [];
  for (const c of v.checks.slice(-4)) {
    rows.push(el("div", { class: "vrow" }, [
      el("span", { class: `vmark ${c.ok ? "ok" : "bad"}` }, [c.ok ? "✓" : "✗"]),
      el("code", { class: "vcmd" }, [c.command]),
      ...(c.ok ? [] : [el("span", { class: "vexit" }, [`exit ${String(c.exitCode)}`])]),
    ]));
  }
  if (v.tests) {
    rows.push(el("div", { class: "vrow vtests" }, [
      el("span", { class: "vmark muted" }, ["≡"]),
      el("span", {}, [`${String(v.tests.passed)} passed${v.tests.failed > 0 ? ` · ${String(v.tests.failed)} failed` : ""}`]),
    ]));
  }
  if (v.editsAfterCheck > 0) {
    rows.push(el("div", { class: "vrow warn" }, [
      el("span", { class: "vmark warn" }, ["⚠"]),
      el("span", {}, [`${String(v.editsAfterCheck)} file${v.editsAfterCheck === 1 ? "" : "s"} edited after the last passing check — not re-verified.`]),
    ]));
  }
  const gates = v.gates ?? [];
  const refusals = gates.reduce((n, g) => n + g.count, 0);
  if (refusals > 0) {
    rows.push(el("div", { class: "vrow vgates" }, [
      el("span", { class: "vmark accent" }, ["⊘"]),
      el("span", {}, [`Tavsin refused to finish ${String(refusals)}× — ${gates.map((g) => GATE_TEXT[g.kind] ?? g.kind).join(", ")}.`]),
    ]));
  }
  if (rows.length === 0) return null;
  return el("div", { class: "vpanel" }, [
    el("div", { class: "vhead" }, [
      el("span", { class: "vtitle" }, ["Verification"]),
      el("span", { class: `vstatus ${tone.cls}` }, [tone.label]),
    ]),
    ...rows,
  ]);
}
/** Pending follow-ups typed while a turn streams — shown above the composer. */
function handoffStrip(): HTMLElement | null {
  if (state.handoffDismissed || !state.activeSession || state.turns.length === 0) return null;
  const usedTokens = state.turns.reduce((n, t) => n + estimateTokens(t.content), 0);
  const ctxWindow = contextWindowFor(state.resolvedProvider);
  const ctxPct = Math.min(100, Math.round((usedTokens / ctxWindow) * 100));
  if (ctxPct < HANDOFF_SUGGEST_PCT) return null;
  const cont = el("button", { class: "vibe-btn vibe-btn-sm", type: "button" }, ["Continue in new session"]);
  cont.onclick = () => void continueInNewSession();
  const dismiss = el("button", { class: "iconbtn", type: "button", title: "Not now" }, ["✕"]);
  dismiss.onclick = () => { state.handoffDismissed = true; render(); };
  return el("div", { class: "handoff-banner", role: "status" }, [
    el("span", {}, [`Context ~${ctxPct}% — start a fresh session with a carried brief (files, checks, open plan).`]),
    cont,
    dismiss,
  ]);
}

/**
 * The task queue, where the user can see it: what is waiting in THIS session
 * (each removable, movable, labelled with why it is waiting), a count of what
 * waits elsewhere, and — after a Stop or a failure — the paused state with
 * Resume and Clear. Nothing waits invisibly.
 */
function queuedStrip(): HTMLElement | null {
  const mine = state.activeSession ? taskQueue.forSession(state.activeSession) : [];
  const elsewhere = taskQueue.size - mine.length;
  if (mine.length === 0 && elsewhere === 0) return null;
  const items = mine.map((q) => {
    const up = el("button", { class: "queued-x", title: "Run sooner", "aria-label": "Move up" }, ["↑"]);
    up.onclick = () => { taskQueue.moveUp(q.id); render(); };
    const x = el("button", { class: "queued-x", title: "Remove from the queue", "aria-label": "Remove" }, ["✕"]);
    x.onclick = () => { taskQueue.remove(q.id); render(); };
    return el("div", { class: "queued-item" }, [
      el("span", { class: `queued-why queued-${q.reason}` }, [reasonLabel(q.reason)]),
      el("span", { class: "queued-text" }, [q.text]),
      up,
      x,
    ]);
  });
  const head: (string | HTMLElement)[] = [
    taskQueue.paused
      ? `Queue paused — ${String(taskQueue.size)} task${taskQueue.size === 1 ? "" : "s"} waiting since the last turn stopped`
      : `${String(mine.length)} queued here${elsewhere > 0 ? ` · ${String(elsewhere)} in other sessions` : ""} — they run one at a time, in order`,
  ];
  if (taskQueue.paused) {
    const resume = el("button", { class: "vibe-btn vibe-btn-sm", type: "button" }, ["Resume"]);
    resume.onclick = () => { taskQueue.resume(); drainQueue(); };
    const clear = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, ["Clear"]);
    clear.onclick = () => { taskQueue.clear(); render(); };
    head.push(resume, clear);
  }
  return el("div", { class: "queued", role: "status" }, [el("div", { class: "queued-head" }, head), ...items]);
}

function center(): HTMLElement {
  if (state.turns.length === 0) {
    // A new session whose first message is waiting behind another session's
    // run must still SHOW that it is waiting.
    const waiting = queuedStrip();
    return el("section", { class: "center landing", id: "center" }, waiting ? [waiting, hero()] : [hero()]);
  }
  const planWrap = el("div", { id: "plan-wrap" }, (() => { const c = planCard(); return c ? [c] : []; })());
  const children: HTMLElement[] = [planWrap, el("div", { class: "transcript", id: "transcript" }, state.turns.map((t, i) => renderTurn(t, i)))];
  const queued = queuedStrip();
  if (queued) children.push(queued);
  const handoff = handoffStrip();
  if (handoff) children.push(handoff);
  children.push(composer(false));
  return el("section", { class: "center stream", id: "center" }, children);
}
function paintStream(text: string): void {
  const t = document.getElementById("transcript");
  if (!t) return;
  // Remove thinking indicator when first token arrives
  const thinking = document.getElementById("thinking");
  if (thinking) {
    const parent = thinking.parentElement;
    if (parent) {
      thinking.remove();
      parent.append(el("div", { class: "turn-body", html: formatBody(text) }));
    }
  } else {
    const body = t.lastElementChild?.querySelector(".turn-body");
    if (body) body.innerHTML = formatBody(text);
  }
  t.scrollTop = t.scrollHeight;
}

// ── rail (workspace tabs) ──
function rail(): HTMLElement {
  return el("aside", { class: "rail", id: "rail" }, railChildren());
}
function tabBtn(id: Tab, label: string): HTMLElement {
  const b = el("button", { class: `tab ${state.tab === id ? "tab-active" : ""}` }, [label]);
  b.onclick = () => setTab(id);
  return b;
}
const TAB_LABELS: Record<Tab, string> = { changes: "Changes", files: "Files", runs: "Runs", preview: "Preview", intel: "Intel", deploy: "Deploy", team: "Team", orchestration: "Orchestration" };
function railChildren(): HTMLElement[] {
  if (state.tab !== "files") destroyWebEditor();
  // Preview sits with Changes / Files / Runs. It is where a build is judged,
  // so it is not an extra tab that appears only after you find the icon.
  // Intel / Deploy / Orchestration still open from the icon rail.
  const showTeam = state.teams.length > 0 || state.canCreateTeam;
  // A tab for a feature this host cannot run is a dead end; it is not shown.
  const shown: Tab[] = playgroundUnavailable("jobsBackground") ? ["changes", "preview", "files"] : ["changes", "preview", "files", "runs"];
  if (showTeam) shown.push("team");
  if (!shown.includes(state.tab)) shown.push(state.tab);
  const tabs = el("div", { class: "tabs" }, shown.map((id) => tabBtn(id, TAB_LABELS[id])));
  let body: HTMLElement;
  if (state.tab === "changes") body = changesPanel();
  else if (state.tab === "files") body = filesPanel();
  else if (state.tab === "preview") body = previewPanel();
  else if (state.tab === "intel") body = intelPanel();
  else if (state.tab === "orchestration") body = orchestrationPanel();
  else if (state.tab === "deploy") body = deployPanel();
  else if (state.tab === "team") body = teamPanel();
  else body = runsPanel();
  return [tabs, body];
}
function updateRail(): void {
  const r = document.getElementById("rail");
  if (r) r.replaceChildren(...railChildren());
}

/** Best-effort invoke that swallows auth + "no scan yet" errors into a fallback. */
async function tryInvoke<T>(channel: string, payload: unknown, fallback: T): Promise<T> {
  try {
    return await withAuth(() => invoke<T>(channel, payload), fallback);
  } catch {
    return fallback;
  }
}

async function refreshIntel(): Promise<void> {
  state.intelLoading = true;
  if (state.tab === "intel") updateRail();
  const emptyEmbed: IntelEmbed = { model: "", embedded: 0, pending: 0, total: 0 };
  const [subsystems, fragile, embed, memories, status] = await Promise.all([
    tryInvoke<IntelSubsystem[]>("graph.subsystems", undefined, []),
    tryInvoke<IntelFragile[]>("graph.fragile", { limit: 8 }, []),
    tryInvoke<IntelEmbed>("memory.embeddings.status", undefined, emptyEmbed),
    tryInvoke<IntelMemoryRow[]>("memory.list", undefined, []),
    tryInvoke<IntelStatus>("graph.status", undefined, { scanned: false }),
  ]);
  state.intel = { subsystems, fragile, embed, memories, status };
  state.intelLoading = false;
  if (state.tab === "intel") updateRail();

  // Turnkey: the first time Intel opens on a never-scanned project, scan
  // automatically (once) so it populates instead of showing "Not scanned yet".
  if (!status.scanned && !state.intelAutoScanned && !state.intelBusy) {
    state.intelAutoScanned = true;
    void runScan();
  }
}

async function checkContradictions(id: string): Promise<void> {
  if (state.intelContraFor === id) { state.intelContraFor = null; state.intelContra = null; updateRail(); return; }
  state.intelContraFor = id;
  state.intelContra = null;
  state.intelContraBusy = true;
  updateRail();
  state.intelContra = await tryInvoke<IntelContradiction[]>("memory.contradictions", { id }, []);
  state.intelContraBusy = false;
  if (state.tab === "intel") updateRail();
}

async function retireSuperseded(id: string): Promise<void> {
  try {
    await withAuth(() => invoke("memory.reconcile", { id, autoRetire: true }), null);
    toast("Retired superseded beliefs", "success");
  } catch (e) {
    toast(e instanceof Error ? e.message : "Could not retire", "error");
  }
  state.intelContraFor = null;
  state.intelContra = null;
  await refreshIntel();
}

async function runScan(): Promise<void> {
  if (state.intelBusy) return;
  state.intelBusy = true;
  updateRail();
  try {
    await withAuth(() => invoke("graph.scan"), null);
    toast("Scan complete", "success");
    state.intelFile = null;
    state.intelSymbols = [];
    state.intelSymbol = null;
    state.intelCalls = null;
    await refreshIntel();
  } catch (e) {
    toast(e instanceof Error ? e.message : "Scan failed", "error");
  } finally {
    state.intelBusy = false;
    if (state.tab === "intel") updateRail();
  }
}

async function backfillEmbeddings(): Promise<void> {
  if (state.intelBusy) return;
  state.intelBusy = true;
  updateRail();
  try {
    const r = await withAuth(() => invoke<{ embedded: number }>("memory.embeddings.backfill", {}), { embedded: 0 });
    toast(`Embedded ${r.embedded} memories`, "success");
    await refreshIntel();
  } catch (e) {
    toast(e instanceof Error ? e.message : "Backfill failed (needs an OpenAI key)", "error");
  } finally {
    state.intelBusy = false;
    if (state.tab === "intel") updateRail();
  }
}

type CallNode = { name: string; path: string | null };

async function loadSymbols(path: string): Promise<void> {
  state.intelFile = path;
  state.intelSymbol = null;
  state.intelCalls = null;
  state.intelSymbols = await tryInvoke<Array<{ name: string }>>("graph.symbols", { path }, []);
  if (state.tab === "intel") updateRail();
}

async function loadCalls(path: string, name: string): Promise<void> {
  state.intelSymbol = name;
  state.intelCalls = null;
  if (state.tab === "intel") updateRail();
  const [callers, callees] = await Promise.all([
    tryInvoke<CallNode[]>("graph.callers", { path, name }, []),
    tryInvoke<CallNode[]>("graph.callees", { path, name }, []),
  ]);
  state.intelCalls = { callers, callees };
  if (state.tab === "intel") updateRail();
}

function callList(title: string, nodes: CallNode[]): HTMLElement {
  return el("div", { class: "intel-calllist" }, [
    el("div", { class: "intel-h" }, [`${title} (${nodes.length})`]),
    ...(nodes.length === 0
      ? [el("div", { class: "intel-meta" }, ["none"])]
      : nodes.slice(0, 20).map((n) => el("div", { class: "intel-name" }, [`${n.name}${n.path ? ` · ${n.path}` : ""}`]))),
  ]);
}

/**
 * The Intel tab — a live window into Tavsin's compounding code intelligence:
 * detected subsystems, the fragile hotspots to touch carefully, a symbol-level
 * call-graph explorer, and the neural memory-embedding cache. Insight plus a few
 * safe actions (scan, backfill).
 */
// ── Orchestration surface (plan 6.1-6.3) ─────────────────────────────────────
interface OrchLatency { samples: number; p50Ms: number; p95Ms: number; maxMs: number }
interface OrchMetrics {
  traces: number;
  timeToFirstAction: OrchLatency;
  timeToWorkingPreview: OrchLatency;
  retry: { nodes: number; retried: number; rate: number };
  supervisorRejection: { decided: number; rejected: number; rate: number };
}
interface OrchOtel { enabled: boolean; configured: boolean; endpoint: string | null; serviceName: string | null; redact: boolean }
interface OrchDecision {
  id: string; title: string; decision: string; reason: string;
  alternatives: string[]; reconsiderWhen: string; codePaths: string[];
}
interface OrchProcedure { id: string; command: string; successes: number; failures: number; medianMs: number }
interface OrchCase { id: string; goal: string; verifyCommand: string; files: string[] }
interface OrchSummaries { total: number; fresh: number; stale: number; files: number; subsystems: number; running: boolean; scheduled: boolean; lastRunAt: string | null; lastError: string | null }
interface OrchestrationData {
  metrics: OrchMetrics;
  /** null when the surface does not expose it (see webChannels.ts). */
  otel: OrchOtel | null;
  decisions: OrchDecision[];
  procedures: OrchProcedure[];
  cases: OrchCase[] | null;
  summaries: OrchSummaries;
}

const EMPTY_LATENCY: OrchLatency = { samples: 0, p50Ms: 0, p95Ms: 0, maxMs: 0 };

function orchMs(value: number): string {
  return value >= 1000 ? `${(value / 1000).toFixed(1)}s` : `${String(value)}ms`;
}
function orchPct(rate: number): string {
  return `${(rate * 100).toFixed(0)}%`;
}

async function refreshOrchestration(): Promise<void> {
  state.orchestrationLoading = true;
  if (state.tab === "orchestration") updateRail();
  const [metrics, otel, decisions, procedures, summaries] = await Promise.all([
    tryInvoke<OrchMetrics>("metrics.orchestration", undefined, {
      traces: 0,
      timeToFirstAction: EMPTY_LATENCY,
      timeToWorkingPreview: EMPTY_LATENCY,
      retry: { nodes: 0, retried: 0, rate: 0 },
      supervisorRejection: { decided: 0, rejected: 0, rate: 0 },
    }),
    tryInvoke<OrchOtel | null>("otel.status", undefined, null),
    tryInvoke<OrchDecision[]>("decisions.list", undefined, []),
    tryInvoke<OrchProcedure[]>("procedures.list", undefined, []),
    tryInvoke<OrchSummaries>("summaries.status", undefined, {
      total: 0, fresh: 0, stale: 0, files: 0, subsystems: 0, running: false, scheduled: false, lastRunAt: null, lastError: null,
    }),
  ]);
  state.orchestration = { metrics, otel, decisions, procedures, summaries, cases: state.orchestration?.cases ?? null };
  state.orchestrationLoading = false;
  if (state.tab === "orchestration") updateRail();
}

function orchMetricTile(label: string, value: string, hint: string): HTMLElement {
  return el("div", { class: "orch-tile" }, [
    el("div", { class: "orch-tile-label" }, [label]),
    el("div", { class: "orch-tile-value" }, [value]),
    el("div", { class: "orch-tile-hint" }, [hint]),
  ]);
}

function orchestrationPanel(): HTMLElement {
  const refreshBtn = el("button", { class: "btn btn-ghost btn-sm", title: "Recompute from the run history" }, [
    state.orchestrationLoading ? "…" : "Refresh",
  ]);
  refreshBtn.onclick = () => void refreshOrchestration();
  const head = el("div", { class: "panel-head" }, [el("span", {}, ["Orchestration"]), refreshBtn]);

  if (state.orchestration === null) {
    if (!state.orchestrationLoading) void refreshOrchestration();
    return el("div", { class: "panel" }, [head, el("div", { class: "panel-empty" }, ["Loading orchestration metrics…"])]);
  }

  const { metrics: m, otel, decisions, procedures, cases } = state.orchestration;
  const sections: HTMLElement[] = [];

  if (state.orchestrationNote) sections.push(el("div", { class: "intel-fresh" }, [state.orchestrationNote]));

  // ── performance (6.2) ──
  sections.push(el("div", { class: "panel-sub" }, [`Performance · ${String(m.traces)} run${m.traces === 1 ? "" : "s"}`]));
  sections.push(
    el("div", { class: "orch-grid" }, [
      orchMetricTile(
        "First action",
        m.timeToFirstAction.samples ? orchMs(m.timeToFirstAction.p50Ms) : "—",
        m.timeToFirstAction.samples ? `p95 ${orchMs(m.timeToFirstAction.p95Ms)}` : "no runs yet",
      ),
      orchMetricTile(
        "Working preview",
        m.timeToWorkingPreview.samples ? orchMs(m.timeToWorkingPreview.p50Ms) : "—",
        m.timeToWorkingPreview.samples ? `p95 ${orchMs(m.timeToWorkingPreview.p95Ms)}` : "none verified yet",
      ),
      orchMetricTile(
        "Retry rate",
        m.retry.nodes ? orchPct(m.retry.rate) : "—",
        m.retry.nodes ? `${String(m.retry.retried)} of ${String(m.retry.nodes)} nodes` : "no task graphs yet",
      ),
      orchMetricTile(
        "No-evidence rejections",
        m.supervisorRejection.decided ? orchPct(m.supervisorRejection.rate) : "—",
        m.supervisorRejection.decided ? `${String(m.supervisorRejection.rejected)} of ${String(m.supervisorRejection.decided)}` : "nothing decided yet",
      ),
    ]),
  );
  // A zero here is ambiguous — say so rather than let it read as good news.
  if (m.supervisorRejection.decided > 0 && m.supervisorRejection.rejected === 0) {
    sections.push(el("div", { class: "orch-note" }, ["Nothing rejected for missing evidence — either agents are proving their work, or nothing required proof."]));
  }
  sections.push(el("div", { class: "orch-note" }, ["Measured from recorded events, not from what agents said about themselves."]));

  // ── trace export (6.2) ──
  // Omitted entirely when the surface does not expose it — showing "Off" for an
  // answer we were not given would be a confident lie about where data goes.
  if (otel !== null) {
  sections.push(el("div", { class: "panel-sub" }, ["Trace export"]));
  sections.push(
    el("div", { class: "orch-note" }, [
      otel.configured
        ? `Exporting to ${otel.endpoint ?? ""}${otel.redact ? " · redacted" : " · full detail (set TAVSIN_OTEL_REDACT=1 to strip goals, paths and commands)"}`
        : otel.enabled
          ? "TAVSIN_OTEL_EXPORT is set but TAVSIN_OTEL_ENDPOINT is missing — both are required, so nothing is sent."
          : "Off. Traces stay on this machine. This is the only feature that sends data anywhere else.",
    ]),
  );
  }

  // ── persisted summaries (5.2) ──
  const sumBtn = el("button", { class: "btn btn-ghost btn-sm" }, [state.orchestrationBusy ? "…" : "Refresh now"]);
  sumBtn.onclick = () => void refreshSummaries();
  sections.push(el("div", { class: "panel-sub panel-sub-row" }, [el("span", {}, ["Summaries"]), sumBtn]));
  const sum = state.orchestration.summaries;
  sections.push(
    el("div", { class: "orch-note" }, [
      sum.total === 0
        ? "None yet — they describe what each file and subsystem is FOR, and are generated after a scan."
        : `${String(sum.fresh)} current, ${String(sum.stale)} awaiting refresh · ${String(sum.files)} files, ${String(sum.subsystems)} subsystems` +
          (sum.running ? " · refreshing…" : sum.scheduled ? " · queued" : ""),
    ]),
  );
  if (sum.lastError) sections.push(el("div", { class: "orch-note" }, [`Last refresh failed: ${sum.lastError}`]));

  // ── decisions (6.1) ──
  sections.push(el("div", { class: "panel-sub" }, ["Decisions"]));
  if (decisions.length === 0) {
    sections.push(el("div", { class: "panel-empty" }, ["No decision records yet — they capture why something was chosen, and what would make it worth revisiting."]));
  } else {
    for (const d of decisions.slice(0, 20)) {
      sections.push(
        el("div", { class: "orch-card" }, [
          el("div", { class: "orch-card-title" }, [d.title]),
          el("div", { class: "orch-card-body" }, [d.decision]),
          el("div", { class: "orch-card-meta" }, [d.reason]),
          ...(d.alternatives.length > 0 ? [el("div", { class: "orch-card-meta" }, [`Instead of: ${d.alternatives.join("; ")}`])] : []),
          el("div", { class: "orch-card-revisit" }, [`Revisit when: ${d.reconsiderWhen}`]),
        ]),
      );
    }
  }

  // ── procedures (6.1) ──
  const promoteBtn = el("button", { class: "btn btn-ghost btn-sm" }, [state.orchestrationBusy ? "…" : "Find from run history"]);
  promoteBtn.onclick = () => void promoteProcedures();
  sections.push(el("div", { class: "panel-sub panel-sub-row" }, [el("span", {}, ["Procedures"]), promoteBtn]));
  if (procedures.length === 0) {
    sections.push(el("div", { class: "panel-empty" }, ["No procedures yet. Commands that succeed across several separate runs get promoted here so agents stop guessing them."]));
  } else {
    for (const p of procedures.slice(0, 25)) {
      sections.push(
        el("div", { class: "orch-row" }, [
          el("code", { class: "orch-cmd" }, [p.command]),
          el("span", { class: "orch-row-meta" }, [
            `${String(p.successes)} run${p.successes === 1 ? "" : "s"}${p.failures > 0 ? `, ${String(p.failures)} failed` : ""} · ~${orchMs(p.medianMs)}`,
          ]),
        ]),
      );
    }
  }

  // ── private benchmarks (6.3) ──
  const mineBtn = el("button", { class: "btn btn-ghost btn-sm" }, [state.orchestrationBusy ? "…" : "Mine from run history"]);
  mineBtn.onclick = () => void mineBenchmarks();
  sections.push(el("div", { class: "panel-sub panel-sub-row" }, [el("span", {}, ["Private benchmarks"]), mineBtn]));
  sections.push(el("div", { class: "orch-note" }, ["Regression cases built from runs that genuinely finished and were checked. Each reuses the command that actually verified that run."]));
  if (cases !== null) {
    if (cases.length === 0) {
      sections.push(el("div", { class: "panel-empty" }, ["No cases yet — finish some runs that end in a real check (tests, typecheck, lint)."]));
    } else {
      for (const c of cases.slice(0, 25)) {
        sections.push(
          el("div", { class: "orch-card" }, [
            el("div", { class: "orch-card-body" }, [c.goal]),
            el("div", { class: "orch-card-meta" }, [`${c.verifyCommand}${c.files.length > 0 ? ` · ${String(c.files.length)} file${c.files.length === 1 ? "" : "s"}` : ""}`]),
          ]),
        );
      }
    }
  }

  return el("div", { class: "panel" }, [head, ...sections]);
}

async function promoteProcedures(): Promise<void> {
  state.orchestrationBusy = true;
  updateRail();
  const report = await tryInvoke<{ promoted: number; rejectedAsFlaky: string[]; scannedTraces: number }>(
    "procedures.promote",
    undefined,
    { promoted: 0, rejectedAsFlaky: [], scannedTraces: 0 },
  );
  state.orchestrationNote =
    `Scanned ${String(report.scannedTraces)} runs — promoted ${String(report.promoted)}` +
    (report.rejectedAsFlaky.length > 0 ? `, rejected ${String(report.rejectedAsFlaky.length)} as too flaky` : "");
  state.orchestrationBusy = false;
  await refreshOrchestration();
}

async function refreshSummaries(): Promise<void> {
  state.orchestrationBusy = true;
  updateRail();
  const res = await tryInvoke<{ passes: number; fresh: number; stale: number }>("summaries.refresh", undefined, { passes: 0, fresh: 0, stale: 0 });
  state.orchestrationNote = `Summaries refreshed in ${String(res.passes)} pass${res.passes === 1 ? "" : "es"} — ${String(res.fresh)} current, ${String(res.stale)} left`;
  state.orchestrationBusy = false;
  await refreshOrchestration();
}

async function mineBenchmarks(): Promise<void> {
  state.orchestrationBusy = true;
  updateRail();
  const cases = await tryInvoke<OrchCase[]>("benchmarks.mine", undefined, []);
  if (state.orchestration) state.orchestration = { ...state.orchestration, cases };
  state.orchestrationBusy = false;
  updateRail();
}

function intelPanel(): HTMLElement {
  const scanBtn = el("button", { class: "btn btn-ghost btn-sm", title: "Rescan the project graph" }, [state.intelBusy ? "…" : "Scan"]);
  scanBtn.onclick = () => void runScan();
  const head = el("div", { class: "panel-head" }, [el("span", {}, ["Intelligence"]), scanBtn]);

  if (state.intel === null) {
    if (!state.intelLoading) void refreshIntel();
    return el("div", { class: "panel" }, [head, el("div", { class: "panel-empty" }, ["Loading codebase intelligence…"])]);
  }
  const { subsystems, fragile, embed, status } = state.intel;
  const sections: HTMLElement[] = [];

  // Scan freshness — so a stale graph is never mistaken for the current code.
  const freshness = status.scanned && status.at
    ? `Scanned ${relTime(status.at)} · ${status.nodes ?? 0} nodes`
    : "Not scanned yet — click Scan.";
  sections.push(el("div", { class: "intel-fresh" }, [freshness]));

  // Subsystems
  const subRows = subsystems.length === 0
    ? [el("div", { class: "panel-empty" }, ["No subsystems yet — run a scan to cluster the codebase."])]
    : subsystems.slice(0, 12).map((s) =>
        el("div", { class: "intel-row" }, [
          el("span", { class: "intel-name" }, [s.name]),
          el("span", { class: "intel-meta" }, [`${s.fileCount} files${s.riskiestFragility !== null ? ` · riskiest ${(s.riskiestPath ?? "").split("/").pop() ?? ""} ${s.riskiestFragility.toFixed(2)}` : ""}`]),
        ]),
      );
  sections.push(el("div", { class: "intel-section" }, [el("div", { class: "intel-h" }, ["Subsystems"]), ...subRows]));

  // Fragile hotspots — clickable to explore the file's call graph.
  const fragRows = fragile.length === 0
    ? [el("div", { class: "panel-empty" }, ["No fragility data yet."])]
    : fragile.map((f) => {
        const path = (f.path ?? f.name).replace(/^file:/, "");
        const row = el("div", { class: `intel-row intel-row-click ${state.intelFile === path ? "intel-row-on" : ""}` }, [
          el("span", { class: "intel-name" }, [path]),
          el("span", { class: "intel-meta" }, [f.fragility !== null ? f.fragility.toFixed(2) : "—"]),
        ]);
        row.onclick = () => void loadSymbols(path);
        return row;
      });
  sections.push(el("div", { class: "intel-section" }, [el("div", { class: "intel-h" }, ["Fragile hotspots"]), ...fragRows]));

  // Call-graph explorer for the picked file.
  if (state.intelFile) {
    const chips = state.intelSymbols.length === 0
      ? [el("span", { class: "intel-meta" }, ["No exported symbols in this file."])]
      : state.intelSymbols.map((s) => {
          const chip = el("button", { class: `intel-chip ${state.intelSymbol === s.name ? "intel-chip-on" : ""}` }, [s.name]);
          chip.onclick = () => void loadCalls(state.intelFile as string, s.name);
          return chip;
        });
    const callBlocks: HTMLElement[] = [];
    if (state.intelSymbol && state.intelCalls) {
      callBlocks.push(callList("Called by", state.intelCalls.callers), callList("Calls", state.intelCalls.callees));
    }
    sections.push(
      el("div", { class: "intel-section" }, [
        el("div", { class: "intel-h" }, [`Call graph · ${state.intelFile.split("/").pop() ?? state.intelFile}`]),
        el("div", { class: "intel-chips" }, chips),
        ...callBlocks,
      ]),
    );
  }

  // Memory embedding cache
  const backfillBtn = el("button", { class: "btn btn-sm", title: "Compute neural embeddings for memories (needs an OpenAI key)" }, [state.intelBusy ? "…" : "Backfill"]);
  backfillBtn.onclick = () => void backfillEmbeddings();
  const embedLine = embed.total === 0
    ? "No approved memories to embed yet."
    : `${embed.embedded}/${embed.total} embedded${embed.pending > 0 ? ` · ${embed.pending} pending` : ""}${embed.model ? ` · ${embed.model}` : ""}`;
  sections.push(
    el("div", { class: "intel-section" }, [
      el("div", { class: "intel-h" }, ["Memory embeddings"]),
      el("div", { class: "intel-row" }, [el("span", { class: "intel-meta" }, [embedLine]), embed.pending > 0 ? backfillBtn : el("span", {})]),
    ]),
  );

  // Memory upkeep — approved beliefs, each checkable for what it supersedes.
  const approved = (state.intel.memories ?? []).filter((m) => m.approvalStatus === "approved");
  if (approved.length > 0) {
    const upkeep: HTMLElement[] = [el("div", { class: "intel-h" }, ["Memory upkeep"])];
    for (const m of approved.slice(0, 8)) {
      const row = el("div", { class: `intel-row intel-row-click ${state.intelContraFor === m.id ? "intel-row-on" : ""}` }, [
        el("span", { class: "intel-name" }, [m.title]),
        el("span", { class: "intel-meta" }, ["supersedes?"]),
      ]);
      row.onclick = () => void checkContradictions(m.id);
      upkeep.push(row);
      if (state.intelContraFor === m.id) {
        if (state.intelContraBusy) {
          upkeep.push(el("div", { class: "intel-meta", style: "padding:4px 14px" }, ["Checking…"]));
        } else if (state.intelContra && state.intelContra.length > 0) {
          for (const c of state.intelContra) {
            upkeep.push(el("div", { class: "intel-meta", style: "padding:2px 22px" }, [`${(c.similarity * 100).toFixed(0)}% · ${c.stale.title}`]));
          }
          const retire = el("button", { class: "btn btn-sm", style: "margin:4px 14px 6px" }, [`Retire ${state.intelContra.length} superseded`]);
          retire.onclick = (e) => { e.stopPropagation(); void retireSuperseded(m.id); };
          upkeep.push(retire);
        } else {
          upkeep.push(el("div", { class: "intel-meta", style: "padding:2px 22px 6px" }, ["Nothing superseded by this memory."]));
        }
      }
    }
    sections.push(el("div", { class: "intel-section" }, upkeep));
  }

  return el("div", { class: "panel intel" }, [head, ...sections]);
}

const PREVIEW_DEVICES: ReadonlyArray<{ id: PreviewDevice; label: string; w: number; h: number }> = [
  { id: "web", label: "🖥 Web", w: 0, h: 0 },
  { id: "iphone", label: "▢ iPhone", w: 390, h: 844 },
  { id: "android", label: "▢ Android", w: 360, h: 800 },
];

/**
 * The Preview tab — a live, device-framed window onto the running app. Loads the
 * dev server URL in an iframe wrapped in a phone/web frame so you can see exactly
 * how the build looks on iPhone / Android / desktop, right inside the workspace.
 * (Web-based preview renders the app's *web* build; a browser can't run a native
 * iOS/Android binary — no in-browser tool can — but responsive & Expo apps are
 * pixel-accurate at these viewports.)
 */
/** Point-and-edit state for the built-site preview (see visualEdit.ts). */
let previewPicking = false;
let previewPickOff: (() => void) | null = null;

const siteIo: FileIo = {
  read: async (path) => (await invoke<{ content: string }>("fs.read", { path })).content,
  write: async (path, content) => {
    await invoke("fs.write", { path, content });
  },
};

function reloadPreviewFrame(): void {
  const f = document.querySelector(".preview-frame iframe") as HTMLIFrameElement | null;
  if (f) { const src = f.src; f.src = "about:blank"; requestAnimationFrame(() => { f.src = src; }); }
}

function previewPanel(): HTMLElement {
  const device = PREVIEW_DEVICES.find((d) => d.id === state.previewDevice) ?? PREVIEW_DEVICES[0]!;
  previewPickOff?.();
  previewPickOff = null;

  const switcher = el("div", { class: "seg preview-devices" },
    PREVIEW_DEVICES.map((d) => {
      const b = el("button", { class: `seg-btn ${state.previewDevice === d.id ? "seg-on" : ""}` }, [d.label]);
      b.onclick = () => { state.previewDevice = d.id; updateRail(); };
      return b;
    }),
  );

  const urlInput = el("input", { class: "preview-url", type: "text", placeholder: "http://localhost:3000", value: state.previewUrl }) as HTMLInputElement;
  const applyUrl = (): void => {
    state.previewUrl = urlInput.value.trim();
    try { localStorage.setItem(PREVIEW_URL_KEY, state.previewUrl); } catch { /* ignore */ }
    updateRail();
  };
  urlInput.addEventListener("keydown", (e) => { if (e.key === "Enter") applyUrl(); });
  urlInput.addEventListener("blur", applyUrl);

  const reload = el("button", { class: "btn btn-ghost btn-sm", title: "Reload preview" }, ["↻"]);
  reload.onclick = reloadPreviewFrame;
  const openExt = el("button", { class: "btn btn-ghost btn-sm", title: "Open in a new tab" }, ["↗"]);
  openExt.onclick = () => { if (state.previewUrl) window.open(state.previewUrl, "_blank", "noopener"); };
  const detect = el("button", { class: "btn btn-ghost btn-sm", title: "Auto-detect the running dev server" }, ["⌖"]);
  detect.onclick = async () => {
    try {
      const r = await withAuth(() => invoke<{ url: string; source: string; framework: string }>("preview.detect"), null);
      if (r?.url) {
        state.previewUrl = r.url;
        try { localStorage.setItem(PREVIEW_URL_KEY, r.url); } catch { /* ignore */ }
        updateRail();
        const fw = r.framework && r.framework !== "unknown" ? `${r.framework} · ` : "";
        const live = r.source.includes("listening");
        toast(live ? `Detected ${fw}${r.url}` : `No live server — guessed ${fw}${r.url}`, live ? "success" : "info");
      }
    } catch { toast("Could not detect a dev server", "error"); }
  };
  // Static preview: render the agent-built site (index.html) straight from the
  // project — no dev server needed. Served by the sandboxed /preview route.
  const built = el("button", { class: "btn btn-primary btn-sm", title: "Preview the built site (index.html) — no dev server needed" }, ["▶ Built site"]);
  built.onclick = () => {
    state.previewUrl = staticPreviewUrl("index.html");
    try { localStorage.setItem(PREVIEW_URL_KEY, state.previewUrl); } catch { /* ignore */ }
    updateRail();
  };

  // Server-side live dev server — only shown when the host has the feature on.
  // Probe once (async) so the control appears only where it actually works.
  if (state.liveSandbox === null) {
    void devSandboxStatus().then((s) => { state.liveSandbox = { available: s.available, running: s.running }; if (state.tab === "preview") updateRail(); });
  }
  const liveActions: HTMLElement[] = [];
  if (state.liveSandbox?.available) {
    const running = state.liveSandbox.running;
    const live = el("button", { class: "btn btn-ghost btn-sm", title: "Run the project's dev server in an isolated sandbox and preview it live" }, [running ? "■ Stop live" : "▶ Live server"]);
    live.onclick = () => {
      if (running) {
        void stopDevSandbox().then(() => { state.liveSandbox = { available: true, running: false }; if (state.previewUrl.startsWith("/preview/live/")) { state.previewUrl = staticPreviewUrl("index.html"); } updateRail(); });
        return;
      }
      toast("Starting the dev server…", "info");
      void startDevSandbox().then((r) => {
        if ("url" in r) {
          state.previewUrl = r.url;
          state.liveSandbox = { available: true, running: true };
          try { localStorage.setItem(PREVIEW_URL_KEY, r.url); } catch { /* ignore */ }
          updateRail();
          toast("Live dev server is up", "success");
        } else {
          const msg = r.error === "tier" ? "Live preview needs a Team plan" : r.error === "capacity" ? "The server is at capacity — try again shortly" : r.error === "disabled" ? "Live preview isn't enabled on this server" : `Couldn't start the dev server: ${r.error}`;
          toast(msg, "error");
        }
      });
    };
    liveActions.push(live);
  }

  // No-model editing of the built site: click an element to change its text,
  // or open the theme panel to change the design tokens.
  const siteTools: HTMLElement[] = [];
  const overlay = el("div", { class: "vedit-layer" });
  let iframeRef: HTMLIFrameElement | null = null;
  if (state.previewUrl.startsWith("/preview") && !state.previewUrl.startsWith("/preview/live/")) {
    const editBtn = el("button", { class: `btn btn-ghost btn-sm${previewPicking ? " is-on" : ""}`, type: "button", title: "Click an element in the page to change it — no model call", "aria-pressed": String(previewPicking) }, ["✎ Edit"]);
    editBtn.onclick = () => {
      previewPicking = !previewPicking;
      editBtn.classList.toggle("is-on", previewPicking);
      editBtn.setAttribute("aria-pressed", String(previewPicking));
      if (iframeRef) setPicking(iframeRef, previewPicking);
      if (previewPicking) toast("Click any text in the page to edit it. Esc to stop.", "info");
      else overlay.replaceChildren();
    };
    const themeBtn = el("button", { class: "btn btn-ghost btn-sm", type: "button", title: "Change the site's colours and fonts — no model call" }, ["◐ Theme"]);
    themeBtn.onclick = async () => {
      const found = await findTokenFile(siteIo).catch(() => null);
      if (!found) {
        toast("No design tokens found (a :root block in styles/tokens.css or styles.css). Start from a template, or ask Tavsin to add them.", "info");
        return;
      }
      overlay.replaceChildren(themeEditor(found, {
        io: siteIo,
        onSaved: () => { overlay.replaceChildren(); reloadPreviewFrame(); toast(`Theme saved to ${found.path}`, "success"); },
        onClose: () => overlay.replaceChildren(),
      }));
    };
    siteTools.push(editBtn, themeBtn);
  }

  const head = el("div", { class: "panel-head preview-head" }, [switcher, el("div", { class: "preview-head-actions" }, [built, ...siteTools, ...liveActions, detect, reload, openExt])]);
  const previewKind = !state.previewUrl
    ? "Nothing to show yet"
    : state.previewUrl.startsWith("/preview/live/")
      ? "Live server, inside Tavsin"
      : state.previewUrl.startsWith("/preview")
        ? "Built site, inside Tavsin"
        : "External URL";
  const kicker = el("div", { class: "preview-kicker" }, [previewKind]);

  const isStatic = state.previewUrl.startsWith("/preview");
  let body: HTMLElement;
  if (!state.previewUrl) {
    body = el("div", { class: "preview-stage preview-stage-empty" }, [
      el("div", { class: "preview-empty" }, [
        el("p", { class: "preview-empty-folio" }, ["Stage · 01"]),
        el("p", { class: "preview-empty-title" }, ["The page lands here"]),
        el("p", {}, ["Pick a look, or write a brief. When files change, this stage opens the built site — phone, tablet width, or the full canvas."]),
        el("p", { class: "preview-empty-looks" }, [[POSSIBILITIES.label, ...LOOKS.map((l) => l.label)].join(" · ")]),
      ]),
    ]);
  } else if (!isStatic && isMixedContent(state.previewUrl)) {
    // An http:// dev-server URL can't be framed inside this https app — the
    // browser blocks it as mixed content and the frame would just sit empty.
    // Say so, and point at the two things that DO work.
    body = el("div", { class: "panel-empty" }, [
      el("p", {}, [`“${state.previewUrl}” can’t load here — a browser blocks an http:// page inside this secure (https) app.`]),
      el("p", {}, ["Use ", el("b", {}, ["▶ Built site"]), " to preview what the agent built, or enter an https:// dev-server URL."]),
    ]);
  } else {
    // Our own /preview content is sandboxed (no same-origin) so AI-generated
    // HTML can't touch this app's session; external dev servers are cross-origin.
    const iframe = el("iframe", { class: "preview-iframe", src: isStatic ? withPreviewToken(state.previewUrl) : state.previewUrl, title: "App preview", loading: "lazy", ...(isStatic ? { sandbox: "allow-scripts allow-forms allow-popups allow-modals" } : {}) });
    if (isStatic) {
      iframeRef = iframe;
      // A reload loses the frame's picker state; turn it back on.
      iframe.addEventListener("load", () => { if (previewPicking) setPicking(iframe, true); });
      previewPickOff = listenForPicks(
        iframe,
        (picked) => {
          overlay.replaceChildren(pickedEditor(picked, {
            io: siteIo,
            onDone: (changed) => {
              overlay.replaceChildren();
              if (changed) { reloadPreviewFrame(); toast(`Saved to ${changed}`, "success"); }
            },
            onAsk: (request) => void send(request),
          }));
        },
        () => {
          previewPicking = false;
          setPicking(iframe, false);
          overlay.replaceChildren();
          document.querySelector(".preview-head .is-on")?.classList.remove("is-on");
        },
      );
    }
    // Device frames set an explicit aspect via CSS; web fills the panel.
    const frameClass = device.id === "web" ? "preview-frame preview-web" : `preview-frame preview-phone preview-${device.id}`;
    const frame = el("div", { class: frameClass, ...(device.w ? { style: `aspect-ratio:${device.w}/${device.h}` } : {}) }, [iframe]);
    const dim = device.w ? el("div", { class: "preview-dim" }, [`${device.w} × ${device.h}`]) : null;
    body = el("div", { class: "preview-stage" }, [frame, dim, overlay]);
  }

  const urlBar = el("div", { class: "preview-urlbar" }, [urlInput]);
  return el("div", { class: "panel preview" }, [head, kicker, body, urlBar]);
}

/** Changes rail — today's diffs, opt-in GitHub publish, recent runs. */
function changesPanel(): HTMLElement {
  const refresh = el("button", { class: "iconbtn iconbtn-sm", title: "Refresh" }, [el("span", { class: "glyph", html: ICON.refresh })]);
  refresh.onclick = () => { void refreshChanges(); void refreshJobs(); };
  const head = el("div", { class: "panel-head" }, [el("span", {}, ["Today"]), refresh]);

  let changesBlock: HTMLElement;
  if (state.changed.length === 0 && !state.openDiff) {
    const start = el("button", { class: "empty-cta" }, ["Start your first build"]);
    start.onclick = () => void newSession();
    changesBlock = el("div", { class: "empty-card" }, [
      el("div", { class: "empty-ico", html: ICON.box }),
      el("div", { class: "empty-title" }, ["No changes yet"]),
      el("div", { class: "empty-sub" }, ["Start a build and Tavsin will show proposed changes here."]),
      start,
    ]);
  } else {
    const files = el("div", { class: "files" }, state.changed.map((f) => {
      const letter = f.label.slice(0, 1).toUpperCase();
      const badgeClass = `file-badge badge-${letter === "M" || letter === "A" || letter === "D" || letter === "R" ? letter : "M"}`;
      const item = el("button", { class: `file ${state.openDiff?.path === f.path ? "file-active" : ""}` }, [el("span", { class: badgeClass }, [letter]), el("span", { class: "file-path" }, [f.path])]);
      item.onclick = () => void openDiff(f.path);
      const discard = el("button", { class: "file-discard", title: "Discard this change (restore to HEAD)", "aria-label": `Discard ${f.path}` }, ["↺"]);
      discard.onclick = (e) => { e.stopPropagation(); void discardFile(f.path); };
      return el("div", { class: "change-row" }, [item, discard]);
    }));
    let viewer: HTMLElement | null = null;
    if (state.openDiff) {
      const { adds, dels } = diffStats(state.openDiff.diff);
      const diffHead = el("div", { class: "diff-head" }, [
        el("span", { class: "diff-head-path" }, [state.openDiff.path]),
        el("span", { class: "diff-stat" }, [el("span", { class: "diff-stat-add" }, [`+${adds}`]), el("span", { class: "diff-stat-del" }, [`−${dels}`])]),
      ]);
      viewer = el("div", {}, [diffHead, ...diffHunks(state.openDiff.path, state.openDiff.diff)]);
    }
    changesBlock = el("div", {}, [publishBar(), files, viewer]);
  }

  return el("div", { class: "panel" }, [head, changesBlock, recentRunsCard(), projectRepoCard()]);
}

/**
 * Opt-in publish. Nothing is sent until the person confirms, and the channel
 * uses only the token they stored — a missing token opens Settings.
 */
function publishBar(): HTMLElement {
  const go = el("button", { class: "btn btn-primary btn-sm" }, ["Push to GitHub"]);
  go.onclick = () => openPublish();
  return el("div", { class: "publish-bar" }, [
    el("p", { class: "publish-note" }, ["These files stay here until you send them. Push creates a tavsin/ branch on your repo and can open a pull request."]),
    go,
  ]);
}

/** Directories, the local store, git metadata, and dependency trees are never sent. */
function publishablePath(path: string): boolean {
  if (path.endsWith("/") || path.endsWith("\\")) return false;
  const parts = path.replace(/\\/g, "/").replace(/^\.\//, "").split("/");
  return !parts.some((p) => p === "" || p === "." || p === ".." || p === ".tavsin" || p === ".git" || p === "node_modules");
}

function openPublish(): void {
  const paths = state.changed.map((f) => f.path).filter(publishablePath);
  const held = state.changed.length - paths.length;
  if (paths.length === 0) {
    toast(held > 0 ? "Those paths stay on this machine" : "Nothing to publish yet", "info");
    return;
  }
  const message = el("input", { class: "field", value: "Update from Tavsin" }) as HTMLInputElement;
  const branch = el("input", { class: "field", value: `tavsin/ship-${Date.now().toString(36)}` }) as HTMLInputElement;
  const pull = el("input", { type: "checkbox" }) as HTMLInputElement;
  pull.checked = true;
  const status = el("p", { class: "drawer-note" }, []);
  const shown = paths.slice(0, 12).map((p) => el("li", {}, [p]));
  if (paths.length > 12) shown.push(el("li", {}, [`and ${String(paths.length - 12)} more`]));
  const go = el("button", { class: "btn btn-primary" }, ["Push to GitHub"]) as HTMLButtonElement;
  go.onclick = () => {
    go.disabled = true;
    go.textContent = "Pushing…";
    void (async () => {
      try {
        const r = await invoke<{
          ok: boolean;
          needsToken?: boolean;
          error: string | null;
          branch: string | null;
          prUrl: string | null;
          commitUrl: string | null;
        }>("github.publish", {
          paths,
          message: message.value.trim(),
          branch: branch.value.trim(),
          openPull: pull.checked,
          title: message.value.trim(),
        });
        if (r.needsToken || (r.ok === false && /Settings/.test(r.error ?? ""))) {
          closeModal();
          toast(r.error ?? "Connect GitHub in Settings", "info", { label: "Settings", onClick: () => openAppSettings() });
          return;
        }
        if (!r.ok) {
          status.textContent = r.error ?? "Publish did not complete";
          go.disabled = false;
          go.textContent = "Push to GitHub";
          return;
        }
        closeModal();
        if (r.prUrl) {
          toast("Pull request opened", "success", { label: "Open", onClick: () => window.open(r.prUrl ?? "", "_blank", "noopener") });
        } else if (r.error) {
          toast(r.error, "info");
        } else {
          toast(`Published ${r.branch ?? "the branch"}`, "success", r.commitUrl ? { label: "Open", onClick: () => window.open(r.commitUrl ?? "", "_blank", "noopener") } : undefined);
        }
      } catch (e) {
        status.textContent = e instanceof Error ? e.message : String(e);
        go.disabled = false;
        go.textContent = "Push to GitHub";
      }
    })();
  };
  showModal("Push to GitHub", el("div", { class: "drawer" }, [
    el("p", { class: "drawer-note", style: "margin-top:0" }, ["Sends only the files listed below, using the token in Settings. Nothing is pushed until you confirm, and main is not updated."]),
    ...(held > 0 ? [el("p", { class: "publish-note" }, ["The local store, git metadata, and dependency folders stay on this machine."])] : []),
    el("div", { class: "set-label" }, [`${String(paths.length)} file${paths.length === 1 ? "" : "s"}`]),
    el("ul", { class: "publish-files" }, shown),
    el("label", { class: "field-label" }, ["Commit message"]),
    message,
    el("label", { class: "field-label" }, ["Branch"]),
    branch,
    el("label", { class: "agent-tool-row" }, [pull, el("span", {}, ["Open a pull request"])]),
    status,
    el("div", { class: "drawer-actions" }, [go]),
  ]));
}

/** RECENT RUNS section on the Changes tab — from real jobs.list. */
function recentRunsCard(): HTMLElement {
  const viewAll = el("button", { class: "section-link" }, ["View all"]);
  viewAll.onclick = () => setTab("runs");
  const head = el("div", { class: "section-head" }, [el("span", {}, ["Recent runs"]), viewAll]);
  const rows = state.jobs.slice(0, 4).map((j) => {
    const dot = el("span", { class: `run-status run-${j.status}` });
    const filesChanged = j.status === "completed" ? "" : j.status === "running" ? "running…" : j.status;
    const row = el("button", { class: "runrow" }, [
      dot,
      el("div", { class: "runrow-body" }, [
        el("div", { class: "runrow-goal" }, [j.goal || "Autonomous run"]),
        el("div", { class: "runrow-meta" }, [filesChanged || (j.summary ? j.summary.slice(0, 40) : "done")]),
      ]),
      el("span", { class: "runrow-caret", html: ICON.chevron }),
    ]);
    row.onclick = () => { selectAgentJob(j.id); setTab("runs"); };
    return row;
  });
  const body = rows.length ? el("div", { class: "runlist" }, rows) : el("div", { class: "section-empty" }, ["No runs yet."]);
  return el("div", { class: "section" }, [head, body]);
}

/** PROJECT REPO card — GitHub owner/repo link when connected, else local repo. */
function projectRepoCard(): HTMLElement {
  const head = el("div", { class: "section-head" }, [el("span", {}, ["Project repo"])]);
  const updated = state.lastCommitAt ? relTime(state.lastCommitAt) : null;
  const gh = state.githubRepo;
  const name = gh ? `${gh.owner}/${gh.repo}` : (state.projectName ?? "workspace");
  const metaBits = [
    ...(state.gitBranch ? [`⑂ ${state.gitBranch}`] : []),
    ...(updated ? [`Updated ${updated}`] : []),
    ...(!state.isRepo && !gh ? ["Local project (no git)"] : []),
  ].join("  ·  ");
  const inner = [
    el("span", { class: "repocard-ico", html: ICON.github }),
    el("div", { class: "repocard-body" }, [
      el("div", { class: "repocard-name" }, [name]),
      el("div", { class: "repocard-meta" }, [metaBits || "—"]),
    ]),
    ...(gh ? [el("span", { class: "repocard-ext", html: ICON.arrowR })] : []),
  ];
  // With a git remote, the whole card links out to it (branch-aware on GitHub/GitLab).
  const isGitHost = gh ? /github\.com|gitlab\.com|bitbucket\.org/.test(gh.host) : false;
  const href = gh ? `${gh.webUrl}${isGitHost && state.gitBranch ? `/tree/${state.gitBranch}` : ""}` : "";
  const card = gh
    ? el("a", {
        class: "repocard repocard-link",
        href,
        target: "_blank",
        rel: "noopener",
        title: `Open ${gh.owner}/${gh.repo} on ${gh.host}`,
      }, inner)
    : el("div", { class: "repocard" }, inner);
  return el("div", { class: "section" }, [head, card]);
}

function diffStats(diff: string): { adds: number; dels: number } {
  let adds = 0;
  let dels = 0;
  for (const line of diff.split("\n")) {
    if (line.startsWith("+") && !line.startsWith("+++")) adds++;
    else if (line.startsWith("-") && !line.startsWith("---")) dels++;
  }
  return { adds, dels };
}

/**
 * The open diff, one block per hunk, each with Revert. Revert undoes that hunk
 * on disk and keeps the rest; the server refuses, writing nothing, when those
 * lines changed since the diff was read.
 */
function diffHunks(path: string, diff: string): HTMLElement[] {
  const { hunks } = diffSections(diff);
  if (hunks.length === 0) return [el("pre", { class: "code diff", html: colorDiff(diff) })];
  return hunks.map((lines, i) => {
    const revert = el("button", { class: "btn btn-ghost btn-sm hunk-revert", title: "Undo this part of the change and keep the rest", "aria-label": `Revert hunk ${String(i + 1)} of ${path}` }, ["Revert hunk"]);
    revert.onclick = () => void revertHunk(path, diff, i, revert);
    return el("div", { class: "hunk" }, [
      el("div", { class: "hunk-bar" }, [el("span", { class: "hunk-label" }, [`Hunk ${String(i + 1)} of ${String(hunks.length)}`]), revert]),
      el("pre", { class: "code diff", html: colorDiff(lines.join("\n")) }),
    ]);
  });
}

async function revertHunk(path: string, diff: string, hunk: number, button: HTMLButtonElement): Promise<void> {
  button.disabled = true;
  const r = await withAuth(() => invoke<{ ok: boolean; reason: string | null; removed: boolean }>("changes.rejectHunk", { path, diff, hunk }), null);
  if (!r) { button.disabled = false; toast("Could not revert that hunk", "error"); return; }
  if (!r.ok) { button.disabled = false; toast(r.reason ?? "That hunk was left as it is", "error"); return; }
  toast(r.removed ? `Removed ${path}` : `Reverted hunk ${String(hunk + 1)} of ${path}`, "success");
  await refreshChanges();
  if (r.removed) { state.openDiff = null; updateRail(); return; }
  await openDiff(path);
}

function colorDiff(diff: string): string {
  return escapeHtml(diff)
    .split("\n")
    .map((line) => {
      if (line.startsWith("+") && !line.startsWith("+++")) return `<span class="d-add">${line}</span>`;
      if (line.startsWith("-") && !line.startsWith("---")) return `<span class="d-del">${line}</span>`;
      if (line.startsWith("@@")) return `<span class="d-hunk">${line}</span>`;
      return `<span class="d-ctx">${line}</span>`;
    })
    .join("\n");
}


// Ranked code search over the project ("by meaning" when embeddings are on).
let codeSearchQuery = "";
let codeSearchHits: Array<{ path: string; startLine: number; snippet: string }> = [];
let codeSearchMode: "hybrid" | "lexical" = "lexical";
let codeSearchBusy = false;
let codeSearchTimer: ReturnType<typeof setTimeout> | undefined;

function runCodeSearch(query: string): void {
  codeSearchQuery = query;
  if (codeSearchTimer) clearTimeout(codeSearchTimer);
  if (query.trim().length < 3) {
    codeSearchHits = [];
    codeSearchBusy = false;
    updateRail();
    return;
  }
  codeSearchBusy = true;
  codeSearchTimer = setTimeout(() => {
    void withAuth(
      () => invoke<{ hits: typeof codeSearchHits; mode: "hybrid" | "lexical" }>("code.search", { query: query.trim() }),
      null,
    ).then((r) => {
      if (codeSearchQuery.trim() !== query.trim()) return; // stale response
      codeSearchHits = r?.hits ?? [];
      codeSearchMode = r?.mode ?? "lexical";
      codeSearchBusy = false;
      updateRail();
    });
  }, 350);
}

function filesPanel(): HTMLElement {
  const refresh = el("button", { class: "iconbtn iconbtn-sm", title: "Refresh files" }, [el("span", { class: "glyph", html: ICON.refresh })]);
  refresh.onclick = () => void loadTree(true);
  const head = el("div", { class: "panel-head" }, [el("span", {}, ["Files"]), refresh]);

  const searchInp = el("input", {
    class: "side-search-input",
    type: "text",
    placeholder: "Search code by meaning…",
    value: codeSearchQuery,
  }) as HTMLInputElement;
  searchInp.oninput = () => runCodeSearch(searchInp.value);
  const searchBox = el("div", { class: "side-search" }, [searchInp]);

  const searching = codeSearchQuery.trim().length >= 3;
  let body: HTMLElement;
  if (searching) {
    const rows = codeSearchHits.map((h) => {
      const row = el("button", { class: "search-hit", title: h.snippet }, [
        el("span", { class: "search-hit-path" }, [`${h.path}:${h.startLine}`]),
        el("span", { class: "search-hit-line" }, [h.snippet.split("\n")[0] ?? ""]),
      ]);
      row.onclick = () => void openFile(h.path);
      return row;
    });
    const label = codeSearchBusy ? "Ranking…" : codeSearchMode === "hybrid" ? "Ranked by meaning (semantic + identifiers)" : "Ranked by identifiers — enable embeddings in Settings for true semantic search";
    body = el("div", { class: "tree" }, [
      el("div", { class: "search-mode" }, [label]),
      ...(rows.length ? rows : codeSearchBusy ? [] : [el("div", { class: "panel-empty" }, ["No matches."])]),
    ]);
  } else {
    const empty = state.tree === null ? "No files yet — ask Tavsin to build something." : "Loading…";
    body = el("div", { class: "tree" }, state.tree && state.tree.length ? state.tree.flatMap((c) => renderNode(c, 0)) : [el("div", { class: "panel-empty" }, [empty])]);
  }
  let viewer: HTMLElement | null = null;
  if (state.openFile) viewer = fileEditor();
  else destroyWebEditor();
  return el("div", { class: "panel" }, [head, searchBox, body, viewer]);
}

/**
 * In-app code editor. One CodeMirror kernel mount, persisted outside
 * render() so a rail rebuild does not drop cursor or undo. Cmd/Ctrl+S
 * saves through fs.write; Tab is the kernel's indentWithTab.
 */
const MARKDOWN_EXTS = ["md", "markdown", "mdx"];
let savePaintScheduled = false;

function scheduleSavePaint(): void {
  if (savePaintScheduled) return;
  savePaintScheduled = true;
  requestAnimationFrame(() => {
    savePaintScheduled = false;
    paintFileEditorChrome();
  });
}

function paintFileEditorChrome(): void {
  const file = state.openFile;
  if (!file) return;
  const head = document.querySelector(".file-view-head");
  if (!head) return;
  const name = file.path.split("/").pop() ?? file.path;
  const dirty = (state.editBuffer ?? file.content) !== file.content;
  const pathEl = head.querySelector(".file-view-path");
  const saveBtn = head.querySelector(".btn-save");
  if (pathEl) pathEl.textContent = `${fileIcon(name)} ${file.path}${dirty ? " •" : ""}`;
  if (saveBtn) {
    saveBtn.textContent = state.saving ? "Saving…" : dirty ? "Save" : "Saved";
    (saveBtn as HTMLButtonElement).disabled = !dirty || state.saving;
    saveBtn.classList.toggle("btn-save-clean", !dirty);
    saveBtn.classList.toggle("btn-save-busy", state.saving);
  }
  const banner = document.querySelector(".file-disk-changed");
  if (banner) banner.toggleAttribute("hidden", !state.fileDiskChanged);
}

function diskChangedBanner(): HTMLElement {
  const reload = el("button", { class: "file-disk-action", type: "button", title: "Replace the buffer with the file on disk" }, ["Reload from disk"]);
  reload.onclick = () => void reloadOpenFileFromDisk();
  const keep = el("button", { class: "file-disk-action", type: "button", title: "Keep the current buffer" }, ["Keep"]);
  keep.onclick = () => keepDiskVersion();
  const banner = el("div", { class: "file-disk-changed" }, [
    el("span", { class: "file-disk-changed-msg" }, ["Disk changed — unsaved edits kept"]),
    reload,
    keep,
  ]);
  if (!state.fileDiskChanged) banner.setAttribute("hidden", "");
  return banner;
}

function fileEditor(): HTMLElement {
  if (!state.openFile) return el("span", {});
  const file = state.openFile;
  const buffer = state.editBuffer ?? file.content;
  const dirty = buffer !== file.content;
  const name = file.path.split("/").pop() ?? file.path;
  const ext = file.path.includes(".") ? file.path.slice(file.path.lastIndexOf(".") + 1).toLowerCase() : "";
  const isMarkdown = MARKDOWN_EXTS.includes(ext);
  const langLabel = el("span", { class: "file-lang" }, [webLanguageLabel(file.path).toUpperCase()]);

  const headKids: HTMLElement[] = [
    el("span", { class: "file-view-path" }, [`${fileIcon(name)} ${file.path}${dirty ? " •" : ""}`]),
    langLabel,
  ];
  // Markdown: a Preview ⇄ Code toggle. Preview renders the file as it looks.
  if (isMarkdown) {
    const toggle = el("button", { class: "file-toggle", title: state.filePreview ? "Edit the source" : "Preview rendered" }, [state.filePreview ? "✎ Code" : "👁 Preview"]);
    toggle.onclick = () => { state.filePreview = !state.filePreview; updateRail(); };
    headKids.push(toggle);
  }
  const save = el("button", {
    class: `btn-save ${state.saving ? "btn-save-busy" : ""} ${dirty ? "" : "btn-save-clean"}`,
    title: "Save (⌘/Ctrl+S)",
  }, [state.saving ? "Saving…" : dirty ? "Save" : "Saved"]) as HTMLButtonElement;
  save.disabled = !dirty || state.saving;
  save.onclick = () => void saveFile();
  headKids.push(save);
  const head = el("div", { class: "file-view-head" }, headKids);

  // Rendered Markdown preview. Destroy the kernel — a view change, not a reconfigure.
  // Banner still shows: fileDiskChanged is independent of the controller.
  if (isMarkdown && state.filePreview) {
    destroyWebEditor();
    const preview = el("div", { class: "md-preview turn-body", html: formatBody(buffer) });
    return el("div", { class: "file-view" }, [head, diskChangedBanner(), el("div", { class: "md-preview-wrap" }, [preview])]);
  }

  const slot = el("div", { class: "editor-slot" });
  attachWebEditor({
    slot,
    path: file.path,
    text: buffer,
    savedContent: file.content,
    appearance: currentTheme(),
    ...(state.fileDiskChanged ? { diskChanged: true } : {}),
    callbacks: {
      onChange: (next) => {
        state.editBuffer = next;
        scheduleSavePaint();
      },
      onSave: () => {
        void saveFile();
      },
    },
  });
  return el("div", { class: "file-view" }, [head, diskChangedBanner(), el("div", { class: "editor-wrap" }, [slot])]);
}
function fileIcon(name: string): string {
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1).toLowerCase() : "";
  if (ext === "ts" || ext === "tsx") return "🟦";
  if (ext === "js" || ext === "jsx") return "🟨";
  if (ext === "json") return "📋";
  if (ext === "md") return "📝";
  if (ext === "css" || ext === "scss") return "🎨";
  if (ext === "html") return "🌐";
  if (ext === "py") return "🐍";
  if (ext === "rs") return "⚙";
  if (ext === "go") return "🔹";
  if (ext === "sql") return "🗄";
  if (ext === "yml" || ext === "yaml" || ext === "toml") return "⚙";
  if (ext === "sh" || ext === "bash" || ext === "zsh") return "💻";
  if (ext === "svg" || ext === "png" || ext === "jpg" || ext === "gif") return "🖼";
  if (name === "Dockerfile" || name === ".dockerignore") return "🐳";
  if (name === ".git") return "📂";
  // `.gitignore` is a FILE; it was sharing a branch with `.git` and inheriting
  // the folder icon, so the tree showed it as a directory.
  if (name === ".gitignore") return "📄";
  return "📄";
}

function renderNode(node: FileTreeNode, depth: number): Array<Node> {
  const pad = `padding-left:${8 + depth * 12}px`;
  if (node.type === "dir") {
    const open = state.treeOpen.has(node.path);
    const row = el("button", { class: "tree-row", style: pad }, [`${open ? "▾" : "▸"} 📁 ${node.name}`]);
    row.onclick = () => {
      if (open) state.treeOpen.delete(node.path);
      else state.treeOpen.add(node.path);
      updateRail();
    };
    const out: Array<Node> = [row];
    if (open) for (const c of node.children ?? []) out.push(...renderNode(c, depth + 1));
    return out;
  }
  const row = el("button", { class: `tree-row tree-file ${state.openFile?.path === node.path ? "file-active" : ""}`, style: pad }, [`${fileIcon(node.name)} ${node.name}`]);
  row.onclick = () => void openFile(node.path);
  return [row];
}

/**
 * Deploy rail — PREVIEW first. Cards name the deploy-preview skill and the
 * preview command the skill already knows. Production is the human-only
 * deploy-production skill; this panel will not send `--prod`.
 */
async function loadSites(): Promise<void> {
  try {
    state.sites = await listSites();
  } catch {
    state.sites = { sites: [], limits: { maxSites: 0, maxBytes: 0, maxFiles: 0 }, host: null };
  }
  render();
}

async function launchSite(slug: string, force = false): Promise<void> {
  if (state.sitesBusy) return;
  state.sitesBusy = true;
  render();
  try {
    const r = await publishSite(slug, force);
    toast(`Live at ${r.site.url}`, "success", { label: "Open", onClick: () => window.open(r.site.url, "_blank", "noopener") });
    if (r.skipped.length > 0) toast(`Not published: ${r.skipped.slice(0, 4).join(", ")}${r.skipped.length > 4 ? ` +${String(r.skipped.length - 4)} more` : ""}`, "info");
    if (r.warnings?.length) toast(`Launched with ${String(r.warnings.length)} warning${r.warnings.length === 1 ? "" : "s"}: ${r.warnings.slice(0, 2).map((w) => w.message).join(" ")}`, "info");
    await loadSites();
  } catch (e) {
    // The pre-launch site check refused it: say what, and let the human decide.
    if (e instanceof SitePreflightError) {
      const blockers = e.findings.filter((f) => f.severity === "block");
      toast(`Not launched — ${String(blockers.length)} problem${blockers.length === 1 ? "" : "s"}: ${blockers.slice(0, 3).map((f) => `${f.file}${f.line !== undefined ? `:${String(f.line)}` : ""} ${f.message}`).join(" · ")}`, "error", { label: "Launch anyway", onClick: () => void launchSite(slug, true) });
      return;
    }
    toast(e instanceof Error ? e.message : String(e), "error");
  } finally {
    state.sitesBusy = false;
    render();
  }
}

/** Domains with a DNS check in flight, so the button can say so across re-renders. */
const domainChecks = new Set<string>();

function copyButton(value: string, label: string): HTMLElement {
  const b = el("button", { class: "btn btn-ghost btn-sm", type: "button", title: `Copy ${label}` }, ["Copy"]);
  b.onclick = () => void navigator.clipboard?.writeText(value).then(() => toast(`Copied ${label}`, "success"));
  return b;
}

async function checkSiteDomain(slug: string, domain: string): Promise<void> {
  const key = `${slug}/${domain}`;
  if (domainChecks.has(key)) return;
  domainChecks.add(key);
  render();
  try {
    const { domain: d } = await verifySiteDomain(slug, domain);
    if (d.status === "verified" && !d.lastError) toast(`${d.domain} is connected — HTTPS starts on the first visit`, "success", { label: "Open", onClick: () => window.open(d.url, "_blank", "noopener") });
    else toast(d.lastError ?? "Not verified yet", "info");
  } catch (e) {
    toast(e instanceof Error ? e.message : String(e), "error");
  } finally {
    domainChecks.delete(key);
    await loadSites();
  }
}

/** One claimed domain: its state in words, and — until it is verified — exactly what to put in DNS. */
function domainRow(site: SiteView, d: SiteDomainView): HTMLElement {
  const checking = domainChecks.has(`${site.slug}/${d.domain}`);
  const label = d.status === "verified" ? "Connected" : d.status === "failed" ? "Not verified" : "Waiting for DNS";
  const verify = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, [checking ? "Checking…" : d.status === "verified" ? "Re-check" : "Verify"]) as HTMLButtonElement;
  verify.disabled = checking;
  verify.onclick = () => void checkSiteDomain(site.slug, d.domain);
  const remove = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, ["Remove"]);
  remove.onclick = () => {
    void inlineConfirm(`Remove ${d.domain}? It stops serving ${site.slug} right away.`).then((ok) => {
      if (!ok) return;
      void removeSiteDomain(site.slug, d.domain).then(() => { toast(`${d.domain} removed`, "info"); return loadSites(); }).catch((e: unknown) => toast(e instanceof Error ? e.message : String(e), "error"));
    });
  };
  const children: HTMLElement[] = [
    el("div", { class: "launch-domain-head" }, [
      el("span", { class: `domain-status domain-${d.status}` }, [label]),
      d.status === "verified"
        ? el("a", { class: "launch-url", href: d.url, target: "_blank", rel: "noopener" }, [d.domain])
        : el("span", { class: "launch-url" }, [d.domain]),
      verify,
      remove,
    ]),
  ];
  if (d.lastError) children.push(el("p", { class: "domain-error" }, [d.lastError]));
  if (d.status === "verified" && d.failingSince) {
    const release = new Date(Date.parse(d.failingSince) + 7 * 24 * 60 * 60 * 1000);
    children.push(el("p", { class: "domain-error" }, [`Its DNS has not pointed here since ${new Date(d.failingSince).toLocaleDateString()}. It keeps serving until ${release.toLocaleDateString()}, then it is released — fix the records above to keep it.`]));
  }
  if (d.status !== "verified") {
    children.push(
      el("p", { class: "deploy-desc" }, ["Create these records where your domain's DNS is managed:"]),
      el("div", { class: "dns-records" }, d.records.map((r) =>
        el("div", { class: "dns-record" }, [
          el("span", { class: "dns-type" }, [r.type]),
          el("span", { class: "dns-name", title: r.name }, [r.name]),
          el("span", { class: "dns-value", title: r.value }, [r.value]),
          copyButton(r.value, `${r.type} value`),
        ]),
      )),
      el("p", { class: "deploy-desc" }, [d.note]),
    );
  }
  return el("div", { class: "launch-domain" }, children);
}

/**
 * SITE DATA — what visitors submitted through /_data/ (siteData.ts): one
 * collection at a time, newest first, with export and per-record delete.
 * The owner sees every field, including the ones never publicly readable.
 */
function openSiteData(src: SiteDataSource, title: string, initial?: string): void {
  const body = el("div", { class: "drawer sitedata" }, [el("p", { class: "drawer-note" }, ["Loading…"])]);
  const render = (view: SiteDataView): void => {
    const names = [...new Set([...Object.keys(view.spec?.collections ?? {}), ...Object.keys(view.counts)])];
    const tabs = el("div", { class: "sitedata-tabs", role: "tablist" }, names.map((n) => {
      const b = el("button", { class: `chip${n === view.collection ? " chip-active" : ""}`, type: "button", role: "tab", "aria-selected": n === view.collection ? "true" : "false" }, [`${n} · ${String(view.counts[n] ?? 0)}`]);
      b.onclick = () => void load(n);
      return b;
    }));
    const spec = view.spec?.collections[view.collection];
    const fields = spec ? Object.keys(spec.fields) : [...new Set(view.records.flatMap((r) => Object.keys(r.data)))];
    const exportBtn = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, ["Export CSV"]) as HTMLButtonElement;
    exportBtn.disabled = view.records.length === 0;
    exportBtn.onclick = () => void exportSiteData(src, view.collection).then((blob) => {
      const a = el("a", { href: URL.createObjectURL(blob), download: `${view.collection}.csv` }) as HTMLAnchorElement;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
    }).catch((e: unknown) => toast(e instanceof Error ? e.message : String(e), "error"));
    const refresh = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, ["Refresh"]);
    refresh.onclick = () => void load(view.collection);
    const rows = view.records.map((r) => {
      const del = el("button", { class: "btn btn-ghost btn-sm", type: "button", "aria-label": `Delete record ${r.id}` }, ["Delete"]);
      del.onclick = () => void inlineConfirm("Delete this record? This cannot be undone.").then((ok) => {
        if (!ok) return;
        void deleteSiteRecord(src, view.collection, r.id).then(() => load(view.collection)).catch((e: unknown) => toast(e instanceof Error ? e.message : String(e), "error"));
      });
      return el("tr", {}, [
        el("td", { class: "sitedata-time" }, [new Date(r.createdAt).toLocaleString()]),
        ...fields.map((f) => el("td", {}, [r.data[f] === undefined ? "" : String(r.data[f])])),
        el("td", {}, [del]),
      ]);
    });
    const access = spec ? `Visitors can ${spec.public.length ? spec.public.join(" and ") : "do nothing with"} this collection.` : "No longer declared in tavsin/backend.json; kept for export.";
    body.replaceChildren(
      ...(view.configError ? [el("p", { class: "publish-note" }, [view.configError])] : []),
      ...(names.length === 0
        ? [el("p", { class: "drawer-note" }, ["No collections yet. Ask Tavsin to add a form (it declares the collection in tavsin/backend.json), or write that file yourself."])]
        : [
          tabs,
          el("p", { class: "drawer-note" }, [access]),
          view.records.length === 0
            ? el("p", { class: "drawer-note" }, ["Nothing submitted yet."])
            : el("div", { class: "sitedata-scroll" }, [el("table", { class: "sitedata-table" }, [
              el("thead", {}, [el("tr", {}, [el("th", {}, ["Received"]), ...fields.map((f) => el("th", {}, [f])), el("th", {}, [])])]),
              el("tbody", {}, rows),
            ])]),
          el("div", { class: "drawer-actions" }, [refresh, exportBtn]),
        ]),
    );
  };
  const load = (collection?: string): Promise<void> => getSiteData(src, collection).then(render).catch((e: unknown) => {
    body.replaceChildren(el("p", { class: "publish-note" }, [e instanceof Error ? e.message : String(e)]));
  });
  showModal(title, body);
  void load(initial);
}

/** Custom domains for one launched site: what is claimed, and a field to claim another. */
function domainsBlock(site: SiteView, info: SitesInfo): HTMLElement | null {
  const cfg = info.domains;
  if (!cfg?.enabled) return null;
  const rows = (site.domains ?? []).map((d) => domainRow(site, d));
  const full = cfg.used >= cfg.limit;
  const input = el("input", { class: "field launch-slug", placeholder: "www.example.com", "aria-label": `Custom domain for ${site.slug}`, autocomplete: "off", spellcheck: "false" }) as HTMLInputElement;
  const add = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, ["Add domain"]) as HTMLButtonElement;
  input.disabled = full;
  add.disabled = full;
  const submit = (): void => {
    const domain = input.value.trim();
    if (!domain) return;
    add.disabled = true;
    void addSiteDomain(site.slug, domain)
      .then((r) => { toast(`Added ${r.domain.domain} — now create the DNS records shown`, "success"); return loadSites(); })
      .catch((e: unknown) => { add.disabled = false; toast(e instanceof Error ? e.message : String(e), "error"); });
  };
  add.onclick = submit;
  input.onkeydown = (e) => { if (e.key === "Enter") submit(); };
  return el("div", { class: "launch-domains" }, [
    el("div", { class: "launch-domains-head" }, [
      el("span", { class: "launch-domains-title" }, ["Custom domain"]),
      el("span", { class: "launch-note" }, [cfg.limit === 0 ? "Not included in this plan" : `${String(cfg.used)} of ${String(cfg.limit)} domains`]),
    ]),
    ...rows,
    ...(cfg.limit === 0 ? [] : [el("div", { class: "launch-row" }, [input, add])]),
  ]);
}

/**
 * LAUNCH — the one-click path from a built page to a public URL Tavsin hosts.
 * A snapshot, not a live view: what is live changes only when the user
 * launches again, every launch is a version, and rollback is one click.
 */
function launchCard(): HTMLElement {
  if (state.sites === null) {
    void loadSites();
    return el("div", { class: "launch-card" }, [el("div", { class: "deploy-desc" }, ["Loading your sites…"])]);
  }
  const info = state.sites;
  const existing = info.sites[0];
  const name = el("input", { class: "field launch-slug", placeholder: "site-name", "aria-label": "Site name", value: existing?.slug ?? deriveTitle(state.projectName || "my-site").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") }) as HTMLInputElement;
  const go = el("button", { class: "vibe-btn", type: "button" }, [state.sitesBusy ? "Launching…" : existing && name.value === existing.slug ? "Update live site" : "Launch"]) as HTMLButtonElement;
  go.disabled = state.sitesBusy;
  go.onclick = () => void launchSite(name.value.trim());
  const live = info.sites.map((site) => {
    const versions = el("select", { class: "field field-sm", "aria-label": `Versions of ${site.slug}` }, site.versions.map((v, i) =>
      el("option", { value: v.id, ...(v.id === site.current ? { selected: "selected" } : {}) }, [`${i === 0 ? "Latest · " : ""}${new Date(v.publishedAt).toLocaleString()} · ${String(v.files)} files`]),
    )) as HTMLSelectElement;
    const roll = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, ["Roll back"]);
    roll.onclick = () => {
      if (versions.value === site.current) return toast("That version is already live", "info");
      void rollbackSite(site.slug, versions.value).then(() => { toast("Rolled back", "success"); return loadSites(); }).catch((e: unknown) => toast(e instanceof Error ? e.message : String(e), "error"));
    };
    const off = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, ["Take offline"]);
    off.onclick = () => {
      void inlineConfirm(`Take ${site.slug} offline? Its URL stops working and its versions are deleted.`).then((ok) => {
        if (!ok) return;
        void unpublishSite(site.slug).then(() => { toast("Taken offline", "info"); return loadSites(); }).catch((e: unknown) => toast(e instanceof Error ? e.message : String(e), "error"));
      });
    };
    const copy = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, ["Copy link"]);
    copy.onclick = () => void navigator.clipboard?.writeText(site.url).then(() => toast("Link copied", "success"));
    const domains = domainsBlock(site, info);
    const dataBtn = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, ["Data"]);
    dataBtn.onclick = () => openSiteData({ slug: site.slug }, `Data · ${site.slug}`);
    return el("div", { class: "launch-site" }, [
      el("span", { class: "launch-dot", "aria-hidden": "true" }),
      el("a", { class: "launch-url", href: site.url, target: "_blank", rel: "noopener" }, [site.url]),
      copy,
      dataBtn,
      versions,
      roll,
      off,
      ...(domains ? [domains] : []),
    ]);
  });
  return el("div", { class: "launch-card" }, [
    el("div", { class: "launch-head" }, [
      el("span", { class: "launch-title" }, ["Launch"]),
      el("span", { class: "launch-note" }, [`${String(info.sites.length)} of ${String(info.limits.maxSites)} sites`]),
    ]),
    el("p", { class: "deploy-desc" }, ["Publishes a snapshot of your built site (dist/, build/, out/ — or the page at the root) to a public link. Later edits are not live until you launch again; every launch is a version you can roll back to."]),
    el("div", { class: "launch-row" }, [name, go]),
    ...live,
    (() => {
      const b = el("button", { class: "btn btn-ghost btn-sm launch-preview-data", type: "button" }, ["Form submissions from the preview"]);
      b.onclick = () => openSiteData({ preview: true }, "Data · preview");
      return b;
    })(),
  ]);
}

function deployPanel(): HTMLElement {
  const head = el("div", { class: "panel-head" }, [el("span", {}, ["Deploy"]), el("span", {})]);

  const TARGETS = [
    { id: "vercel", icon: "▲", name: "Vercel", desc: "A reversible preview. Never production from here.", cmd: "vercel deploy --yes" },
    { id: "netlify", icon: "◆", name: "Netlify", desc: "Preview the build directory. Production is a separate human step.", cmd: "npx netlify deploy --dir=dist" },
    { id: "cloudflare", icon: "☁", name: "Cloudflare", desc: "Upload a preview version or Pages branch.", cmd: "npx wrangler versions upload" },
    { id: "fly", icon: "✈", name: "Fly.io", desc: "Preview app only, and only if one already exists.", cmd: "fly deploy --strategy immediate --app <app>-preview" },
    { id: "docker", icon: "🐳", name: "Docker", desc: "Build the image locally. No registry push from this button.", cmd: "docker build -t app ." },
  ];

  const targetCards = TARGETS.map((t) => {
    const prompt = `Use the deploy-preview skill. Deploy a PREVIEW of this project to ${t.name}. The check command the skill names is: ${t.cmd}. Do not deploy to production, do not pass a production flag, and stop if the target is not linked or authenticated.`;
    const card = el("div", { class: "deploy-card" }, [
      el("div", { class: "deploy-card-head" }, [
        el("span", { class: "deploy-icon" }, [t.icon]),
        el("span", { class: "deploy-name" }, [t.name]),
      ]),
      el("div", { class: "deploy-desc" }, [t.desc]),
      el("div", { class: "deploy-actions" }, [
        (() => {
          const ask = el("button", { class: "btn btn-primary btn-sm" }, ["Preview with agent"]);
          ask.onclick = () => void send(prompt);
          return ask;
        })(),
        (() => {
          const copy = el("button", { class: "btn btn-ghost btn-sm", title: "Copy preview command" }, ["Command"]);
          copy.onclick = () => {
            void navigator.clipboard?.writeText(t.cmd).then(() => toast(`Copied: ${t.cmd}`, "success"));
          };
          return copy;
        })(),
      ]),
    ]);
    return card;
  });

  const github = el("div", { class: "deploy-card" }, [
    el("div", { class: "deploy-card-head" }, [
      el("span", { class: "deploy-icon" }, ["⬡"]),
      el("span", { class: "deploy-name" }, ["GitHub"]),
    ]),
    el("div", { class: "deploy-desc" }, ["Send named files to a tavsin/ branch with the token in Settings. Main is not updated."]),
    el("div", { class: "deploy-actions" }, [
      (() => {
        const go = el("button", { class: "btn btn-primary btn-sm" }, ["Push to GitHub"]);
        go.onclick = () => openPublish();
        return go;
      })(),
    ]),
  ]);

  return el("div", { class: "panel" }, [head, el("div", { class: "deploy-body" }, [
    launchCard(),
    el("p", { class: "deploy-lead" }, ["Or deploy to your own host. These run the vendor's CLI as a preview; production there is the deploy-production skill — human-triggered, not a card here."]),
    el("div", { class: "deploy-grid" }, [...targetCards, github]),
  ])]);
}

/**
 * Team rail — the killer Team-tier feature. Shows the active team's
 * member list, lets the user propose a new memory, lists existing memories
 * (proposed/approved/rejected), and offers Approve/Reject for owners.
 */
function teamPanel(): HTMLElement {
  const owner = state.user?.id ?? "";
  const head = el("div", { class: "panel-head" }, [
    el("span", {}, ["Team"]),
    el("span", {}, []),
  ]);

  // Empty state: not on any team yet
  if (state.teams.length === 0) {
    const note = el("div", { class: "panel-empty" }, [
      state.canCreateTeam
        ? "Create a team to share approved memories with your teammates."
        : "Team-tier subscription required to create or join a team.",
    ]);
    const children: HTMLElement[] = [head, note];
    if (state.canCreateTeam) {
      const create = el("button", { class: "btn btn-primary", style: "margin: 0 12px;" }, ["+ Create team"]);
      create.onclick = async () => {
        const name = await inlinePrompt("Team name");
        if (!name) return;
        try {
          await createTeam(name);
          await refreshTeams();
        } catch (e) {
          toast(e instanceof Error ? e.message : String(e), "error");
        }
      };
      children.push(create);
    }
    return el("div", { class: "panel" }, children);
  }

  const team = state.teams.find((t) => t.id === state.activeTeamId) ?? state.teams[0]!;
  const isOwner = team.ownerUserId === owner;

  // Team switcher (only when there's >1 team)
  const switcher = state.teams.length > 1
    ? (() => {
        const sel = el("select", { class: "mode-select", style: "margin: 6px 12px;" }, state.teams.map((t) => el("option", { value: t.id }, [t.name]))) as HTMLSelectElement;
        sel.value = team.id;
        sel.onchange = () => {
          state.activeTeamId = sel.value;
          void refreshTeams();
        };
        return sel;
      })()
    : null;

  // Members card
  const members = el("div", { class: "panel-section" }, [
    el("div", { class: "panel-subhead" }, [
      el("span", {}, [`Members (${state.teamMembers.length})`]),
      isOwner ? (() => {
        const inv = el("button", { class: "btn btn-ghost btn-sm" }, ["+ Invite"]);
        inv.onclick = async () => {
          const email = await inlinePrompt("Invite email");
          if (!email) return;
          try {
            const r = await inviteToTeam(team.id, email);
            await navigator.clipboard.writeText(r.url).catch(() => undefined);
            toast(`Invite link copied to clipboard`, "success");
          } catch (e) {
            toast(e instanceof Error ? e.message : String(e), "error");
          }
        };
        return inv;
      })() : el("span", {}, []),
    ]),
    el("div", { class: "members" }, state.teamMembers.map((m) => el("div", { class: "member" }, [
      el("span", { class: "member-avatar" }, [(m.displayName ?? m.email ?? "?")[0]!.toUpperCase()]),
      el("span", { class: "member-name" }, [m.displayName || m.email || "—"]),
      el("span", { class: `member-role member-role-${m.role}` }, [m.role]),
    ]))),
  ]);

  // Memory proposer (any member)
  const proposeArea = el("textarea", { class: "field", rows: "2", placeholder: "Propose a team rule, e.g. \"never touch src/billing without two reviewers\"" }) as HTMLTextAreaElement;
  const proposeBtn = el("button", { class: "btn btn-sm" }, ["Propose"]) as HTMLButtonElement;
  proposeBtn.onclick = async () => {
    const v = proposeArea.value.trim();
    if (!v) return;
    proposeBtn.disabled = true;
    try {
      await proposeTeamMemory(team.id, v);
      proposeArea.value = "";
      await refreshTeams();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), "error");
    } finally {
      proposeBtn.disabled = false;
    }
  };
  const proposeRow = el("div", { class: "panel-section" }, [
    el("div", { class: "panel-subhead" }, [el("span", {}, ["Propose a memory"]), el("span", {}, [])]),
    proposeArea,
    el("div", { style: "padding: 6px 12px;" }, [proposeBtn]),
  ]);

  // Memory list
  const memories = el("div", { class: "panel-section" }, [
    el("div", { class: "panel-subhead" }, [el("span", {}, [`Memories (${state.teamMemories.length})`]), el("span", {}, [])]),
    ...(state.teamMemories.length === 0
      ? [el("div", { class: "panel-empty" }, ["No memories yet. Propose one above."])]
      : state.teamMemories.map((m) => {
          const row = el("div", { class: `team-memory team-memory-${m.approvalStatus}` }, [
            el("div", { class: "team-memory-title" }, [m.title]),
            el("div", { class: "team-memory-body" }, [m.content]),
            el("div", { class: "team-memory-meta" }, [`${m.approvalStatus} · ${m.kind}`]),
          ]);
          if (isOwner && m.approvalStatus === "proposed") {
            const approve = el("button", { class: "btn btn-sm" }, ["✓ Approve"]);
            approve.onclick = async () => {
              try { await decideTeamMemory(team.id, m.id, "approve"); await refreshTeams(); }
              catch (e) { toast(e instanceof Error ? e.message : String(e), "error"); }
            };
            const reject = el("button", { class: "btn btn-ghost btn-sm" }, ["✗ Reject"]);
            reject.onclick = async () => {
              try { await decideTeamMemory(team.id, m.id, "reject"); await refreshTeams(); }
              catch (e) { toast(e instanceof Error ? e.message : String(e), "error"); }
            };
            row.append(el("div", { class: "team-memory-actions" }, [approve, reject]));
          }
          return row;
        })),
  ]);

  const children: HTMLElement[] = [head];
  if (switcher) children.push(switcher);
  children.push(members, proposeRow, memories);
  return el("div", { class: "panel" }, children);
}

function agentRowButton(row: AgentControlRowView): HTMLElement {
  const meta: string[] = [];
  if (row.lastActivity) meta.push(safeDisplayText(row.lastActivity));
  const ml = modelLabel(row);
  if (ml) meta.push(ml);
  meta.push(costLabel(row.costUsd));
  if (row.verificationStatus) meta.push(row.verificationStatus);
  const btn = el("button", { class: `job agent-row ${state.job === row.id ? "job-active" : ""}` }, [
    el("span", { class: `status status-${row.status}` }, [row.status]),
    el("span", { class: "job-goal" }, [safeDisplayText(row.name ?? row.goal)]),
    meta.length ? el("span", { class: "agent-row-meta" }, [meta.join(" · ")]) : null,
  ]);
  btn.onclick = () => selectAgentJob(row.id);
  return btn;
}

function agentGroup(title: string, rows: AgentControlRowView[]): HTMLElement | null {
  if (rows.length === 0) return null;
  return el("div", { class: "agent-group" }, [
    el("div", { class: "agent-group-title" }, [title]),
    el("div", { class: "jobs" }, rows.map((r) => agentRowButton(r))),
  ]);
}

function runsPanel(): HTMLElement {
  const children: HTMLElement[] = [
    el("div", { class: "panel-head" }, [el("span", {}, ["Autonomous runs"]), el("span", {})]),
  ];
  if (playgroundUnavailable("jobsBackground")) {
    children.push(
      el("div", { class: "panel-empty" }, [
        "Fire-and-forget background runs are not available on the hosted playground. ",
        "Use the composer for a full agent turn in this session, or the Tavsin desktop app for parallel background jobs.",
      ]),
    );
  } else {
    const goalInput = el("input", { class: "field", placeholder: "Autonomous goal — e.g. add a /health route + test" }) as HTMLInputElement;
    const sandbox = el("input", { type: "checkbox", checked: "" }) as HTMLInputElement;
    sandbox.checked = true;
    const go = el("button", { class: "btn btn-primary btn-sm" }, ["Run"]);
    go.onclick = () => {
      void launchRun(goalInput.value, sandbox.checked);
      goalInput.value = "";
    };
    children.push(el("div", { class: "run-launch" }, [goalInput, el("label", { class: "toggle" }, [sandbox, "Sandbox"]), go]));
  }

  if (state.agentsError) {
    children.push(el("div", { class: "panel-error" }, [state.agentsError]));
  } else if (state.agentsLoading && state.agentRows.length === 0) {
    children.push(el("div", { class: "panel-empty" }, ["Loading agents…"]));
  } else if (state.agentRows.length === 0) {
    children.push(
      el("div", { class: "panel-empty" }, [
        "No runs yet. Launch one above — it plans, edits & tests in a sandbox you can review.",
      ]),
    );
  } else {
    const groups = groupAgentRows(state.agentRows);
    const list = el("div", { class: "agent-list" }, [
      agentGroup("Running", groups.running),
      agentGroup("Queued", groups.queued),
      agentGroup("Recent", groups.recent),
    ].filter((n): n is HTMLElement => n !== null));
    children.push(list);
  }

  const selectedRow = state.job ? state.agentRows.find((r) => r.id === state.job) ?? null : null;
  const headerRow = selectedRow ?? (state.agentDetail ? rowFromDetail(state.agentDetail) : null);
  if (state.job && headerRow) {
    const sandbox = state.agentDetail?.sandbox ?? headerRow.sandbox;
    const gates = webAgentActionGates({
      row: selectedRow,
      sandbox,
    });
    const verification = state.agentDetail?.verification ?? null;

    const detailHead = el("div", { class: "agent-detail-head" }, [
      el("div", { class: "agent-detail-goal" }, [safeDisplayText(headerRow.name ?? headerRow.goal)]),
      el("div", { class: "agent-detail-meta" }, [
        el("span", { class: `status status-${headerRow.status}` }, [headerRow.status]),
        headerRow.lastActivity ? el("span", {}, [safeDisplayText(headerRow.lastActivity)]) : null,
        modelLabel(headerRow) ? el("span", { class: "agent-mono" }, [modelLabel(headerRow)!]) : null,
        el("span", {}, [costLabel(headerRow.costUsd)]),
        headerRow.verificationStatus ? el("span", { class: `verify-${headerRow.verificationStatus}` }, [headerRow.verificationStatus]) : null,
      ]),
    ]);

    const evidence =
      verification
        ? el("div", { class: "agent-evidence" }, [
            el("div", { class: `verify-${verification.status}` }, [
              verification.status,
              verification.editsAfterCheck > 0
                ? ` · ${String(verification.editsAfterCheck)} edit${verification.editsAfterCheck === 1 ? "" : "s"} after last check`
                : "",
            ]),
            verification.checks.length
              ? el("ul", { class: "agent-checks" }, verification.checks.map((c) =>
                  el("li", {}, [`${c.exitCode === 0 ? "✓" : "✗"} ${safeDisplayText(c.command)}`]),
                ))
              : null,
          ])
        : null;

    const timeline = state.agentTimeline.length
      ? el(
          "div",
          { class: "agent-timeline" },
          state.agentTimeline.slice(-40).map((item) =>
            el("div", { class: "console-line" }, [
              el("span", { class: "console-caret" }, ["›"]),
              el("span", {}, [safeDisplayText(item.summary || payloadPreview(item.payload))]),
            ]),
          ),
        )
      : state.agentDetailError
        ? el("div", { class: "panel-error" }, [state.agentDetailError])
        : el("div", { class: "panel-empty panel-empty-sm" }, ["No timeline yet."]);

    const steerInput = el("input", {
      class: "field agent-steer",
      placeholder: "Steer this agent…",
      value: state.agentSteerDraft,
    }) as HTMLInputElement;
    steerInput.oninput = () => { state.agentSteerDraft = steerInput.value; };
    const steerBtn = el("button", { class: "btn btn-sm" }, ["Steer"]);
    steerBtn.disabled = !gates.canSteer || state.agentSteerDraft.trim().length === 0 || state.agentBusyAction !== null;
    steerBtn.onclick = () => {
      const text = state.agentSteerDraft.trim();
      if (!text || !state.job) return;
      void runAgentAction("steer", async () => {
        const res = await invoke<{ accepted: boolean }>("jobs.steer", { id: state.job!, message: text });
        // Clear the draft only once it was delivered — a refused steer keeps
        // the text so it can be sent somewhere else instead of being lost.
        if (res.accepted) state.agentSteerDraft = "";
        state.agentActionNote = res.accepted ? "Steer sent." : "That agent is no longer running — your message is still in the box.";
        await refreshAgents();
      });
    };
    const cancelBtn = el("button", { class: "btn btn-ghost btn-sm" }, ["Stop"]);
    cancelBtn.disabled = !gates.canCancel || state.agentBusyAction !== null;
    cancelBtn.onclick = () => {
      if (!state.job) return;
      void runAgentAction("stop", async () => {
        await invoke("jobs.cancel", { id: state.job! });
        state.agentActionNote = "Job cancelled.";
        await refreshAgents();
      });
    };
    const diffBtn = el("button", { class: "btn btn-ghost btn-sm" }, ["View diff"]);
    diffBtn.disabled = !gates.canSandbox || state.agentBusyAction !== null;
    diffBtn.onclick = () => {
      if (!state.job) return;
      void runAgentAction("diff", async () => {
        const d = await invoke<{ diff: string }>("sandbox.diff", { jobId: state.job! });
        state.agentSandboxDiff = d.diff?.trim() || "(empty diff)";
      });
    };
    const applyBtn = el("button", { class: "btn btn-primary btn-sm" }, ["Apply"]);
    applyBtn.disabled = !gates.canSandbox || state.agentBusyAction !== null;
    applyBtn.onclick = () => {
      if (!state.job) return;
      void (async () => {
        if (!(await inlineConfirm("Apply this sandbox onto your working tree?"))) return;
        void runAgentAction("apply", async () => {
          const r = await invoke<{ applied: boolean }>("sandbox.apply", { jobId: state.job! });
          state.agentActionNote = r.applied ? "Applied to your working tree." : "Nothing to apply.";
          state.agentSandboxDiff = null;
          await refreshAgents();
          await refreshChanges();
        });
      })();
    };
    const discardBtn = el("button", { class: "btn btn-ghost btn-sm" }, ["Discard sandbox"]);
    discardBtn.disabled = !gates.canSandbox || state.agentBusyAction !== null;
    discardBtn.onclick = () => {
      if (!state.job) return;
      void (async () => {
        if (!(await inlineConfirm("Discard this sandbox? Isolated work is removed."))) return;
        void runAgentAction("discard", async () => {
          await invoke("sandbox.discard", { jobId: state.job! });
          state.agentActionNote = "Sandbox discarded.";
          state.agentSandboxDiff = null;
          await refreshAgents();
        });
      })();
    };

    const actions: HTMLElement[] = [];
    if (gates.canSteer) actions.push(steerInput, steerBtn);
    if (gates.canCancel) actions.push(cancelBtn);
    if (gates.canSandbox) actions.push(diffBtn, applyBtn, discardBtn);

    children.push(
      el("div", { class: "agent-detail" }, [
        detailHead,
        headerRow.summary || headerRow.error
          ? el("div", { class: "agent-summary" }, [safeDisplayText(headerRow.error ?? headerRow.summary ?? "")])
          : null,
        evidence,
        actions.length ? el("div", { class: "agent-actions" }, actions) : null,
        state.agentActionNote ? el("div", { class: "panel-note" }, [state.agentActionNote]) : null,
        state.agentActionError ? el("div", { class: "panel-error" }, [state.agentActionError]) : null,
        state.agentSandboxDiff
          ? el("pre", { class: "console agent-diff" }, [state.agentSandboxDiff])
          : null,
        el("div", { class: "agent-timeline-label" }, ["Timeline"]),
        timeline,
      ]),
    );
  }

  return el("div", { class: "panel panel-runs" }, children);
}

// ── shell ──
function render(): void {
  if (!state.ready) {
    root.replaceChildren(el("div", { class: "boot" }, ["Loading Tavsin…"]));
    return;
  }
  const bodyClass = ["body", state.sidebarCollapsed ? "body-collapsed" : "", state.tab === "preview" ? "body-stage" : ""].filter(Boolean).join(" ");
  root.replaceChildren(topbar(), el("div", { class: bodyClass }, [iconRail(), sidebar(), center(), rail()]));
}
function updateCenter(): void {
  const c = document.getElementById("center");
  if (c) c.replaceWith(center());
}

// ── settings drawer ──
function openSettings(preferred?: string): void {
  // The one field most people need: paste a key and the provider is read off
  // it (sk-ant-, sk-or-, AIza, sk-, a .env line). The server saves it, makes it
  // active and checks it with a one-token call; a rejected key is not kept.
  const paste = el("input", { class: "field", type: "password", placeholder: "Paste an API key", autocomplete: "off", spellcheck: "false", "aria-label": "API key" }) as HTMLInputElement;
  const pasteHint = el("p", { class: "drawer-note" }, ["Anthropic, OpenAI, Gemini or OpenRouter keys, or a line from a .env file."]);
  const connect = el("button", { class: "btn btn-primary", type: "button" }, ["Connect"]) as HTMLButtonElement;
  const describe = (): void => {
    const d = paste.value.trim() === "" ? null : detectApiKey(paste.value);
    connect.disabled = !d?.ok;
    pasteHint.textContent = !d ? "Anthropic, OpenAI, Gemini or OpenRouter keys, or a line from a .env file." : d.ok ? `Looks like ${/^[AEIOU]/.test(d.label) ? "an" : "a"} ${d.label} key.` : d.reason;
  };
  paste.oninput = describe;
  describe();
  const runConnect = async (): Promise<void> => {
    if (connect.disabled) return;
    connect.disabled = true;
    connect.textContent = "Checking…";
    try {
      const r = await invoke<{ ok: boolean; label?: string; verified?: boolean; model?: string; warning?: string; error?: string }>("providers.connect", { key: paste.value });
      if (!r.ok) {
        pasteHint.textContent = r.error ?? "That did not work.";
        connect.disabled = false;
        connect.textContent = "Connect";
        return;
      }
      await loadStatus();
      closeModal();
      render();
      toast(r.verified ? `Connected to ${r.label ?? "your provider"}` : `Saved for ${r.label ?? "your provider"} — the check did not finish: ${(r.warning ?? "").slice(0, 120)}`, r.verified ? "success" : "info");
    } catch (e) {
      pasteHint.textContent = e instanceof Error ? e.message : String(e);
      connect.disabled = false;
      connect.textContent = "Connect";
    }
  };
  connect.onclick = () => void runConnect();
  paste.onkeydown = (e) => { if (e.key === "Enter") void runConnect(); };

  const provider = el("select", { class: "field" }, PROVIDERS.map((p) => el("option", { value: p }, [p]))) as HTMLSelectElement;
  provider.value = preferred || (state.resolvedProvider !== "—" ? state.resolvedProvider : "openrouter");
  const key = el("input", { class: "field", type: "password", placeholder: "Paste your API key", autocomplete: "off" }) as HTMLInputElement;
  const model = el("input", { class: "field", placeholder: "Default model (optional — leave blank for the free model)" }) as HTMLInputElement;
  const baseUrl = el("input", { class: "field", placeholder: "Base URL (local / OpenAI-compatible, optional)" }) as HTMLInputElement;
  const note = el("p", { class: "drawer-note" }, ["🔒 Your key is stored locally in this project's database — it is never shown in chat, never sent to the model, and never leaves your server."]);
  // Provider-specific helper — the free models run on OpenRouter, so point there.
  const help = el("p", { class: "drawer-note" }, []);
  const renderHelp = (): void => {
    help.replaceChildren();
    if (provider.value === "openrouter") {
      const a = el("a", { href: "https://openrouter.ai/keys", target: "_blank", rel: "noopener", style: "color:var(--sky);text-decoration:underline" }, ["openrouter.ai/keys"]);
      help.append("Tavsin's free models run on OpenRouter. Create a free key at ", a, ", paste it above, and you're set. (Adding ~$10 of OpenRouter credit makes the free models much more reliable.)");
    }
  };
  provider.onchange = renderHelp;
  renderHelp();
  const save = el("button", { class: "btn btn-primary" }, ["Save key"]) as HTMLButtonElement;
  save.onclick = async () => {
    save.disabled = true;
    save.textContent = "Saving…";
    try {
      await invoke("providers.configure", { provider: provider.value, ...(key.value ? { apiKey: key.value } : {}), ...(model.value ? { defaultModel: model.value } : {}), ...(baseUrl.value ? { baseUrl: baseUrl.value } : {}) });
      await invoke("providers.activate", { provider: provider.value });
      await loadStatus();
      closeModal();
      render();
      toast(`${provider.value} key saved`, "success");
    } catch (e) {
      note.textContent = e instanceof Error ? e.message : String(e);
      save.disabled = false;
      save.textContent = "Save key";
    }
  };
  const more = el("details", { class: "drawer-more" }, [
    el("summary", {}, ["Choose a provider, model or base URL"]),
    el("label", { class: "field-label" }, ["Provider"]), provider,
    el("label", { class: "field-label" }, ["API key"]), key, help,
    el("label", { class: "field-label" }, ["Default model"]), model,
    el("label", { class: "field-label" }, ["Base URL"]), baseUrl,
    el("div", { class: "drawer-actions" }, [save]),
  ]);
  if (preferred) (more as HTMLDetailsElement).open = true;
  showModal("Connect a model", el("div", { class: "drawer" }, [
    el("label", { class: "field-label" }, ["API key"]),
    el("div", { class: "paste-row" }, [paste, connect]),
    pasteHint,
    more,
    note,
  ]));
  setTimeout(() => (preferred ? key : paste).focus(), 0);
}

/** Mode-aware unauthorized handler — chooses login page vs token paste. */
function promptToken(): void {
  // "Create account" links (tavsin.xyz/workspace, the pricing page) arrive
  // with ?signup=1 and must open the sign-up form, not sign-in.
  if (state.authMode === "multiuser") showAuthScreen(new URLSearchParams(window.location.search).has("signup") ? "signup" : "login");
  else if (state.authMode === "password") showLogin();
  else showTokenPrompt();
}

/**
 * Multi-user signup/login screen. One modal, two tabs (Sign in / Create
 * account) so the user never has to bounce between pages.
 */
declare const __APP_VERSION__: string;
const APP_VERSION = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";

function currentTheme(): WebTheme {
  try { return readStoredTheme((k) => localStorage.getItem(k)); } catch { return "light"; }
}
function applyTheme(theme: WebTheme): void {
  document.documentElement.classList.toggle("light", theme === "light");
  try { localStorage.setItem(THEME_KEY, theme); } catch { /* ignore */ }
  currentWebEditor()?.setAppearance(theme);
}
function currentLang(): string {
  try { return localStorage.getItem("tavsin.web.lang") || "en"; } catch { return "en"; }
}

/** App Settings — appearance (light/dark), language, help/FAQ, support, version. */
/** Settings section: connect GitHub with YOUR own token (per-user; never the operator's). */
function buildGithubSection(): HTMLElement {
  const status = el("p", { class: "drawer-note", style: "margin-top:0" }, ["Loading…"]);
  const token = el("input", { class: "field", type: "password", placeholder: "ghp_… (a fine-grained or classic token)" }) as HTMLInputElement;
  const owner = el("input", { class: "field", placeholder: "owner (e.g. your-org)" }) as HTMLInputElement;
  const repo = el("input", { class: "field", placeholder: "repo (e.g. your-app)" }) as HTMLInputElement;
  const result = el("span", { class: "drawer-note", style: "margin:0 0 0 10px" }, []);

  const refresh = async (): Promise<void> => {
    const s = await withAuth(() => invoke<{ github: { configured: boolean; owner: string | null; repo: string | null; hasToken: boolean } }>("connectors.status"), null);
    if (!s) { status.textContent = "Sign in to configure GitHub."; return; }
    const g = s.github;
    status.textContent = g.configured ? `Connected: ${g.owner}/${g.repo}${g.hasToken ? " · token set" : ""}` : "Not connected. Add a token + repo below to attach issues and PRs.";
    if (g.owner) owner.value = g.owner;
    if (g.repo) repo.value = g.repo;
    token.placeholder = g.hasToken ? "•••• (leave blank to keep)" : "ghp_… (a fine-grained or classic token)";
  };
  void refresh();

  const save = el("button", { class: "btn btn-primary btn-sm" }, ["Save"]);
  save.onclick = async () => {
    const payload: Record<string, unknown> = {};
    if (token.value.trim()) payload["token"] = token.value.trim();
    if (owner.value.trim()) payload["owner"] = owner.value.trim();
    if (repo.value.trim()) payload["repo"] = repo.value.trim();
    const r = await withAuth(() => invoke<{ ok: boolean; configured: boolean }>("connectors.github.configure", payload), null);
    token.value = "";
    result.textContent = r?.configured ? "Saved ✓" : "Saved";
    await refresh();
  };

  const fieldRow = (label: string, input: HTMLElement): HTMLElement =>
    el("label", { class: "set-embed-field" }, [el("span", {}, [label]), input]);

  return el("div", { class: "set-section" }, [
    el("div", { class: "set-label" }, ["GitHub"]),
    status,
    el("p", { class: "drawer-note" }, ["Uses your token, stored in your account. Tavsin never uses a shared login. To push from Changes, the token needs contents and pull requests on the repo you name."]),
    fieldRow("Token", token),
    fieldRow("Owner", owner),
    fieldRow("Repo", repo),
    el("div", {}, [save, result]),
  ]);
}

/** Settings section: connect GitLab with YOUR own token (per-user). */
function buildAtlassianSection(): HTMLElement {
  const status = el("p", { class: "drawer-note", style: "margin-top:0" }, ["Loading…"]);
  const siteUrl = el("input", { class: "field", placeholder: "https://your-site.atlassian.net" }) as HTMLInputElement;
  const email = el("input", { class: "field", placeholder: "you@company.com (Atlassian account email)" }) as HTMLInputElement;
  const apiToken = el("input", { class: "field", type: "password", placeholder: "API token (id.atlassian.com → Security → API tokens)" }) as HTMLInputElement;
  const jiraKey = el("input", { class: "field", placeholder: "Default Jira project key, e.g. ENG (optional)" }) as HTMLInputElement;
  const spaceKey = el("input", { class: "field", placeholder: "Default Confluence space key, e.g. DOCS (optional)" }) as HTMLInputElement;
  const result = el("span", { class: "drawer-note", style: "margin:0 0 0 10px" }, []);

  const refresh = async (): Promise<void> => {
    const s = await withAuth(() => invoke<{ atlassian?: { configured: boolean; siteUrl: string | null; jiraProjectKey: string | null; confluenceSpaceKey: string | null; hasToken: boolean } }>("connectors.status"), null);
    const a = s?.atlassian;
    status.textContent = a?.configured
      ? `Connected: ${a.siteUrl}${a.jiraProjectKey ? ` · Jira ${a.jiraProjectKey}` : ""}${a.confluenceSpaceKey ? ` · Confluence ${a.confluenceSpaceKey}` : ""}`
      : "Not connected. One credential set unlocks BOTH Jira and Confluence for the agent.";
    if (a?.siteUrl) siteUrl.value = a.siteUrl;
    if (a?.jiraProjectKey) jiraKey.value = a.jiraProjectKey;
    if (a?.confluenceSpaceKey) spaceKey.value = a.confluenceSpaceKey;
    apiToken.placeholder = a?.hasToken ? "•••• (leave blank to keep)" : "API token (id.atlassian.com → Security → API tokens)";
  };
  void refresh();

  const save = el("button", { class: "btn btn-primary btn-sm" }, ["Save"]);
  save.onclick = async () => {
    const payload: Record<string, unknown> = {};
    if (siteUrl.value.trim()) payload["siteUrl"] = siteUrl.value.trim();
    if (email.value.trim()) payload["email"] = email.value.trim();
    if (apiToken.value.trim()) payload["apiToken"] = apiToken.value.trim();
    if (jiraKey.value.trim()) payload["jiraProjectKey"] = jiraKey.value.trim();
    if (spaceKey.value.trim()) payload["confluenceSpaceKey"] = spaceKey.value.trim();
    const r = await withAuth(() => invoke<{ ok: boolean; configured: boolean }>("connectors.atlassian.configure", payload), null);
    apiToken.value = "";
    result.textContent = r?.configured ? "Saved ✓" : "Saved";
    await refresh();
  };

  const fieldRow = (label: string, input: HTMLElement): HTMLElement =>
    el("label", { class: "set-embed-field" }, [el("span", {}, [label]), input]);

  return el("div", { class: "set-section" }, [
    el("div", { class: "set-label" }, ["Atlassian — Jira + Confluence"]),
    status,
    el("p", { class: "drawer-note" }, ["Uses YOUR API token, stored in your account. The agent can then read/create Jira issues and search/write Confluence pages."]),
    fieldRow("Site URL", siteUrl),
    fieldRow("Email", email),
    fieldRow("API token", apiToken),
    fieldRow("Jira project", jiraKey),
    fieldRow("Space key", spaceKey),
    el("div", {}, [save, result]),
  ]);
}

function buildSlackSection(): HTMLElement {
  const status = el("p", { class: "drawer-note", style: "margin-top:0" }, ["Loading…"]);
  const token = el("input", { class: "field", type: "password", placeholder: "xoxb-… (bot token)" }) as HTMLInputElement;
  const channel = el("input", { class: "field", placeholder: "Default channel, e.g. #eng (optional)" }) as HTMLInputElement;
  const result = el("span", { class: "drawer-note", style: "margin:0 0 0 10px" }, []);

  const refresh = async (): Promise<void> => {
    const s = await withAuth(() => invoke<{ slack?: { configured: boolean; defaultChannel: string | null; hasToken: boolean } }>("connectors.status"), null);
    const sk = s?.slack;
    status.textContent = sk?.configured ? `Connected${sk.defaultChannel ? ` · default ${sk.defaultChannel}` : ""}` : "Not connected. The agent can read channels and post updates with your bot token.";
    if (sk?.defaultChannel) channel.value = sk.defaultChannel;
    token.placeholder = sk?.hasToken ? "•••• (leave blank to keep)" : "xoxb-… (bot token)";
  };
  void refresh();

  const save = el("button", { class: "btn btn-primary btn-sm" }, ["Save"]);
  save.onclick = async () => {
    const payload: Record<string, unknown> = {};
    if (token.value.trim()) payload["token"] = token.value.trim();
    if (channel.value.trim()) payload["defaultChannel"] = channel.value.trim();
    const r = await withAuth(() => invoke<{ ok: boolean; configured: boolean }>("connectors.slack.configure", payload), null);
    token.value = "";
    result.textContent = r?.configured ? "Saved ✓" : "Saved";
    await refresh();
  };

  const fieldRow = (label: string, input: HTMLElement): HTMLElement =>
    el("label", { class: "set-embed-field" }, [el("span", {}, [label]), input]);
  return el("div", { class: "set-section" }, [
    el("div", { class: "set-label" }, ["Slack"]),
    status,
    fieldRow("Bot token", token),
    fieldRow("Channel", channel),
    el("div", {}, [save, result]),
  ]);
}

function buildLinearSection(): HTMLElement {
  const status = el("p", { class: "drawer-note", style: "margin-top:0" }, ["Loading…"]);
  const apiKey = el("input", { class: "field", type: "password", placeholder: "lin_api_… (Settings → Security & access)" }) as HTMLInputElement;
  const teamKey = el("input", { class: "field", placeholder: "Default team key, e.g. ENG (optional)" }) as HTMLInputElement;
  const result = el("span", { class: "drawer-note", style: "margin:0 0 0 10px" }, []);

  const refresh = async (): Promise<void> => {
    const s = await withAuth(() => invoke<{ linear?: { configured: boolean; teamKey: string | null; hasKey: boolean } }>("connectors.status"), null);
    const ln = s?.linear;
    status.textContent = ln?.configured ? `Connected${ln.teamKey ? ` · team ${ln.teamKey}` : ""}` : "Not connected. The agent can read, create, and comment on issues with your API key.";
    if (ln?.teamKey) teamKey.value = ln.teamKey;
    apiKey.placeholder = ln?.hasKey ? "•••• (leave blank to keep)" : "lin_api_… (Settings → Security & access)";
  };
  void refresh();

  const save = el("button", { class: "btn btn-primary btn-sm" }, ["Save"]);
  save.onclick = async () => {
    const payload: Record<string, unknown> = {};
    if (apiKey.value.trim()) payload["apiKey"] = apiKey.value.trim();
    if (teamKey.value.trim()) payload["teamKey"] = teamKey.value.trim();
    const r = await withAuth(() => invoke<{ ok: boolean; configured: boolean }>("connectors.linear.configure", payload), null);
    apiKey.value = "";
    result.textContent = r?.configured ? "Saved ✓" : "Saved";
    await refresh();
  };

  const fieldRow = (label: string, input: HTMLElement): HTMLElement =>
    el("label", { class: "set-embed-field" }, [el("span", {}, [label]), input]);
  return el("div", { class: "set-section" }, [
    el("div", { class: "set-label" }, ["Linear"]),
    status,
    fieldRow("API key", apiKey),
    fieldRow("Team key", teamKey),
    el("div", {}, [save, result]),
  ]);
}

function buildGitlabSection(): HTMLElement {
  const status = el("p", { class: "drawer-note", style: "margin-top:0" }, ["Loading…"]);
  const token = el("input", { class: "field", type: "password", placeholder: "glpat_… (a personal access token)" }) as HTMLInputElement;
  const project = el("input", { class: "field", placeholder: "group/project (or numeric id)" }) as HTMLInputElement;
  const baseUrl = el("input", { class: "field", placeholder: "https://gitlab.example.com/api/v4 (self-hosted, optional)" }) as HTMLInputElement;
  const result = el("span", { class: "drawer-note", style: "margin:0 0 0 10px" }, []);

  const refresh = async (): Promise<void> => {
    const s = await withAuth(() => invoke<{ gitlab?: { configured: boolean; project: string | null; baseUrl: string | null; hasToken: boolean } }>("connectors.status"), null);
    const g = s?.gitlab;
    status.textContent = g?.configured ? `Connected: ${g.project}${g.hasToken ? " · token set" : ""}` : "Not connected. Add a token + project below to attach issues and MRs.";
    if (g?.project) project.value = g.project;
    if (g?.baseUrl) baseUrl.value = g.baseUrl;
    token.placeholder = g?.hasToken ? "•••• (leave blank to keep)" : "glpat_… (a personal access token)";
  };
  void refresh();

  const save = el("button", { class: "btn btn-primary btn-sm" }, ["Save"]);
  save.onclick = async () => {
    const payload: Record<string, unknown> = {};
    if (token.value.trim()) payload["token"] = token.value.trim();
    if (project.value.trim()) payload["project"] = project.value.trim();
    if (baseUrl.value.trim()) payload["baseUrl"] = baseUrl.value.trim();
    const r = await withAuth(() => invoke<{ ok: boolean; configured: boolean }>("connectors.gitlab.configure", payload), null);
    token.value = "";
    result.textContent = r?.configured ? "Saved ✓" : "Saved";
    await refresh();
  };

  const fieldRow = (label: string, input: HTMLElement): HTMLElement =>
    el("label", { class: "set-embed-field" }, [el("span", {}, [label]), input]);

  return el("div", { class: "set-section" }, [
    el("div", { class: "set-label" }, ["GitLab"]),
    status,
    el("p", { class: "drawer-note" }, ["Uses YOUR token, stored in your account. A read token lets you attach issues & merge requests to a chat."]),
    fieldRow("Token", token),
    fieldRow("Project", project),
    fieldRow("Base URL", baseUrl),
    el("div", {}, [save, result]),
  ]);
}

interface EmbedCfg { enabled: boolean; provider: string; model: string; baseUrl?: string; hasApiKey: boolean }

/** Settings section: enable + configure the neural embedder (OpenAI / Voyage / local). */
function buildEmbeddingsSection(): HTMLElement {
  const enable = el("input", { type: "checkbox" }) as HTMLInputElement;
  const provider = el("select", { class: "field" }, [
    el("option", { value: "openai" }, ["OpenAI"]),
    el("option", { value: "local" }, ["Custom / local (OpenAI-compatible)"]),
  ]) as HTMLSelectElement;
  const model = el("input", { class: "field", placeholder: "text-embedding-3-small" }) as HTMLInputElement;
  const baseUrl = el("input", { class: "field", placeholder: "https://api.voyageai.com/v1" }) as HTMLInputElement;
  const apiKey = el("input", { class: "field", type: "password", placeholder: "sk-… / voyage key" }) as HTMLInputElement;
  const testBtn = el("button", { class: "btn btn-sm" }, ["Test connection"]);
  const testResult = el("span", { class: "drawer-note", style: "margin:0 0 0 10px" }, []);

  const fieldRow = (label: string, input: HTMLElement): HTMLElement =>
    el("label", { class: "set-embed-field" }, [el("span", {}, [label]), input]);
  const baseUrlRow = fieldRow("Base URL", baseUrl);
  const advanced = el("div", { class: "set-embed-advanced" }, [
    fieldRow("Provider", provider),
    fieldRow("Model", model),
    baseUrlRow,
    fieldRow("API key", apiKey),
    el("div", {}, [testBtn, testResult]),
  ]);

  const apply = (c: EmbedCfg): void => {
    enable.checked = c.enabled;
    provider.value = c.provider;
    model.value = c.model;
    baseUrl.value = c.baseUrl ?? "";
    apiKey.placeholder = c.hasApiKey ? "•••• (leave blank to keep)" : "sk-… / voyage key";
    advanced.style.display = c.enabled ? "" : "none";
    baseUrlRow.style.display = c.provider === "local" ? "" : "none";
  };
  const save = async (patch: Record<string, unknown>): Promise<void> => {
    const c = await withAuth(() => invoke<EmbedCfg>("memory.embeddings.setConfig", patch), null);
    if (c) apply(c);
  };
  enable.onchange = () => void save({ enabled: enable.checked });
  provider.onchange = () => void save({ provider: provider.value });
  model.onblur = () => { if (model.value.trim()) void save({ model: model.value.trim() }); };
  baseUrl.onblur = () => void save({ baseUrl: baseUrl.value.trim() });
  apiKey.onblur = () => { if (apiKey.value) { void save({ apiKey: apiKey.value }); apiKey.value = ""; } };
  testBtn.onclick = async () => {
    testResult.textContent = "Testing…";
    const r = await withAuth(() => invoke<{ ok: boolean; dims?: number; error?: string }>("memory.embeddings.test"), null);
    testResult.textContent = r ? (r.ok ? `OK — ${r.dims}-dim vectors` : `Failed: ${r.error ?? "?"}`) : "Failed";
  };

  advanced.style.display = "none";
  void withAuth(() => invoke<EmbedCfg>("memory.embeddings.config"), null).then((c) => { if (c) apply(c); });

  return el("div", { class: "set-section" }, [
    el("div", { class: "set-label" }, ["Neural embeddings"]),
    el("p", { class: "drawer-note" }, ["Off by default (offline embedder). Turn on to embed memory with a real model — OpenAI, or any OpenAI-compatible host (Voyage, Together, local) — for stronger semantic recall."]),
    el("label", { class: "set-embed-toggle" }, [enable, el("span", {}, ["Enabled"])]),
    advanced,
  ]);
}

interface ReleaseNote { version: string; date: string; title: string; highlights: string[] }

/** Settings section: the app's release notes ("What's new"), newest expanded. */
function buildReleaseNotesSection(): HTMLElement {
  const list = el("div", { class: "set-releases" }, [el("p", { class: "drawer-note" }, ["Loading…"])]);
  void withAuth(() => invoke<{ version: string; releases: ReleaseNote[] }>("app.releases"), null).then((data) => {
    list.replaceChildren();
    if (!data || data.releases.length === 0) {
      list.append(el("p", { class: "drawer-note" }, ["Release notes unavailable."]));
      return;
    }
    data.releases.forEach((r, idx) => {
      const notes = el("ul", { class: "set-release-notes" }, r.highlights.map((h) => el("li", {}, [h])));
      const details = el("details", { class: "faq" }, [
        el("summary", {}, [`v${r.version} — ${r.title}`, el("span", { class: "set-muted", style: "margin-left:8px" }, [r.date])]),
        notes,
      ]) as HTMLDetailsElement;
      if (idx === 0) details.open = true;
      list.append(details);
    });
  });
  return el("div", { class: "set-section" }, [
    el("div", { class: "set-label" }, ["What's new"]),
    list,
  ]);
}

interface WebProfileSections {
  overview: string;
  architecture: string;
  conventions: string;
  preferences: string;
  source?: "human" | "ai" | "mixed";
}

const PROFILE_FIELDS: Array<{ key: keyof WebProfileSections; label: string; hint: string; rows: number }> = [
  { key: "overview", label: "Overview", hint: "What the project is and does, plus the tech stack.", rows: 4 },
  { key: "architecture", label: "Architecture", hint: "Subsystems, data flow, key boundaries.", rows: 4 },
  { key: "conventions", label: "Conventions", hint: "Coding standards, patterns, tooling, review expectations.", rows: 4 },
  { key: "preferences", label: "Your preferences", hint: "How you like to work — tone, autonomy, always/never.", rows: 3 },
];

/**
 * The persistent per-project "teammate briefing". Set once and injected into
 * every chat session's system prompt, so the agent knows the codebase instead
 * of relearning it each time. Can AI-autofill from the repo, then be edited.
 */
async function openProjectProfile(): Promise<void> {
  let existing: WebProfileSections | null = null;
  try {
    existing = await withAuth(() => invoke<WebProfileSections | null>("profile.get"), null);
  } catch {
    existing = null;
  }
  const fields: WebProfileSections = {
    overview: existing?.overview ?? "",
    architecture: existing?.architecture ?? "",
    conventions: existing?.conventions ?? "",
    preferences: existing?.preferences ?? "",
    source: existing?.source ?? "human",
  };

  const inputs = new Map<keyof WebProfileSections, HTMLTextAreaElement>();
  const sections = PROFILE_FIELDS.map(({ key, label, hint, rows }) => {
    const ta = el("textarea", { class: "field", rows: String(rows) }) as HTMLTextAreaElement;
    ta.value = String(fields[key] ?? "");
    ta.oninput = () => { (fields[key] as string) = ta.value; };
    inputs.set(key, ta);
    return el("div", { class: "set-section" }, [
      el("div", { class: "set-label" }, [label]),
      el("p", { class: "drawer-note", style: "margin-top:0" }, [hint]),
      ta,
    ]);
  });

  const err = el("p", { class: "drawer-note", style: "color:var(--red);display:none" }, []);
  const setErr = (m: string): void => { err.textContent = m; err.style.display = m ? "block" : "none"; };

  const autofillBtn = el("button", { class: "btn" }, ["✨ AI autofill"]);
  autofillBtn.onclick = async () => {
    autofillBtn.textContent = "Drafting from your code…";
    (autofillBtn as HTMLButtonElement).disabled = true;
    setErr("");
    try {
      const draft = await withAuth(() => invoke<WebProfileSections>("profile.autofill"), fields);
      for (const { key } of PROFILE_FIELDS) {
        const ta = inputs.get(key)!;
        if (!ta.value.trim() && draft[key]) {
          ta.value = String(draft[key]);
          (fields[key] as string) = ta.value;
        }
      }
      fields.source = "ai";
    } catch (e) {
      setErr(e instanceof Error ? e.message : "autofill failed");
    } finally {
      autofillBtn.textContent = "✨ AI autofill";
      (autofillBtn as HTMLButtonElement).disabled = false;
    }
  };

  const saveBtn = el("button", { class: "btn btn-primary" }, ["Save profile"]);
  saveBtn.onclick = async () => {
    saveBtn.textContent = "Saving…";
    (saveBtn as HTMLButtonElement).disabled = true;
    setErr("");
    try {
      const source = fields.source === "ai" ? "mixed" : (fields.source ?? "human");
      await withAuth(() => invoke<unknown>("profile.upsert", { ...fields, source }), null);
      try { localStorage.setItem(profileSeenKey(), "1"); } catch { /* ignore */ }
      closeModal();
      toast("Project profile saved — Tavsin will use it every session.", "success");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "save failed");
      saveBtn.textContent = "Save profile";
      (saveBtn as HTMLButtonElement).disabled = false;
    }
  };

  const body = el("div", { class: "settings" }, [
    el("p", { class: "drawer-note", style: "margin-top:0" }, [
      "Set this once. Tavsin carries it into every session so it works like a teammate who already knows this codebase.",
    ]),
    el("div", { class: "set-section" }, [
      el("div", { style: "display:flex;align-items:center;justify-content:space-between;gap:12px" }, [
        el("span", { class: "set-muted" }, ["Let AI draft the sections from your codebase, then edit."]),
        autofillBtn,
      ]),
    ]),
    ...sections,
    err,
    el("div", { class: "set-section", style: "display:flex;justify-content:flex-end;gap:8px" }, [saveBtn]),
  ]);
  showModal("Project profile", body);
}

/** Per-project localStorage key so the first-run prompt fires once per project. */
function profileSeenKey(): string {
  return `tavsin.web.profileSeen.${state.projectName ?? "default"}`;
}

function openAppSettings(): void {
  const seg = el("div", { class: "seg" });
  const syncSeg = (): void => {
    const t = currentTheme();
    seg.querySelectorAll(".seg-btn").forEach((b) => b.classList.toggle("seg-on", (b as HTMLElement).dataset["v"] === t));
  };
  ([["light", "Light"], ["dark", "Dark"]] as const).forEach(([v, label]) => {
    const b = el("button", { class: "seg-btn" }, [label]);
    b.dataset["v"] = v;
    b.onclick = () => { applyTheme(v); syncSeg(); };
    seg.append(b);
  });
  syncSeg();

  const langs: Array<[string, string]> = [
    ["en", "English"], ["es", "Español"], ["fr", "Français"], ["de", "Deutsch"],
    ["pt", "Português"], ["ja", "日本語"], ["zh", "中文"], ["ar", "العربية"],
    ["ru", "Русский"], ["pl", "Polski"],
  ];
  const langSel = el("select", { class: "field" }, langs.map(([c, n]) => el("option", { value: c }, [n]))) as HTMLSelectElement;
  langSel.value = currentLang();
  langSel.onchange = () => {
    try { localStorage.setItem("tavsin.web.lang", langSel.value); } catch { /* ignore */ }
    document.documentElement.lang = langSel.value;
  };

  const faq: Array<[string, string]> = [
    ["What is Tavsin?", "An AI-native workspace where every fix, review, and rule becomes reusable project intelligence your team owns — model-agnostic and local-first."],
    ["Where does my data live?", "Per-account on the server you sign in to. Your projects and memory are isolated to your account and never shared."],
    ["Which models can I use?", "Anthropic, OpenAI, Gemini, or a local OpenAI-compatible endpoint — switch anytime without losing memory or history."],
    ["How do I upgrade my plan?", "Open your account menu → Upgrade, or visit tavsin.xyz/pricing. Free, Pro, Team, and Enterprise tiers are available."],
  ];

  // Billing section — only shows for authenticated multi-user accounts where
  // billing is actually configured on this server. Free users see "Upgrade",
  // paid users see "Manage subscription" (opens the Stripe Customer Portal).
  const billingSection = state.user && state.billingEnabled
    ? buildBillingSection(state.user)
    : null;

  const activeProv = state.providers.find((p) => p.id === state.resolvedProvider);
  const ownKey = Boolean(activeProv?.ownKey);
  const managed = state.managed || Boolean(activeProv?.managed);
  const providerSection = el("div", { class: "set-section" }, [
    el("div", { class: "set-label" }, [managed && !ownKey ? "Model — you're on the free tier" : "Model provider & API key"]),
    el("p", { class: "drawer-note", style: "margin-top:0" }, [
      ownKey
        ? `Active: ${state.resolvedProvider}${activeProv?.defaultModel ? ` · ${activeProv.defaultModel}` : ""} — your own key is set. 🔒 Keys are stored locally, never shown in chat.`
        : managed
          ? `You're building on Tavsin's free managed model — no key needed. Want higher limits or a specific model? Add your own key (optional); it's stored locally and never appears in chat.`
          : `Tavsin defaults to a free OpenRouter model, but this instance has no managed key — add your OpenRouter key once to run. It's stored locally and never appears in chat.`,
    ]),
    (() => {
      const btn = el("button", { class: managed && !ownKey ? "btn" : "btn btn-primary" }, [ownKey ? "Change API key" : managed ? "Add your own key (optional)" : "Add API key"]);
      btn.onclick = () => { closeModal(); openSettings(state.resolvedProvider !== "—" ? state.resolvedProvider : "openrouter"); };
      return btn;
    })(),
  ]);
  // App version — from the shared app.releases channel (falls back to the
  // frontend bundle version while it loads / if offline).
  const aboutVersion = el("div", {}, [el("span", { class: "set-muted" }, ["Tavsin "]), `v${APP_VERSION}`]);
  void withAuth(() => invoke<{ version: string }>("app.releases"), null).then((d) => {
    if (d?.version) aboutVersion.replaceChildren(el("span", { class: "set-muted" }, ["Tavsin "]), `v${d.version}`);
  });

  const body = el("div", { class: "settings" }, [
    providerSection,
    el("div", { class: "set-section" }, [
      el("div", { class: "set-label" }, ["Appearance"]),
      seg,
    ]),
    el("div", { class: "set-section" }, [
      el("div", { class: "set-label" }, ["Language"]),
      langSel,
      el("p", { class: "drawer-note" }, ["Interface translation is rolling out — your choice is saved now."]),
    ]),
    ...(billingSection ? [billingSection] : []),
    buildGithubSection(),
    buildGitlabSection(),
    buildAtlassianSection(),
    buildSlackSection(),
    buildLinearSection(),
    buildEmbeddingsSection(),
    buildReleaseNotesSection(),
    el("div", { class: "set-section" }, [
      el("div", { class: "set-label" }, ["Help & FAQ"]),
      ...faq.map(([q, a]) => el("details", { class: "faq" }, [el("summary", {}, [q]), el("p", {}, [a])])),
    ]),
    el("div", { class: "set-about" }, [
      aboutVersion,
      el("div", { class: "set-links" }, [
        el("a", { href: "mailto:hello@tavsin.xyz", class: "set-link" }, ["Contact support"]),
        el("a", { href: "https://tavsin.xyz", target: "_blank", rel: "noopener", class: "set-link" }, ["tavsin.xyz"]),
      ]),
    ]),
  ]);
  showModal("Settings", body);
}

/**
 * Billing card for the in-app Settings drawer. Shows current tier, an
 * "Upgrade to Pro/Team" CTA for Free users, and "Manage subscription"
 * (Stripe Customer Portal) for paid users — same auth as everything else,
 * so a logged-in user can land in the portal in one click.
 */
function buildBillingSection(user: PublicUser): HTMLElement {
  const tierLabel = user.tier === "local" ? "Free" : user.tier === "enterprise" ? "Enterprise" : planPrice(user.tier).label;
  const tierClass = user.tier === "local" ? "set-tier-free" : `set-tier-${user.tier}`;

  const buttons: HTMLElement[] = [];
  if (user.tier === "local") {
    if (state.billingPlans.length > 0) {
      const plans = el("button", { class: "btn btn-primary" }, ["✦ See plans — Pro from $20/mo"]);
      plans.onclick = () => showPlans();
      buttons.push(plans);
    }
  } else {
    const portal = el("button", { class: "btn" }, ["Manage subscription, invoices, cancel →"]);
    portal.onclick = async () => {
      try {
        const url = await openBillingPortal();
        window.location.href = url;
      } catch (e) {
        toast(e instanceof Error ? e.message : String(e), "error");
      }
    };
    buttons.push(portal);
  }
  // Credits on EVERY plan: a prepaid balance that never expires and pays for
  // usage beyond the plan (core QuotaManager.admit / debitBalance).
  const credits = el("button", { class: "btn" }, [state.quota?.metered ? `Buy tokens · ${fmtTokens(state.quota.balanceTokens ?? 0)} purchased` : `Buy credits · balance ${fmtUsd(state.quota?.balanceUsd ?? 0)}`]);
  credits.onclick = () => { closeModal(); showBuyCredits(); };
  buttons.push(credits);

  return el("div", { class: "set-section" }, [
    el("div", { class: "set-label" }, ["Billing"]),
    el("div", { class: "set-billing-row" }, [
      el("div", { class: "set-billing-tier" }, [
        el("span", { class: `set-tier-badge ${tierClass}` }, [tierLabel]),
        el("span", { class: "set-muted" }, [" · ", user.email]),
      ]),
    ]),
    ...buttons,
    el("p", { class: "drawer-note" }, [
      user.tier === "local"
        ? isManagedInference()
          ? "Every plan includes a monthly token budget on Tavsin's models — Claude, GPT and Gemini, no API key needed. Auto picks the best model for each task; lighter models stretch your tokens further."
          : "Pro unlocks fully-autonomous Agent mode, the MCP server, cloud sync of memory, and unlimited trace history. Team adds a shared memory layer your team builds together."
        : "Credits never expire and pay for usage beyond your plan, at each model's real price. Cancel anytime through the portal — your data stays put.",
    ]),
  ]);
}

/** The Tavsin brand mark, inline SVG (sky squircle + "T" feeding a 3-node graph).
 *  Gradient ids are unique per instance so multiple marks on a page don't clash. */
let logoSeq = 0;
function logoMark(size = 28, className = ""): HTMLElement {
  const u = `lg${++logoSeq}`;
  const span = el("span", className ? { class: className } : {});
  span.style.display = "inline-block";
  span.style.height = `${size}px`;
  span.style.width = `${size}px`;
  span.style.lineHeight = "0";
  span.innerHTML =
    `<svg width="${size}" height="${size}" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Tavsin">` +
    `<defs><linearGradient id="${u}b" x1="0" y1="0" x2="1024" y2="1024" gradientUnits="userSpaceOnUse">` +
    `<stop offset="0" stop-color="#3b82f6"/><stop offset="0.55" stop-color="#2563eb"/><stop offset="1" stop-color="#1e40af"/></linearGradient>` +
    `<radialGradient id="${u}g" cx="0.3" cy="0.25" r="0.9"><stop offset="0" stop-color="#fff" stop-opacity="0.30"/><stop offset="0.5" stop-color="#fff" stop-opacity="0"/></radialGradient></defs>` +
    `<rect x="112" y="112" width="800" height="800" rx="184" fill="url(#${u}b)"/>` +
    `<rect x="112" y="112" width="800" height="800" rx="184" fill="url(#${u}g)"/>` +
    `<g fill="#fff"><rect x="304" y="286" width="416" height="94" rx="30"/><rect x="465" y="286" width="94" height="286" rx="30"/></g>` +
    `<g stroke="#fff" stroke-opacity="0.6" stroke-width="14" stroke-linecap="round"><line x1="330" y1="742" x2="512" y2="662"/><line x1="694" y1="742" x2="512" y2="662"/></g>` +
    `<g fill="#fff"><circle cx="512" cy="662" r="40"/><circle cx="330" cy="742" r="32"/><circle cx="694" cy="742" r="32"/></g></svg>`;
  return span;
}

function showAuthScreen(initial: "login" | "signup" = "login"): void {
  showSignIn(signInDeps(), initial);
}

/** What the sign-in card needs from the app (signIn.ts). */
function signInDeps(): SignInDeps {
  return {
    providers: state.signInProviders,
    passkeys: state.passkeysOn,
    emailCode: state.emailCodeOn,
    allowsSignup: state.allowsSignup,
    showModal,
    lockOverlay: () => document.getElementById("overlay")?.classList.add("overlay-locked"),
    brand: (tagline) => el("div", { class: "auth-brand" }, [
      logoMark(44, "auth-logo"),
      el("div", { class: "auth-wordmark" }, ["TAVSIN"]),
      el("div", { class: "auth-tagline" }, [tagline]),
    ]),
    beforeLeave: () => {
      // Stash the deep-linked plan BEFORE auth — `boot()` post-auth picks it up.
      stashPendingPlan();
      // Same trick for /team/accept?token=… invites — survive the redirect.
      try {
        const params = new URLSearchParams(window.location.search);
        const tt = params.get("token");
        if (window.location.pathname.startsWith("/team/accept") && tt) sessionStorage.setItem("tavsin.web.invite", tt);
      } catch { /* ignore */ }
    },
    onSignedIn: async (result) => {
      setToken(result.token);
      state.user = result.user;
      // Drop ?signup so a reload after signing in does not reopen the form.
      try {
        const u = new URL(window.location.href);
        if (u.searchParams.has("signup")) {
          u.searchParams.delete("signup");
          window.history.replaceState(null, "", u.pathname + u.search);
        }
      } catch { /* ignore */ }
      closeModal();
      await boot();
    },
    signup,
    loginWithEmail,
    requestPasswordReset,
  };
}

function showTokenPrompt(): void {
  const input = el("input", { class: "field", type: "password", placeholder: "Access token" }) as HTMLInputElement;
  input.value = getToken();
  const save = el("button", { class: "btn btn-primary" }, ["Connect"]);
  save.onclick = async () => {
    setToken(input.value.trim());
    closeModal();
    await boot();
  };
  showModal("This Tavsin server requires an access token", el("div", { class: "drawer" }, [
    el("p", { class: "drawer-note" }, ["Set TAVSIN_WEB_TOKEN on the server, then paste it here. Stored only in this browser."]),
    input,
    el("div", { class: "drawer-actions" }, [save]),
  ]));
}

/** Owner-password login page — first thing the user sees on a password server. */
function showLogin(): void {
  const password = el("input", { class: "field", type: "password", placeholder: "Owner password", autocomplete: "current-password" }) as HTMLInputElement;
  const note = el("p", { class: "drawer-note" }, ["Set on the server with TAVSIN_OWNER_PASSWORD. Your session lives in this browser only."]);
  const submit = el("button", { class: "btn btn-primary" }, ["Sign in"]) as HTMLButtonElement;
  const tryIt = async (): Promise<void> => {
    submit.disabled = true;
    submit.textContent = "Signing in…";
    try {
      const token = await loginWithPassword(password.value);
      setToken(token);
      closeModal();
      await boot();
    } catch (e) {
      note.textContent = e instanceof Error ? e.message : String(e);
      submit.disabled = false;
      submit.textContent = "Sign in";
    }
  };
  submit.onclick = () => void tryIt();
  password.onkeydown = (e) => {
    if (e.key === "Enter") void tryIt();
  };
  showModal("Sign in to Tavsin", el("div", { class: "drawer" }, [
    el("label", { class: "field-label" }, ["Password"]),
    password,
    el("div", { class: "drawer-actions" }, [submit]),
    note,
  ]));
  // Don't allow dismissing the login modal — sign-in is required.
  document.getElementById("overlay")?.classList.add("overlay-locked");
  setTimeout(() => password.focus(), 0);
}

function showModal(title: string, content: HTMLElement): void {
  closeModal();
  const close = el("button", { class: "btn btn-ghost btn-sm" }, ["✕"]);
  close.onclick = closeModal;
  const overlay = el("div", { class: "overlay", id: "overlay" }, [el("div", { class: "modal" }, [el("div", { class: "modal-head" }, [el("span", {}, [title]), close]), el("div", { class: "modal-body" }, [content])])]);
  overlay.addEventListener("click", (e) => {
    // Don't dismiss a locked overlay (e.g. required sign-in) on backdrop click —
    // that left the app in a half-booted "loading" limbo.
    if (e.target === overlay && !overlay.classList.contains("overlay-locked")) closeModal();
  });
  document.body.append(overlay);
}
function closeModal(): void {
  document.getElementById("overlay")?.remove();
}

/** Send the browser to Stripe Checkout for the given plan. Errors surface as a toast. */
async function launchCheckout(plan: PlanId, quantity?: number, interval: "month" | "year" = "month"): Promise<void> {
  try {
    const url = await startCheckout(plan, quantity, interval);
    window.location.href = url;
  } catch (e) {
    toast(e instanceof Error ? e.message : String(e), "error");
  }
}

const fmtPlanUsd = (n: number): string => (Number.isInteger(n) ? `$${String(n)}` : `$${n.toFixed(2)}`);

/**
 * CHOOSE A PLAN — Cursor's ladder (plans.ts): Pro, Pro+, Ultra and Teams,
 * monthly or yearly at 20% off. Only plans with a configured Stripe price
 * are offered, and Yearly appears only when every offered plan has one. On a
 * managed host each card says how many tokens it includes.
 */
/** Offered once per account, in its first day, while it is on Free and billing is on. */
const PLANS_OFFERED_KEY = "tavsin.plansOffered:";
const NEW_ACCOUNT_MS = 24 * 60 * 60 * 1000;
function shouldOfferPlans(user: PublicUser): boolean {
  if (!state.billingEnabled || state.billingPlans.length === 0 || user.tier !== "local") return false;
  const made = user.createdAt ? Date.parse(user.createdAt) : NaN;
  if (!Number.isFinite(made) || Date.now() - made > NEW_ACCOUNT_MS) return false;
  try { return localStorage.getItem(PLANS_OFFERED_KEY + user.id) === null; } catch { return false; }
}
function markPlansOffered(userId: string): void {
  try { localStorage.setItem(PLANS_OFFERED_KEY + userId, new Date().toISOString()); } catch { /* shown again next time, which is harmless */ }
}

function showPlans(opts: { welcome?: boolean } = {}): void {
  const offered = PLAN_CATALOG.filter((p) => state.billingPlans.includes(p.id));
  if (offered.length === 0) {
    toast("Plans are not available on this server.", "info");
    return;
  }
  const yearlyOk = offered.every((p) => state.billingYearly.includes(p.id));
  let interval: "month" | "year" = yearlyOk ? "year" : "month";
  const tokensFor = (id: PlanId): number | null => state.managedCatalog?.plans.find((p) => p.tier === id)?.tokens ?? null;
  const grid = el("div", { class: "plan-grid" });
  const toggle = el("div", { class: "plan-toggle", role: "tablist" });
  const paint = (): void => {
    toggle.replaceChildren(...(["month", "year"] as const).filter((i) => i === "month" || yearlyOk).map((i) => {
      const b = el("button", { class: `plan-toggle-btn${interval === i ? " on" : ""}`, type: "button", role: "tab", "aria-selected": String(interval === i) }, [i === "month" ? "Monthly" : "Yearly · save 20%"]);
      b.onclick = () => { interval = i; paint(); };
      return b;
    }));
    grid.replaceChildren(...offered.map((p) => {
      const per = monthlyEquivalent(p, interval);
      const tokens = tokensFor(p.id);
      const choose = el("button", { class: p.id === "pro" ? "btn btn-primary" : "btn", type: "button" }, [p.perSeat ? "Choose team size" : `Get ${p.label}`]);
      choose.onclick = () => {
        closeModal();
        if (p.perSeat) showTeamSeatPicker(interval);
        else void launchCheckout(p.id, undefined, interval);
      };
      return el("div", { class: `plan-card${p.id === "pro" ? " plan-card-pick" : ""}` }, [
        el("div", { class: "plan-name" }, [p.label]),
        el("div", { class: "plan-price" }, [fmtPlanUsd(per), el("span", { class: "plan-unit" }, [p.perSeat ? " /seat/mo" : " /mo"])]),
        el("div", { class: "plan-billed" }, [interval === "year" ? `${fmtPlanUsd(p.yearlyUsd)} billed yearly${p.perSeat ? " per seat" : ""}` : "billed monthly"]),
        el("div", { class: "plan-blurb" }, [p.blurb]),
        // The credits ARE the price: every plan's monthly allowance is worth
        // what it costs (plans.ts creditsUsd), metered at the vendors' prices.
        el("div", { class: "plan-usage" }, [`${fmtPlanUsd(p.creditsUsd)} of AI credits every month${p.perSeat ? " per seat" : ""}`]),
        tokens ? el("div", { class: "plan-billed" }, [`about ${fmtTokens(tokens)} tokens on balanced models`]) : null,
        choose,
      ]);
    }));
  };
  paint();
  const free = opts.welcome ? (() => {
    const b = el("button", { class: "btn btn-ghost", type: "button" }, ["Continue with Free"]);
    b.onclick = () => closeModal();
    return b;
  })() : null;
  const byok = opts.welcome ? (() => {
    const b = el("button", { class: "btn btn-ghost", type: "button" }, ["I have an API key"]);
    b.onclick = () => { closeModal(); openSettings(); };
    return b;
  })() : null;
  showModal(opts.welcome ? "Welcome to Tavsin — choose a plan" : "Choose a plan", el("div", {}, [
    toggle,
    grid,
    el("p", { class: "drawer-note" }, ["Each plan includes AI credits worth what you pay, every month, for Claude, GPT and Gemini with no API key. Lighter models stretch them further, you can buy more any time, and you can cancel whenever you like."]),
    free || byok ? el("div", { class: "plan-welcome-actions" }, [free, byok]) : null,
  ]));
}

/**
 * Team seat picker — Teams is per seat with a minimum of three, at the
 * monthly or yearly price the plan picker chose.
 */
function showTeamSeatPicker(interval: "month" | "year" = "month"): void {
  const team = planPrice("team");
  let seats = team.minSeats;
  const unit = interval === "year" ? team.yearlyUsd : team.monthlyUsd;
  const period = interval === "year" ? "year" : "month";
  const note = el("p", { class: "drawer-note" }, [`Teams is ${fmtPlanUsd(unit)} per seat per ${period}, minimum ${String(team.minSeats)} seats. You can adjust at any time.`]);
  const total = el("p", { class: "drawer-note", style: "color: var(--text); font-size: 14px;" }, []);
  const updateTotal = (): void => {
    total.textContent = `Total: ${fmtPlanUsd(seats * unit)}/${period} for ${String(seats)} seats`;
  };
  updateTotal();

  const minus = el("button", { class: "btn" }, ["−"]) as HTMLButtonElement;
  const plus = el("button", { class: "btn" }, ["+"]) as HTMLButtonElement;
  const count = el("input", { class: "field", type: "number", min: String(team.minSeats), max: "999" }) as HTMLInputElement;
  count.value = String(seats);
  count.style.cssText = "width: 80px; text-align: center;";

  const apply = (n: number): void => {
    seats = Math.max(team.minSeats, Math.min(999, Math.floor(n) || team.minSeats));
    count.value = String(seats);
    updateTotal();
  };
  minus.onclick = () => apply(seats - 1);
  plus.onclick = () => apply(seats + 1);
  count.onchange = () => apply(Number(count.value));

  const submit = el("button", { class: "btn btn-primary" }, ["Continue to checkout"]) as HTMLButtonElement;
  submit.onclick = async () => {
    submit.disabled = true;
    submit.textContent = "Redirecting…";
    closeModal();
    await launchCheckout("team", seats, interval);
  };

  showModal("Pick your team size", el("div", { class: "drawer" }, [
    el("label", { class: "field-label" }, ["Seats"]),
    el("div", { style: "display: flex; gap: 8px; align-items: center;" }, [minus, count, plus]),
    total,
    el("div", { class: "drawer-actions" }, [submit]),
    note,
  ]));
}

/** Account menu off the user pill: manage billing, sign out. */
function showAccountMenu(user: PublicUser): void {
  const rows: HTMLElement[] = [];
  rows.push(el("div", { class: "drawer-note" }, [`Signed in as ${user.email}`]));

  if (user.tier !== "local") {
    const portal = el("button", { class: "btn" }, ["Manage billing & invoices"]);
    portal.onclick = async () => {
      try {
        const url = await openBillingPortal();
        window.location.href = url;
      } catch (e) {
        toast(e instanceof Error ? e.message : String(e), "error");
      }
    };
    rows.push(portal);
  } else if (state.billingEnabled) {
    if (state.billingPlans.length > 0) {
      const up = el("button", { class: "btn btn-primary" }, ["✦ See plans"]);
      up.onclick = () => showPlans();
      rows.push(up);
    }
  }

  if (state.authMode === "multiuser") {
    const methods = el("button", { class: "btn" }, ["Sign-in methods"]);
    methods.onclick = () => void openSignInMethods({ user, showModal, reauth: () => signOut(), requestPasswordReset });
    rows.push(methods);
  }

  const signOutBtn = el("button", { class: "btn" }, ["Sign out"]);
  signOutBtn.onclick = () => void signOut();
  rows.push(signOutBtn);

  showModal(`Account`, el("div", { class: "drawer" }, rows));
}

/** Show the password-reset form when the URL has ?token=… on /reset. */
function showResetForm(token: string): void {
  const password = el("input", { class: "field", type: "password", placeholder: "At least 10 characters", autocomplete: "new-password" }) as HTMLInputElement;
  const note = el("p", { class: "drawer-note" }, ["Enter a new password. You'll be signed in straight after."]);
  const submit = el("button", { class: "btn btn-primary" }, ["Set new password"]) as HTMLButtonElement;
  submit.onclick = async () => {
    submit.disabled = true;
    submit.textContent = "Saving…";
    try {
      const newToken = await confirmPasswordReset(token, password.value);
      setToken(newToken);
      closeModal();
      // Strip the ?token=… off the URL so refresh doesn't retry.
      window.history.replaceState(null, "", window.location.pathname);
      await boot();
      toast("Password updated", "success");
    } catch (e) {
      note.textContent = e instanceof Error ? e.message : String(e);
      submit.disabled = false;
      submit.textContent = "Set new password";
    }
  };
  password.onkeydown = (e) => { if (e.key === "Enter") submit.click(); };
  showModal("Choose a new password", el("div", { class: "drawer" }, [
    el("label", { class: "field-label" }, ["New password"]),
    password,
    el("div", { class: "drawer-actions" }, [submit]),
    note,
  ]));
  document.getElementById("overlay")?.classList.add("overlay-locked");
  setTimeout(() => password.focus(), 0);
}

/** Tiny URL-param handler — verify and reset flows piggyback on the SPA. */
async function handleUrlParams(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const path = window.location.pathname;
  // ?billing=ok / ?billing=cancel from Stripe Checkout return.
  const billing = params.get("billing");
  if (billing === "ok") {
    toast("Subscription active — welcome to Pro!", "success");
    window.history.replaceState(null, "", path);
  } else if (billing === "credits_ok") {
    // The balance lands when Stripe's webhook confirms the charge, usually
    // within seconds of this redirect — the usage pill refreshes on the next turn.
    toast("Payment received — your tokens are being added.", "success");
    window.history.replaceState(null, "", path);
  } else if (billing === "cancel") {
    toast("Checkout cancelled.", "info");
    window.history.replaceState(null, "", path);
  }
  // /verify?token=… — single-use verification from the welcome email.
  if (path.startsWith("/verify")) {
    const t = params.get("token");
    if (t) {
      try {
        await verifyEmail(t);
        toast("Email verified ✓", "success");
      } catch (e) {
        toast(e instanceof Error ? e.message : String(e), "error");
      }
      window.history.replaceState(null, "", "/");
    }
  }
  // /reset?token=… — password-reset link from a forgot-password email.
  if (path.startsWith("/reset")) {
    const t = params.get("token");
    if (t) showResetForm(t);
  }
  // /team/accept?token=… — team invite link. Requires sign-in first; the
  // token is stashed in sessionStorage so it survives the signup/login round-
  // trip and gets consumed by `consumePendingInvite()` after boot.
  if (path.startsWith("/team/accept")) {
    const t = params.get("token");
    if (t) {
      try { sessionStorage.setItem("tavsin.web.invite", t); } catch { /* ignore */ }
      window.history.replaceState(null, "", "/");
    }
  }
}

// ── boot ──
/**
 * Ship browser crashes to the operator's error log. Installed before anything
 * else so a failure during boot is still reported.
 */
function installClientErrorReporting(): void {
  window.addEventListener("error", (e) => {
    reportClientError(e.message || "uncaught error", e.error instanceof Error ? e.error.stack : undefined);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = (e as PromiseRejectionEvent).reason;
    reportClientError(
      r instanceof Error ? r.message : String(r),
      r instanceof Error ? r.stack : undefined,
    );
  });
}

/**
 * Logout / user switch. Reset the editor BEFORE the token is dropped and
 * BEFORE boot paints, so a previous tenant's buffer cannot remain on screen.
 */
async function signOut(): Promise<void> {
  state.user = null;
  resetWebEditorState(state);
  closeModal();
  render();
  await logout();
  await boot();
}

async function boot(): Promise<void> {
  // Auth / user / project boundary: drop any previous tenant's editor
  // before the first paint of this boot so a leftover buffer cannot show.
  resetWebEditorState(state);
  installClientErrorReporting();
  state.ready = false;
  render();
  try {
    const caps = await fetch("/api/playground/capabilities").then((r) => r.json()) as { ok?: boolean; data?: typeof state.playgroundCaps };
    if (caps.ok && caps.data) state.playgroundCaps = caps.data;
  } catch {
    /* offline dev — UI still works */
  }
  // Verify / reset / billing return URLs are handled the moment the SPA loads —
  // BEFORE we touch auth, because /verify and /reset are anonymous flows.
  await handleUrlParams();
  // OAuth handoff: the provider callback redirected here with ?oauth=<code>.
  // Exchange it (single-use) for a session token, then strip it from the URL.
  const bootParams = new URLSearchParams(window.location.search);
  const oauthCode = bootParams.get("oauth");
  if (oauthCode) {
    const r = await exchangeOAuth(oauthCode).catch(() => null);
    if (r) { setToken(r.token); state.user = r.user; } else toast("Sign-in didn't complete — please try again.", "error");
    history.replaceState({}, "", window.location.pathname);
  } else if (bootParams.get("auth_error")) {
    toast(authErrorMessage(bootParams.get("auth_error") ?? "", bootParams.get("provider")), "error");
    history.replaceState({}, "", window.location.pathname);
  } else if (bootParams.get("linked")) {
    toast(`${({ google: "Google", apple: "Apple", microsoft: "Microsoft", github: "GitHub" } as Record<string, string>)[bootParams.get("linked") ?? ""] ?? "That account"} is now linked — you can sign in with it.`, "success");
    history.replaceState({}, "", window.location.pathname);
  }
  // Discover the server's auth mode FIRST so a 401 routes to the right surface.
  try {
    const info = await fetchAuthInfo();
    state.authMode = info.mode;
    state.authRequired = info.required;
    state.allowsSignup = info.allowsSignup;
    state.signInProviders = info.providers ?? (info.oauthProviders ?? []).map((id) => ({ id, label: id }));
    state.passkeysOn = info.passkeys === true;
    state.emailCodeOn = info.emailCode === true;
    if (info.required) {
      // Existing token? Try to fetch /me — confirms it's still valid and gives
      // us tier + email for the topbar in one round-trip.
      if (getToken()) {
        const me = await fetchMe();
        if (me) {
          state.user = me;
        } else {
          promptToken();
          return;
        }
      } else {
        promptToken();
        return;
      }
    }
  } catch {
    // The auth-info probe failing is non-fatal — proceed in anonymous mode.
  }
  // A teammate's live-share link (`/#live=<token>`) opens the viewer page
  // instead of the workspace. Auth was resolved above: the server still
  // insists the viewer is signed in AND on the share's team.
  const liveToken = liveTokenFromHash();
  if (liveToken) {
    state.ready = true;
    mountLiveView(root, liveToken, () => promptToken());
    return;
  }
  // Which project this page works in — settled before anything per-project
  // loads. A stale id reloads the page onto the right one.
  if (!(await settleActiveProject())) return;
  {
    const u = new URL(window.location.href);
    if (u.searchParams.has("project")) {
      u.searchParams.delete("project");
      history.replaceState({}, "", u.pathname + (u.search ? u.search : ""));
    }
  }
  // Deleted in another tab while this one was open: the server refuses the
  // stale id (api.ts forgets it), and the page reopens on a project that exists.
  window.addEventListener(PROJECT_GONE_EVENT, () => {
    toast("That project was deleted — opening another.", "error");
    window.setTimeout(() => window.location.assign(window.location.pathname), 1200);
  }, { once: true });
  await loadStatus();
  await loadSessions();
  // A tapped phone notification opens its session (`/?session=…`); the worker
  // routes later taps into this already-open page.
  const linked = sessionFromUrl(window.location.href);
  if (linked && state.sessions.some((x) => x.id === linked)) {
    await openSession(linked);
    history.replaceState({}, "", window.location.pathname);
  }
  registerServiceWorker((url) => {
    // A notification for a session in ANOTHER project opens that project.
    let linked: URL | null = null;
    try { linked = new URL(url, window.location.origin); } catch { linked = null; }
    const linkedProject = linked && linked.origin === window.location.origin ? linked.searchParams.get("project") : null;
    if (linked && linkedProject && linkedProject !== getActiveProject() && !state.run) {
      window.location.assign(linked.pathname + linked.search);
      return;
    }
    const id = sessionFromUrl(url);
    if (id && state.sessions.some((x) => x.id === id)) void openSession(id);
  });
  await autoResumeSession();
  await loadUsage();
  void loadProjectContext();
  void loadNotifications();
  void refreshJobs();
  // Billing info is cheap and gates the Upgrade pill — load alongside the rest.
  try {
    const b = await fetchBillingInfo();
    state.billingEnabled = b.enabled;
    state.billingPlans = b.plans;
    state.billingYearly = b.yearlyPlans ?? [];
  } catch {
    /* billing is optional; if the probe fails just hide upgrade UI */
  }
  // Pull team list eagerly — costs ~ms and lets the Team tab render the
  // moment the user clicks it, plus drives whether the tab appears at all.
  if (state.user) void refreshTeams();
  state.ready = true;
  render();
  void refreshChanges();
  if (state.tab === "preview" && !state.previewAutoDetected) {
    state.previewAutoDetected = true;
    void autoDetectPreview();
  }
  // Deep-link from the pricing page: tavsin.xyz/pricing → /?plan=pro|team
  // arrives here AFTER signup/login, and once billing is confirmed enabled
  // we trigger Checkout straight away. Free users only — never auto-charge
  // anyone who's already on a paid tier.
  const pending = consumePendingPlan();
  if (pending && state.billingEnabled && state.user && state.user.tier === "local") {
    if (pending.plan === "team") showTeamSeatPicker(pending.interval);
    else void launchCheckout(pending.plan, undefined, pending.interval);
  } else if (state.user && shouldOfferPlans(state.user)) {
    // A new account is offered the plans once, with Free one click away. The
    // project-profile prompt waits for another visit: two dialogs at once is
    // how a first run turns into a list of things to dismiss.
    markPlansOffered(state.user.id);
    showPlans({ welcome: true });
    return;
  }
  // Pending team invite from a /team/accept?token=… deep-link survived the
  // login round-trip in sessionStorage. Consume it now.
  if (state.user) void consumePendingTeamInvite();

  // First-run: nudge the user to set up the project profile once, so the agent
  // works like a teammate who knows the codebase. Only when a model provider is
  // ready (autofill needs it) and we haven't prompted for this project before.
  void maybePromptProjectProfile();
}

async function maybePromptProjectProfile(): Promise<void> {
  if (!state.providerOk) return; // autofill needs a model; don't nag otherwise
  let seen = false;
  try { seen = localStorage.getItem(profileSeenKey()) === "1"; } catch { /* ignore */ }
  if (seen) return;
  try {
    const existing = await withAuth(() => invoke<WebProfileSections | null>("profile.get"), null);
    if (existing) {
      try { localStorage.setItem(profileSeenKey(), "1"); } catch { /* ignore */ }
      return;
    }
  } catch {
    return; // never block boot on this
  }
  // Mark as prompted regardless of whether they save, so we ask at most once.
  try { localStorage.setItem(profileSeenKey(), "1"); } catch { /* ignore */ }
  void openProjectProfile();
}

async function consumePendingTeamInvite(): Promise<void> {
  let token = "";
  try { token = sessionStorage.getItem("tavsin.web.invite") ?? ""; } catch { /* ignore */ }
  if (!token) return;
  try { sessionStorage.removeItem("tavsin.web.invite"); } catch { /* ignore */ }
  try {
    const team = await acceptTeamInvite(token);
    toast(`Joined ${team.name}`, "success");
    state.activeTeamId = team.id;
    await refreshTeams();
    setTab("team");
  } catch (e) {
    toast(e instanceof Error ? e.message : String(e), "error");
  }
}

/**
 * Read `?plan=` from the URL OR a saved value from sessionStorage; clear both.
 * sessionStorage is the bridge from signup (which reloads) back into boot.
 */
function consumePendingPlan(): { plan: PlanId; interval: "month" | "year" } | null {
  const params = new URLSearchParams(window.location.search);
  const url = params.get("plan");
  if (isPlanId(url)) {
    window.history.replaceState(null, "", window.location.pathname);
    return { plan: url, interval: params.get("interval") === "year" ? "year" : "month" };
  }
  try {
    const saved = sessionStorage.getItem("tavsin.web.plan");
    const [plan, interval] = (saved ?? "").split(":");
    if (isPlanId(plan)) {
      sessionStorage.removeItem("tavsin.web.plan");
      return { plan, interval: interval === "year" ? "year" : "month" };
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Stash the plan across the signup reload so consumePendingPlan picks it up. */
function stashPendingPlan(): void {
  const params = new URLSearchParams(window.location.search);
  const plan = params.get("plan");
  if (isPlanId(plan)) {
    try {
      sessionStorage.setItem("tavsin.web.plan", `${plan}:${params.get("interval") === "year" ? "year" : "month"}`);
    } catch { /* ignore */ }
  }
}

function isPlanId(v: unknown): v is PlanId {
  return typeof v === "string" && (PLAN_IDS as readonly string[]).includes(v);
}

function showShortcuts(): void {
  const shortcuts: Array<[string, string]> = [
    ["⌘/Ctrl + N", "New session"],
    ["⌘/Ctrl + S", "Save file"],
    ["⌘/Ctrl + /", "Show shortcuts"],
    ["Enter", "Send message"],
    ["Shift + Enter", "New line in composer"],
    ["Tab (in editor)", "Insert indent"],
  ];
  const body = el("div", { style: "display: flex; flex-direction: column; gap: 6px;" },
    shortcuts.map(([key, desc]) =>
      el("div", { style: "display: flex; justify-content: space-between; align-items: center; padding: 6px 0;" }, [
        el("span", { style: "color: var(--muted); font-size: 13px;" }, [desc]),
        el("kbd", { style: "background: var(--bg-2); border: 1px solid var(--border); border-radius: 6px; padding: 3px 8px; font-family: ui-monospace, monospace; font-size: 11px; color: var(--text);" }, [key]),
      ]),
    ),
  );
  showModal("Keyboard shortcuts", body);
}
window.addEventListener("keydown", (e) => {
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "n") {
    e.preventDefault();
    void newSession();
  }
  if ((e.metaKey || e.ctrlKey) && e.key === "/") {
    e.preventDefault();
    showShortcuts();
  }
});

// Apply saved appearance + language before the first paint.
applyTheme(currentTheme());
document.documentElement.lang = currentLang();
void boot();
